import "./styles.css";
import "./harbour-ui.css";
import "./harbour-flow.css";
import "./home-flow.css";
import "./message-wall.css";
import "./themes.css";
import "./editorial-ui.css";
import { pageCursor, reconnectCursor, reconnectDelay } from "./catchup.js";
import {
  isAfterLocalClear,
  isConversationTrace,
  localClearStorageKey,
  mentionsNickname,
  parseLocalClearSequence,
  recentRoomActivity,
  roomStreamMessages,
  shouldShowLobbyMessage,
  shouldSuggestFocus,
  type RoomActivitySample,
} from "./chat-view.js";
import { canAppealReportingRestriction } from "./notifications.js";
import {
  CHAT_BACKGROUNDS,
  chatBackgroundTitle,
  parseChatBackground,
  type ChatBackgroundId,
} from "./chat-backgrounds.js";
import { COMPOSER_EMOJI_COUNT, COMPOSER_EMOJI_GROUPS } from "./emoji-catalog.js";
import { quietRoomPlan, type QuietRoomPlan } from "./quiet-room.js";
import {
  AOTEAROA_MAP_PATH,
  HOME_STORIES,
  homeMarkup,
  homeOrbitMembers,
  initialHomePreviewState,
  reduceHomePreview,
  type HomeModule,
  type HomePreviewEvent,
  type HomePreviewState,
} from "./home.js";
import {
  COMMUNITY_RULES_VERSION,
  MESSAGE_RECALL_WINDOW_MS,
  REPORT_CATEGORIES,
  REPORT_CATEGORY_RULES,
  SLOW_MODE_INTERVALS,
  isReportCategory,
  type RoomSlowMode,
} from "../../shared/policy.js";
import { GUESTBOOK_PAGE_SIZE } from "../../shared/guestbook.js";
import { campaignIdFromSearch } from "../../shared/campaign.js";
import {
  canUseCharacterAppearance,
  characterPortraitForRobes,
  characterAppearanceMinLevel,
  type CharacterAppearanceProgressionSlot,
} from "../../shared/appearance.js";
import {
  STICKER_PACKS,
  STICKER_REACTIONS,
  stickerPackById,
  stickerReactionById,
  stickerTextFallback,
  stickerTextParts,
  stickerToken,
  unlockedStickerPacks,
  type StickerPackId,
  type StickerReactionId,
} from "../../shared/stickers.js";
import { wallClusters, wallNotePresentation } from "./message-wall.js";

const signupCampaign = campaignIdFromSearch(window.location.search);

type Role = "owner" | "admin" | "member";
type RoomId = "nz-lobby" | "reo-maori";
type PresenceStatus = "online" | "busy" | "invisible";
type AvatarSource = "system" | "character";
type CharacterAppearance = {
  form: "harbour" | "wayfinder" | "sentinel";
  hair: "portrait" | "tide-knot" | "windswept";
  face: string;
  robes: "coastal-blue" | "mist-scholar" | "night-watch";
  relic: "none" | "jade-compass" | "star-token";
  scene: "harbour-night" | "morning-mist" | "moon-passage";
};
type CharacterAppearanceSlot = CharacterAppearanceProgressionSlot;
type RoomSummary = {
  id: RoomId;
  title: string;
  eyebrow: string;
  description: string;
  announcement: string;
  language: "multilingual" | "mi";
  languageLabel: string;
  onlineCount: number;
  memberPreviews: Array<{ id: string; nickname: string; avatar: string; presenceStatus?: "online" | "busy" }>;
};
type ProgressionLevel = { level: number; xp: number; xpFromPrevious: number; title: string; unlocks: string[] };
type User = {
  id: string;
  isGuest: boolean;
  nickname: string;
  role: Role;
  color: string | null;
  avatar: string;
  avatarSource: AvatarSource;
  systemAvatar: string;
  appearance: CharacterAppearance;
  fontStyle: string;
  textEffect: string;
  profileTheme: string;
  xp: number;
  level: number;
  title: string;
  systemTitle: string;
  customTitle: string | null;
  badges: string[];
  joinedYear: number;
  foundingMember: boolean;
  levelStartXp: number;
  nextLevelXp: number | null;
  unlocks: {
    reactions: boolean;
    kiwiReactions: boolean;
    nicknameColor: boolean;
    profileTheme: boolean;
    fontStyle: boolean;
    textEffects: boolean;
    customTitle: boolean;
    adminApplication: boolean;
  };
};
type PresentUser = User & { presenceStatus?: "online" | "busy" };
type Message = {
  seq: number;
  roomId: RoomId;
  clientId: string;
  body: string | null;
  replyTo: number | null;
  createdAt: number;
  deletedAt: number | null;
  recalledAt: number | null;
  author: User;
  authorRoleAtPost: Role;
  reactions: Record<string, string[]>;
};
type RoomHighlight = {
  message: Message;
  featuredAt: number;
};
type RoomNotice = {
  id: string;
  action: "joined" | "left";
  roomId: RoomId;
  user: { id: string; nickname: string };
  createdAt: number;
};
type Sanction = { id: number; type: "mute" | "kick" | "black_room"; reason: string; startsAt: number; expiresAt: number; actorNickname: string };
type NotificationItem = {
  id: number;
  kind: "moderation_action" | "report_decision" | "appeal_decision" | "application_decision" | "mention";
  title: string;
  body: string;
  reference: string | null;
  createdAt: number;
  readAt: number | null;
  savedAt: number | null;
  expiresAt: number | null;
  roomId: RoomId | null;
  messageSeq: number | null;
  actorUserId: string | null;
};
type NotificationInbox = {
  items: NotificationItem[];
  savedItems: NotificationItem[];
  unreadCount: number;
  savedCount: number;
  savedLimit: number;
  retentionDays: number;
};
type AccountSession = {
  id: string;
  assuranceLevel: "aal1" | "aal2";
  createdAt: number;
  lastSeenAt: number;
  expiresAt: number;
  current: boolean;
};
type RoomMemberState = {
  roomId: RoomId;
  lastReadSeq: number;
  latestSeq: number;
  firstUnreadSeq: number | null;
  unreadCount: number;
  focusUnreadCount: number;
  mentionUnreadCount: number;
  focusEnabled: boolean;
  focusIds: string[];
};
type GuestbookEntry = {
  id: number;
  author: User;
  authorRoleAtPost: Role;
  signature: string;
  body: string;
  createdAt: number;
};
type GamePhase = "waiting" | "investigation" | "accusation" | "finished";
type ColorThemePreference = "night" | "light" | "system";
type GameRoomSummary = {
  id: string;
  title: string;
  scenarioId: string;
  imageUrl: string;
  phase: GamePhase;
  host: User;
  playerCount: number;
  minPlayers: number;
  maxPlayers: number;
  joinable: boolean;
};
type GameMessage = { id: number; clientId: string; body: string; createdAt: number; author: User };
type PendingMessage = {
  body: string;
  replyTo: number | null;
  createdAt: number;
  status: "sending" | "saved" | "waiting" | "failed";
  error?: string;
};
type GameState = {
  room: GameRoomSummary & {
    hostUserId: string;
    players: Array<{ user: User; investigated: boolean; voted: boolean }>;
  };
  scenario: { schemaVersion: 1; engine: "hidden-role-investigation-v1"; title: string; premise: string; imageUrl: string; rules: string[] };
  actions: Array<{ id: "lighthouse" | "market" | "ferry"; title: string; description: string }>;
  you: null | {
    roleId: "saboteur" | "inspector" | "courier" | "witness";
    title: string;
    alignment: "crew" | "saboteur";
    brief: string;
    clue: string;
    action: string | null;
    actionResult: string | null;
    voted: boolean;
  };
  messages: GameMessage[];
  result: null | {
    winner: "crew" | "saboteur";
    culprit: User;
    summary: string;
    votes: Array<{ voter: User; target: User }>;
  };
};

const copy = {
  guest: "Guest · public chat",
};

const systemColorPreference = window.matchMedia("(prefers-color-scheme: light)");
function savedColorTheme(): ColorThemePreference {
  const saved = localStorage.getItem("kiwichat:color-theme");
  return saved === "light" || saved === "system" ? saved : "night";
}

function savedChatBackground(): ChatBackgroundId {
  return parseChatBackground(localStorage.getItem("kiwichat:chat-background"));
}

function parsePresenceStatus(value: unknown): PresenceStatus {
  return value === "busy" || value === "invisible" ? value : "online";
}

function savedPresenceStatus(): PresenceStatus {
  return parsePresenceStatus(localStorage.getItem("kiwichat:presence-status"));
}

function presenceStatusLabel(status: PresenceStatus) {
  return status === "busy" ? "Busy" : status === "invisible" ? "Invisible" : "Online";
}

const fallbackRooms: RoomSummary[] = [
  { id: "nz-lobby", title: "New Zealand Lobby", eyebrow: "NATIONAL ROOM · AOTEAROA", description: "Public chat for people across Aotearoa New Zealand.", announcement: "Welcome to the New Zealand Lobby.", language: "multilingual", languageLabel: "Open conversation", onlineCount: 0, memberPreviews: [] },
  { id: "reo-maori", title: "Te Reo Māori Room", eyebrow: "TE REO MĀORI · LEARNERS WELCOME", description: "A public te reo Māori room. Learners are welcome.", announcement: "Nau mai. Please use te reo Māori here.", language: "mi", languageLabel: "Te reo Māori only", onlineCount: 0, memberPreviews: [] },
];

const avatarLabels: Record<string, string> = {
  kiwi: "Kiwi",
  tui: "Tūī",
  kea: "Kea",
  fern: "Fern",
  wave: "Wave",
  mountain: "Mountain",
  "anime-sky": "Sky",
  "anime-tide": "Tide",
  "anime-spark": "Spark",
  "anime-grove": "Grove",
  "anime-dusk": "Dusk",
  "anime-summit": "Summit",
  "chibi-nova": "Nova",
  "chibi-rio": "Rio",
  "chibi-pixel": "Pixel",
  "chibi-bloom": "Bloom",
  "chibi-ember": "Ember",
  "chibi-luna": "Luna",
  "pocket-astra": "Astra",
  "pocket-momo": "Momo",
  "pocket-kiro": "Kiro",
  "pocket-yuzu": "Yuzu",
  "pocket-sol": "Sol",
  "pocket-lumi": "Lumi",
  "orbit-vesper": "Vesper",
  "orbit-cirrus": "Cirrus",
  "orbit-moss": "Moss",
  "orbit-pip": "Pip",
  "orbit-flicker": "Flicker",
  "orbit-ripple": "Ripple",
  "afterglow-aria": "Aria",
  "afterglow-jun": "Jun",
  "afterglow-sable": "Sable",
  "afterglow-teo": "Teo",
  "afterglow-mira": "Mira",
  "afterglow-ren": "Ren",
  "daydream-tala": "Tala",
  "daydream-cian": "Cian",
  "daydream-priya": "Priya",
  "daydream-leo": "Leo",
  "daydream-aya": "Aya",
  "daydream-robin": "Robin",
  "popchibi-zuri": "Zuri",
  "popchibi-milo": "Milo",
  "popchibi-naia": "Naia",
  "popchibi-kenji": "Kenji",
  "popchibi-sana": "Sana",
  "popchibi-jules": "Jules",
  "panelpop-rowan": "Rowan",
  "panelpop-maeve": "Maeve",
  "panelpop-luca": "Luca",
  "panelpop-celeste": "Celeste",
  "panelpop-theo": "Theo",
  "panelpop-quinn": "Quinn",
  "neoclay-amara": "Amara",
  "neoclay-jude": "Jude",
  "neoclay-noor": "Noor",
  "neoclay-mateo": "Mateo",
  "neoclay-elise": "Elise",
  "neoclay-soren": "Soren",
  "mech-axiom": "Axiom",
  "mech-vela": "Vela",
  "mech-kestrel": "Kestrel",
  "mech-rune": "Rune",
  "mech-solace": "Solace",
  "mech-torque": "Torque",
  "prism-nia": "Nia",
  "prism-elio": "Elio",
  "prism-samira": "Samira",
  "prism-cass": "Cass",
  "prism-davi": "Davi",
  "prism-yuna": "Yuna",
  "show-coastal-blue": "Coastal character",
  "show-mist-scholar": "Mist character",
  "show-night-watch": "Night character",
};
const avatarGlyphs: Record<string, string> = {
  kiwi: "KI",
  tui: "TU",
  kea: "KE",
  fern: "FE",
  wave: "WA",
  mountain: "MT",
  "anime-sky": "SK",
  "anime-tide": "TD",
  "anime-spark": "SP",
  "anime-grove": "GR",
  "anime-dusk": "DU",
  "anime-summit": "SU",
  "chibi-nova": "NO",
  "chibi-rio": "RI",
  "chibi-pixel": "PX",
  "chibi-bloom": "BL",
  "chibi-ember": "EM",
  "chibi-luna": "LU",
  "pocket-astra": "AS",
  "pocket-momo": "MO",
  "pocket-kiro": "KR",
  "pocket-yuzu": "YU",
  "pocket-sol": "SO",
  "pocket-lumi": "LI",
  "orbit-vesper": "VE",
  "orbit-cirrus": "CI",
  "orbit-moss": "MO",
  "orbit-pip": "PI",
  "orbit-flicker": "FL",
  "orbit-ripple": "RI",
  "afterglow-aria": "AR",
  "afterglow-jun": "JU",
  "afterglow-sable": "SA",
  "afterglow-teo": "TE",
  "afterglow-mira": "MI",
  "afterglow-ren": "RE",
  "daydream-tala": "TA",
  "daydream-cian": "CI",
  "daydream-priya": "PR",
  "daydream-leo": "LE",
  "daydream-aya": "AY",
  "daydream-robin": "RO",
  "popchibi-zuri": "ZU",
  "popchibi-milo": "MI",
  "popchibi-naia": "NA",
  "popchibi-kenji": "KE",
  "popchibi-sana": "SA",
  "popchibi-jules": "JU",
  "panelpop-rowan": "RW",
  "panelpop-maeve": "MV",
  "panelpop-luca": "LU",
  "panelpop-celeste": "CE",
  "panelpop-theo": "TH",
  "panelpop-quinn": "QU",
  "neoclay-amara": "AM",
  "neoclay-jude": "JU",
  "neoclay-noor": "NO",
  "neoclay-mateo": "MA",
  "neoclay-elise": "EL",
  "neoclay-soren": "SO",
  "mech-axiom": "AX",
  "mech-vela": "VE",
  "mech-kestrel": "KE",
  "mech-rune": "RU",
  "mech-solace": "SO",
  "mech-torque": "TO",
  "prism-nia": "NI",
  "prism-elio": "EL",
  "prism-samira": "SA",
  "prism-cass": "CA",
  "prism-davi": "DA",
  "prism-yuna": "YU",
};
const avatarCollections = [
  {
    id: "harbour",
    title: "Collection 01 · Harbour emblems",
    description: "Aotearoa birds and landscape symbols in the original enamel style.",
    avatars: ["kiwi", "tui", "kea", "fern", "wave", "mountain"],
  },
  {
    id: "anime",
    title: "Collection 02 · Anime neighbours",
    description: "Original adult characters with a matching anime Kiwi reaction style.",
    avatars: ["anime-sky", "anime-tide", "anime-spark", "anime-grove", "anime-dusk", "anime-summit"],
  },
  {
    id: "chibi",
    title: "Collection 03 · Chibi crew",
    description: "A playful international cast paired with animated Sun-drop reactions.",
    avatars: ["chibi-nova", "chibi-rio", "chibi-pixel", "chibi-bloom", "chibi-ember", "chibi-luna"],
  },
  {
    id: "pocket",
    title: "Collection 04 · Pocket worlds",
    description: "Six global storybook neighbours paired with animated Glowbean reactions.",
    avatars: ["pocket-astra", "pocket-momo", "pocket-kiro", "pocket-yuzu", "pocket-sol", "pocket-lumi"],
  },
  {
    id: "orbit",
    title: "Collection 05 · Little orbit",
    description: "Six head-only fantasy companions paired with expressive Luma face reactions.",
    avatars: ["orbit-vesper", "orbit-cirrus", "orbit-moss", "orbit-pip", "orbit-flicker", "orbit-ripple"],
  },
  {
    id: "afterglow",
    title: "Collection 06 · Afterglow crew",
    description: "Six globally styled adult anime portraits paired with animated Bao face reactions.",
    avatars: ["afterglow-aria", "afterglow-jun", "afterglow-sable", "afterglow-teo", "afterglow-mira", "afterglow-ren"],
  },
  {
    id: "daydream",
    title: "Collection 07 · Daydream studio",
    description: "Six imaginative adult anime portraits paired with Bao's brighter Daydream expressions.",
    avatars: ["daydream-tala", "daydream-cian", "daydream-priya", "daydream-leo", "daydream-aya", "daydream-robin"],
  },
  {
    id: "popchibi",
    title: "Collection 08 · Pop chibi",
    description: "Six bold adult chibi portraits paired with Stackie's expressive burger heads.",
    avatars: ["popchibi-zuri", "popchibi-milo", "popchibi-naia", "popchibi-kenji", "popchibi-sana", "popchibi-jules"],
  },
  {
    id: "panelpop",
    title: "Collection 09 · Panel pop",
    description: "Six adult indie-comic portraits paired with Stackie's high-energy comic-burst expressions.",
    avatars: ["panelpop-rowan", "panelpop-maeve", "panelpop-luca", "panelpop-celeste", "panelpop-theo", "panelpop-quinn"],
  },
  {
    id: "neoclay",
    title: "Collection 10 · Neon clay",
    description: "Six tactile future-clay portraits paired with Stackie's elastic vinyl expressions.",
    avatars: ["neoclay-amara", "neoclay-jude", "neoclay-noor", "neoclay-mateo", "neoclay-elise", "neoclay-soren"],
  },
  {
    id: "mech",
    title: "Collection 11 · Mech signal",
    description: "Six hard-surface machine identities paired with Mechbit's expressive signal visor.",
    avatars: ["mech-axiom", "mech-vela", "mech-kestrel", "mech-rune", "mech-solace", "mech-torque"],
  },
  {
    id: "prism",
    title: "Collection 12 · Prism club",
    description: "Six head-only adult chibi portraits paired with Jellystar's borderless gummy expressions.",
    avatars: ["prism-nia", "prism-elio", "prism-samira", "prism-cass", "prism-davi", "prism-yuna"],
  },
] as const;
const characterSceneSources: Record<CharacterAppearance["scene"], string> = {
  "harbour-night": "/character-assets/scene-harbour-night.png",
  "morning-mist": "/character-assets/scene-morning-mist.png",
  "moon-passage": "/character-assets/scene-moon-passage.png",
};
const characterRobeSources: Record<CharacterAppearance["robes"], string> = {
  "coastal-blue": "/character-assets/robes-coastal-blue-complete-v2.png",
  "mist-scholar": "/character-assets/robes-mist-scholar-complete-v2.png",
  "night-watch": "/character-assets/robes-night-watch-complete-v2.png",
};
const characterHairSources: Partial<Record<CharacterAppearance["hair"], string>> = {
  "tide-knot": "/character-assets/hair-tide-knot.png",
  windswept: "/character-assets/hair-windswept.png",
};
const characterRelicSources: Partial<Record<CharacterAppearance["relic"], string>> = {
  "jade-compass": "/character-assets/relic-jade-compass.png",
  "star-token": "/character-assets/relic-star-token.png",
};
const characterAppearanceSlots: Array<{
  key: CharacterAppearanceSlot;
  label: string;
  description: string;
}> = [
  { key: "form", label: "Form", description: "Sets the full figure's stance and stage presence without changing abilities." },
  { key: "hair", label: "Hair", description: "Adds a style layer to the complete character. It never changes your separate system avatar." },
  { key: "robes", label: "Robes", description: "Sets the garment colours carried by the character stage and profile card." },
  { key: "relic", label: "Relic", description: "A small identity object displayed with the equipped look; it never grants privileges." },
  { key: "scene", label: "Scene", description: "Chooses the original coastal setting behind your character." },
];
const characterAppearanceOptions: Record<CharacterAppearanceSlot, Array<{ value: string; label: string; detail: string }>> = {
  form: [
    { value: "harbour", label: "Harbour form", detail: "Balanced and open, with a broad coastal frame." },
    { value: "wayfinder", label: "Wayfinder form", detail: "A taller field mark for travellers and returning regulars." },
    { value: "sentinel", label: "Sentinel form", detail: "A grounded frame with a quiet watch posture." },
  ],
  hair: [
    { value: "portrait", label: "Portrait original", detail: "Keeps the official portrait exactly as its artist supplied it." },
    { value: "tide-knot", label: "Tide knot", detail: "A sculpted coastal crest aligned around your official portrait." },
    { value: "windswept", label: "Windswept", detail: "A flowing night-blue crest with an open face frame." },
  ],
  robes: [
    { value: "coastal-blue", label: "Coastal blue", detail: "Deep blue cloth with a live-aqua seam." },
    { value: "mist-scholar", label: "Mist scholar", detail: "Pale coastal cloth with blue-grey structure." },
    { value: "night-watch", label: "Night watch", detail: "Dark harbour cloth with a restrained amber fastening." },
  ],
  relic: [
    { value: "none", label: "No relic", detail: "A clear stage with no additional identity object." },
    { value: "jade-compass", label: "Jade compass", detail: "A decorative wayfinding token with no gameplay effect." },
    { value: "star-token", label: "Southern star token", detail: "A decorative night-sky token with no gameplay effect." },
  ],
  scene: [
    { value: "harbour-night", label: "Harbour night", detail: "A calm, original coastal night scene." },
    { value: "morning-mist", label: "Morning mist", detail: "A bright mountain-and-water scene for daylight profiles." },
    { value: "moon-passage", label: "Moon passage", detail: "A quiet moonlit passage between coastal headlands." },
  ],
};

function characterAppearanceLabel(slot: CharacterAppearanceSlot, value: string) {
  return characterAppearanceOptions[slot].find((option) => option.value === value)?.label ?? value;
}

function characterFigure(user: User, variant: "identity" | "studio") {
  const figure = document.createElement("div");
  figure.className = `character-figure character-figure-${variant}`;
  figure.dataset.form = user.appearance.form;
  figure.setAttribute(
    "aria-label",
    `Complete character, ${characterAppearanceLabel("hair", user.appearance.hair)}, ${characterAppearanceLabel("robes", user.appearance.robes)}, ${characterAppearanceLabel("relic", user.appearance.relic)}`,
  );
  figure.setAttribute("role", "img");

  const form = document.createElement("span");
  form.className = "character-form-aura";
  form.setAttribute("aria-hidden", "true");

  const robes = document.createElement("img");
  robes.className = "character-figure-layer character-figure-robes";
  robes.src = characterRobeSources[user.appearance.robes];
  robes.alt = "";
  robes.decoding = "async";

  figure.append(form, robes);

  const hairSource = characterHairSources[user.appearance.hair];
  if (hairSource) {
    const hair = document.createElement("img");
    hair.className = "character-figure-layer character-figure-hair";
    hair.src = hairSource;
    hair.alt = "";
    hair.decoding = "async";
    figure.append(hair);
  }

  const relicSource = characterRelicSources[user.appearance.relic];
  if (relicSource) {
    const relic = document.createElement("img");
    relic.className = "character-figure-relic";
    relic.src = relicSource;
    relic.alt = "";
    relic.decoding = "async";
    figure.append(relic);
  }
  return figure;
}

const baseReactions = ["👍", "❤️", "😂", "👋"];
const kiwiReactions = ["🥝", "✨", "🙌", "💚"];
const reactionVisuals: Record<string, { art: string; label: string }> = {
  "👍": { art: "agree", label: "Good call" },
  "❤️": { art: "care", label: "Care" },
  "😂": { art: "laugh", label: "Laugh" },
  "👋": { art: "welcome", label: "Welcome" },
  "🥝": { art: "kiwichat", label: "ManyHellos" },
  "✨": { art: "idea", label: "Bright idea" },
  "🙌": { art: "celebrate", label: "Celebrate" },
  "💚": { art: "support", label: "Support" },
};
const ROOM_NOTICE_LIMIT = 40;
const ROOM_NOTICE_RETENTION_MS = 30 * 60_000;
const MESSAGE_GROUP_WINDOW_MS = 5 * 60_000;
const messageEffects = new Set(["none", "outfit", "glow", "shimmer", "aurora"]);
const outfitRobes = new Set(["coastal-blue", "mist-scholar", "night-watch"]);
let selectedStickerPackId: StickerPackId | null = null;
let emojiPickerView: "stickers" | "everyday" = "stickers";

const state: {
  homeVisible: boolean;
  homePreview: HomePreviewState;
  viewer: User | null;
  presenceStatus: PresenceStatus;
  roomId: RoomId;
  rooms: RoomSummary[];
  messages: Map<number, Message>;
  highlights: RoomHighlight[];
  roomNotices: RoomNotice[];
  members: PresentUser[];
  onlineCount: number;
  guestCount: number;
  blockedIds: Set<string>;
  blockedUsers: User[];
  progression: ProgressionLevel[];
  classicMode: boolean;
  colorTheme: ColorThemePreference;
  chatBackground: ChatBackgroundId;
  focusEnabled: boolean;
  focusIds: Set<string>;
  clearedThroughByView: Map<string, number>;
  roomStreamOpen: boolean;
  recentRoomActivity: RoomActivitySample[];
  focusSuggestionVisible: boolean;
  effectPending: Set<number>;
  liveMessageMotionPending: Set<number>;
  roomNoticeMotionPending: Set<string>;
  gameClueFoundMotionPending: Set<string>;
  gameResultMotionPendingRoomId: string | null;
  sanction: Sanction | null;
  sanctionSyncing: boolean;
  slowMode: RoomSlowMode | null;
  slowModeNextAt: number;
  slowModeTimer: number | null;
  chatRateLimitedUntil: number;
  gameChatRateLimitedUntil: number;
  chatRateLimitTimer: number | null;
  announcement: string;
  replyTo: number | null;
  socket: WebSocket | null;
  connectionReady: boolean;
  reconnectTimer: number | null;
  reconnectAttempt: number;
  reconnectNotBefore: number;
  catchupAfter: number | null;
  catchupMode: "recent" | "recovery" | "unread";
  roomState: RoomMemberState | null;
  returnStartSeq: number | null;
  returnViewOpened: boolean;
  readCursorTimer: number | null;
  recallExpiryTimer: number | null;
  readCursorSent: number;
  sanctionTimer: number | null;
  sanctionRetryTimer: number | null;
  sanctionRetryUsed: boolean;
  pending: Map<string, PendingMessage>;
  unreadCount: number;
  activeTarget: User | null;
  atGameCount: number;
  gameRooms: GameRoomSummary[];
  game: GameState | null;
  gamePending: Map<string, string>;
  notifications: NotificationItem[];
  savedNotifications: NotificationItem[];
  notificationUnreadCount: number;
  notificationSavedCount: number;
  notificationSavedLimit: number;
  notificationRetentionDays: number;
  inboxView: "all" | "saved";
  quietWallEntry: GuestbookEntry | null;
  quietWallStatus: "idle" | "loading" | "loaded";
} = {
  homeVisible: true,
  homePreview: initialHomePreviewState(localStorage.getItem("kiwichat:room") === "reo-maori" ? "reo-maori" : "nz-lobby"),
  viewer: null,
  presenceStatus: savedPresenceStatus(),
  roomId: localStorage.getItem("kiwichat:room") === "reo-maori" ? "reo-maori" : "nz-lobby",
  rooms: fallbackRooms,
  messages: new Map(),
  highlights: [],
  roomNotices: [],
  members: [],
  onlineCount: 0,
  guestCount: 0,
  blockedIds: new Set(),
  blockedUsers: [],
  progression: [],
  classicMode: localStorage.getItem("kiwichat:classic-mode") === "true",
  colorTheme: savedColorTheme(),
  chatBackground: savedChatBackground(),
  focusEnabled: false,
  focusIds: new Set(),
  clearedThroughByView: new Map(),
  roomStreamOpen: false,
  recentRoomActivity: [],
  focusSuggestionVisible: false,
  effectPending: new Set(),
  liveMessageMotionPending: new Set(),
  roomNoticeMotionPending: new Set(),
  gameClueFoundMotionPending: new Set(),
  gameResultMotionPendingRoomId: null,
  sanction: null,
  sanctionSyncing: false,
  slowMode: null,
  slowModeNextAt: 0,
  slowModeTimer: null,
  chatRateLimitedUntil: 0,
  gameChatRateLimitedUntil: 0,
  chatRateLimitTimer: null,
  announcement: fallbackRooms.find((room) => room.id === (localStorage.getItem("kiwichat:room") === "reo-maori" ? "reo-maori" : "nz-lobby"))!.announcement,
  replyTo: null,
  socket: null,
  connectionReady: false,
  reconnectTimer: null,
  reconnectAttempt: 0,
  reconnectNotBefore: 0,
  catchupAfter: null,
  catchupMode: "recent",
  roomState: null,
  returnStartSeq: null,
  returnViewOpened: false,
  readCursorTimer: null,
  recallExpiryTimer: null,
  readCursorSent: 0,
  sanctionTimer: null,
  sanctionRetryTimer: null,
  sanctionRetryUsed: false,
  pending: new Map(),
  unreadCount: 0,
  activeTarget: null,
  atGameCount: 0,
  gameRooms: [],
  game: null,
  gamePending: new Map(),
  notifications: [],
  savedNotifications: [],
  notificationUnreadCount: 0,
  notificationSavedCount: 0,
  notificationSavedLimit: 200,
  notificationRetentionDays: 45,
  inboxView: "all",
  quietWallEntry: null,
  quietWallStatus: "idle",
};

const app = document.querySelector<HTMLDivElement>("#app")!;
app.innerHTML = `
  <div class="app-shell">
    <div class="harbour-atmosphere" aria-hidden="true">
      <span class="harbour-current harbour-current-a"></span>
      <span class="harbour-current harbour-current-b"></span>
      <span class="harbour-beacon"></span>
    </div>
    <header class="topbar">
      <a class="brand" href="/" aria-label="ManyHellos home">
        <span class="brand-mark" aria-hidden="true"><img src="/brand/manyhellos-mark.svg" width="40" height="40" alt="" /></span>
        <span class="brand-name"><span>Many</span><span>Hellos</span></span>
        <span class="beta">BETA</span>
      </a>
      <div class="topbar-center" role="status" aria-live="polite" aria-atomic="true"><span class="live-dot" aria-hidden="true"></span><strong id="online-count">0</strong><span id="online-label">in this room</span></div>
      <div class="topbar-actions">
        <button class="game-entry" id="game-button" type="button" aria-expanded="false"><span class="game-long">Mystery table</span><span class="game-short">Mystery</span><span class="game-count hidden" id="game-count"></span></button>
        <button class="icon-button mobile-only" id="members-toggle" type="button" aria-label="Show online members" aria-controls="members-panel" aria-expanded="false">People</button>
        <button class="notification-button hidden" id="notification-button" type="button" aria-label="Open your inbox"><span class="notification-icon" aria-hidden="true">✉</span><span class="notification-label">Inbox</span><span class="notification-badge hidden" id="notification-badge">0</span></button>
        <button class="text-button" id="wall-button" type="button">Message wall</button>
        <button class="text-button" id="feedback-button" type="button">Feedback</button>
        <button class="text-button" id="info-button" type="button">Rules & privacy</button>
        <button class="theme-toggle" id="theme-button" type="button" aria-haspopup="dialog"><span class="theme-icon" aria-hidden="true">☾</span><span class="theme-label">Night</span></button>
        <button class="primary small" id="auth-button" type="button">Sign in</button>
        <button class="avatar-button hidden" id="profile-button" type="button" aria-label="Open your profile"></button>
      </div>
    </header>

    ${homeMarkup()}

    <main class="lobby-layout hidden" id="room-screen">
      <aside class="members-panel" id="members-panel" aria-label="Online members">
        <div class="panel-heading"><div><span class="eyebrow">ONLINE NOW</span><h2>In the room</h2></div><button class="icon-button mobile-only" id="members-close" aria-label="Close member list">×</button></div>
        <div id="member-list" class="member-list"></div>
        <div class="guest-count" id="guest-count"></div>
      </aside>

      <section class="chat-panel" aria-label="New Zealand Lobby chat">
        <div class="room-heading">
          <div class="room-title-block">
            <span class="eyebrow" id="room-eyebrow">NATIONAL ROOM · AOTEAROA</span>
            <div class="room-title-row"><h1 id="room-title">New Zealand Lobby</h1><span class="room-language" id="room-language">Open conversation</span></div>
          </div>
          <div class="room-controls">
            <div class="connection-state" id="connection-state"><span></span>Connecting</div>
            <button class="classic-toggle hidden" id="slow-mode-button" type="button">Slow mode</button>
            <button class="classic-toggle background-toggle" id="background-button" type="button" aria-haspopup="dialog"><span aria-hidden="true">▧</span><span class="background-label">Background</span></button>
            <button class="classic-toggle clear-view-toggle" id="clear-view-button" type="button" aria-label="Clear this device's current chat view" title="Hide loaded messages on this device without deleting chat history"><span class="clear-view-icon" aria-hidden="true">⌫</span><span class="clear-view-label">Clear on this device</span></button>
            <button class="classic-toggle" id="classic-toggle" type="button" aria-pressed="false" title="Hide artistic fonts and message effects">Classic view</button>
            <button class="classic-toggle mobile-only" id="mobile-info-button" type="button" aria-haspopup="dialog">Room menu</button>
          </div>
        </div>
        <nav class="room-tabs" id="room-tabs" aria-label="Chat rooms"></nav>
        <nav class="room-tools" id="room-tools" aria-label="Community tools"></nav>
        <div class="announcement" id="announcement"><span aria-hidden="true">✦</span><p></p></div>
        <div class="slow-mode-banner hidden" id="slow-mode-banner" role="status"></div>
        <div class="view-toolbar hidden" id="view-toolbar">
          <div class="view-mode">
            <span class="view-mode-label">Message view</span>
            <div class="view-segments" role="group" aria-label="Choose which public messages to show">
              <button id="all-view-button" type="button" aria-pressed="true">All</button>
              <button id="focus-view-button" type="button" aria-pressed="false">Focus <span id="focus-view-count">0</span></button>
            </div>
          </div>
          <div class="view-actions">
            <button id="focus-edit-button" type="button">Choose people</button>
            <button class="hidden" id="room-stream-button" type="button" aria-controls="room-stream" aria-expanded="false">Open room stream</button>
            <button id="block-list-button" type="button">Blocked <span id="block-list-count">0</span></button>
          </div>
        </div>
        <aside class="focus-suggestion hidden" id="focus-suggestion" aria-labelledby="focus-suggestion-title">
          <div><strong id="focus-suggestion-title">The room is moving quickly</strong><p>Focus shows people you choose, plus your own messages and public @mentions.</p></div>
          <div class="focus-suggestion-actions"><button class="primary" id="focus-suggestion-choose" type="button">Choose people</button><button class="secondary" id="focus-suggestion-dismiss" type="button">Not now</button></div>
        </aside>
        <div class="conversation-workspace" id="conversation-workspace">
          <div class="messages" id="messages" role="log" aria-live="off"></div>
          <aside class="room-stream hidden" id="room-stream" aria-label="Full public room stream">
            <header><div><strong>Room stream</strong><p>All loaded public messages except people you blocked. Read-only.</p></div><button id="room-stream-close" type="button">Close</button></header>
            <div class="room-stream-messages" id="room-stream-messages" role="log" aria-live="off"></div>
          </aside>
        </div>
        <div class="sr-only" id="message-announcer" aria-live="polite" aria-atomic="true"></div>
        <div class="new-message-bar hidden" id="new-message-bar">
          <button id="new-message-button" type="button">New messages ↓</button>
          <button class="history-page-button hidden" id="history-page-button" type="button">Load next 100</button>
        </div>
        <div id="sanction-banner" class="sanction-banner hidden"></div>
        <div class="composer-wrap" id="composer-wrap">
          <div class="reply-strip hidden" id="reply-strip">
            <div class="reply-strip-copy">
              <span class="reply-strip-meta">Quoting <strong></strong><time></time></span>
              <p></p>
            </div>
            <button type="button" aria-label="Cancel quoted reply">×</button>
          </div>
          <form class="composer" id="composer">
            <label class="sr-only" for="message-input">Message</label>
            <textarea id="message-input" rows="1" maxlength="500" placeholder="Say something to the room…"></textarea>
            <div class="composer-bottom"><span class="composer-hint" id="composer-hint">Enter to send · Shift+Enter for a new line</span><span class="message-count" id="message-count">0 / 500</span><button class="emoji-button" id="emoji-button" type="button" aria-haspopup="dialog" aria-controls="emoji-picker" aria-expanded="false" aria-label="Choose text emoji or a sticker"><span aria-hidden="true">☺</span></button><div class="emoji-picker hidden" id="emoji-picker" role="dialog" aria-label="Choose text emoji or a sticker"></div><button class="send-button" type="submit" disabled><span class="send-label">Send</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 12 16-8-6 16-3-6-7-2Z"/><path d="m11 14 9-10"/></svg></button></div>
          </form>
          <div class="guest-gate hidden" id="guest-gate"><p id="guest-gate-copy">Read what people are saying, then join when you’re ready.</p><button class="primary" id="join-button">Join this room</button></div>
        </div>
      </section>

      <aside class="identity-panel" aria-label="Your ManyHellos identity">
        <div id="identity-card" class="identity-card guest-card">
          <span class="eyebrow">YOUR PROFILE</span>
          <div class="identity-avatar">?</div>
          <h2>Browsing as a guest</h2>
          <p>Read the room for free. Sign in to chat and earn XP.</p>
          <button class="primary wide" id="identity-signin">Sign in to join</button>
        </div>
        <div class="room-note"><span id="room-note-label">ROOM RULE</span><p id="room-note-copy">Public text only. No links, social accounts, private messages, uploads, or bots.</p></div>
        <div class="identity-signoff" aria-label="ManyHellos — a more human internet for Aotearoa and beyond">
          <p>A more human internet<br />for Aotearoa, and beyond.</p>
          <span><img src="/brand/manyhellos-mark.svg" width="28" height="28" alt="" />Many<span>Hellos</span></span>
        </div>
      </aside>
    </main>

    <section class="game-screen hidden" id="game-screen" aria-label="Temporary mystery game room">
      <header class="game-hero">
        <img id="game-hero-image" src="/game-assets/harbour-ledger-twilight.webp" width="1672" height="940" loading="lazy" decoding="async" alt="Illustrated New Zealand harbour town at twilight, the setting of the mystery">
        <div class="game-hero-shade">
          <div class="game-heading">
            <span class="game-phase" id="game-phase">Waiting at the wharf</span>
            <h1 id="game-title">The Missing Harbour Ledger</h1>
            <p id="game-premise"></p>
          </div>
          <div class="game-hero-actions">
            <button class="classic-toggle" id="game-classic-toggle" type="button" aria-pressed="false">Classic view</button>
            <button class="game-exit" id="game-leave" type="button">Return to lobby</button>
          </div>
        </div>
      </header>
      <div class="game-layout">
        <aside class="game-dossier" id="game-dossier" aria-label="Your sealed role"></aside>
        <section class="game-chat" aria-label="Game table discussion">
          <header><div><h2>Table talk</h2><p>Only players at this table can read this chat.</p></div><span id="game-chat-count"></span></header>
          <div class="game-messages" id="game-messages" role="log" aria-live="polite"></div>
          <form class="game-composer" id="game-composer">
            <label class="sr-only" for="game-input">Message the game table</label>
            <textarea id="game-input" rows="2" maxlength="500" placeholder="Compare stories, ask a question, or bluff…"></textarea>
            <button id="game-send-button" type="submit">Send to table</button>
            <p class="game-composer-hint" id="game-composer-hint" aria-live="polite">Enter to send · Shift+Enter for a new line</p>
          </form>
        </section>
        <aside class="game-board" id="game-board" aria-label="Case actions"></aside>
      </div>
    </section>

    <aside class="game-drawer hidden" id="game-drawer" aria-label="Mystery tables">
      <header><div><h2>Tonight's mystery</h2><p>A temporary room for 3–6 people.</p></div><button class="drawer-close" id="game-drawer-close" type="button" aria-label="Close mystery table panel">×</button></header>
      <div id="game-room-list" class="game-room-list"></div>
    </aside>
  </div>

  <div class="modal-backdrop hidden" id="modal-backdrop">
    <section class="modal" id="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <button class="modal-close" id="modal-close" aria-label="Close">×</button>
      <div id="modal-content"></div>
    </section>
  </div>
  <section class="blue-screen hidden" id="blue-screen" role="alertdialog" aria-modal="true" aria-labelledby="blue-screen-title">
    <div class="blue-screen-inner">
      <p class="blue-screen-code">MANYHELLOS_ROOM_EXCEPTION</p>
      <h2 id="blue-screen-title">You have been temporarily removed from ManyHellos.</h2>
      <dl>
        <div><dt>Reason</dt><dd id="blue-screen-reason"></dd></div>
        <div><dt>Action</dt><dd id="blue-screen-action"></dd></div>
        <div><dt>Moderator</dt><dd id="blue-screen-moderator"></dd></div>
        <div><dt>Reference</dt><dd id="blue-screen-reference"></dd></div>
      </dl>
      <p>This notice affects ManyHellos only. Your device is fine.</p>
      <div class="blue-screen-actions"><button class="blue-details" id="blue-screen-details">View action & appeal</button><button class="blue-continue" id="blue-screen-continue">Return to chat view</button></div>
    </div>
  </section>
  <div class="toast-region" id="toast-region" aria-live="polite"></div>
`;

const elements = {
  brand: document.querySelector<HTMLAnchorElement>(".brand")!,
  home: document.querySelector<HTMLElement>("#home-screen")!,
  homeTotalOnline: document.querySelector<HTMLElement>("#home-total-online")!,
  homeTotalOnlineLabel: document.querySelector<HTMLElement>("#home-total-online-label")!,
  homeNzCount: document.querySelector<HTMLElement>("#home-nz-count")!,
  homeMaoriCount: document.querySelector<HTMLElement>("#home-maori-count")!,
  homeStoryRoom: document.querySelector<HTMLElement>("#home-story-room")!,
  homeStoryStage: document.querySelector<HTMLElement>("#home-story-stage")!,
  homeStoryContent: document.querySelector<HTMLElement>("#home-story-content")!,
  homeStoryQuote: document.querySelector<HTMLElement>("#home-story-quote")!,
  homeStoryInitials: document.querySelector<HTMLElement>("#home-story-initials")!,
  homeStoryAuthor: document.querySelector<HTMLElement>("#home-story-author")!,
  homeSafetyTitle: document.querySelector<HTMLElement>("#home-safety-title")!,
  homeSafetyCopy: document.querySelector<HTMLElement>("#home-safety-copy")!,
  homeSafetyContent: document.querySelector<HTMLElement>("#home-safety-content")!,
  homePreviewStatus: document.querySelector<HTMLElement>("#home-preview-status")!,
  homeRefreshButton: document.querySelector<HTMLButtonElement>("#home-refresh")!,
  homeEnterButton: document.querySelector<HTMLButtonElement>("#home-enter")!,
  homeEnterBottomButton: document.querySelector<HTMLButtonElement>("#home-enter-bottom")!,
  homeStoryEnterButton: document.querySelector<HTMLButtonElement>("#home-story-enter")!,
  homeRoomSelection: document.querySelector<HTMLElement>("#home-room-selection")!,
  homeOrbit: document.querySelector<HTMLButtonElement>("#home-orbit")!,
  homeOrbitNodes: document.querySelector<HTMLElement>("#home-orbit-nodes")!,
  homeOrbitCaption: document.querySelector<HTMLElement>("#home-orbit-caption")!,
  online: document.querySelector<HTMLElement>("#online-count")!,
  chatPanel: document.querySelector<HTMLElement>(".chat-panel")!,
  onlineLabel: document.querySelector<HTMLElement>("#online-label")!,
  roomTabs: document.querySelector<HTMLElement>("#room-tabs")!,
  roomTools: document.querySelector<HTMLElement>("#room-tools")!,
  roomTitle: document.querySelector<HTMLElement>("#room-title")!,
  roomEyebrow: document.querySelector<HTMLElement>("#room-eyebrow")!,
  roomLanguage: document.querySelector<HTMLElement>("#room-language")!,
  roomNoteLabel: document.querySelector<HTMLElement>("#room-note-label")!,
  roomNoteCopy: document.querySelector<HTMLElement>("#room-note-copy")!,
  guestCount: document.querySelector<HTMLElement>("#guest-count")!,
  members: document.querySelector<HTMLElement>("#member-list")!,
  memberPanel: document.querySelector<HTMLElement>("#members-panel")!,
  messages: document.querySelector<HTMLElement>("#messages")!,
  messageAnnouncer: document.querySelector<HTMLElement>("#message-announcer")!,
  newMessageBar: document.querySelector<HTMLElement>("#new-message-bar")!,
  newMessageButton: document.querySelector<HTMLButtonElement>("#new-message-button")!,
  historyPageButton: document.querySelector<HTMLButtonElement>("#history-page-button")!,
  announcement: document.querySelector<HTMLElement>("#announcement p")!,
  connection: document.querySelector<HTMLElement>("#connection-state")!,
  classicToggle: document.querySelector<HTMLButtonElement>("#classic-toggle")!,
  mobileInfoButton: document.querySelector<HTMLButtonElement>("#mobile-info-button")!,
  themeButton: document.querySelector<HTMLButtonElement>("#theme-button")!,
  backgroundButton: document.querySelector<HTMLButtonElement>("#background-button")!,
  clearViewButton: document.querySelector<HTMLButtonElement>("#clear-view-button")!,
  slowModeButton: document.querySelector<HTMLButtonElement>("#slow-mode-button")!,
  slowModeBanner: document.querySelector<HTMLElement>("#slow-mode-banner")!,
  viewToolbar: document.querySelector<HTMLElement>("#view-toolbar")!,
  allViewButton: document.querySelector<HTMLButtonElement>("#all-view-button")!,
  focusViewButton: document.querySelector<HTMLButtonElement>("#focus-view-button")!,
  focusViewCount: document.querySelector<HTMLElement>("#focus-view-count")!,
  focusEditButton: document.querySelector<HTMLButtonElement>("#focus-edit-button")!,
  roomStreamButton: document.querySelector<HTMLButtonElement>("#room-stream-button")!,
  blockListButton: document.querySelector<HTMLButtonElement>("#block-list-button")!,
  blockListCount: document.querySelector<HTMLElement>("#block-list-count")!,
  focusSuggestion: document.querySelector<HTMLElement>("#focus-suggestion")!,
  focusSuggestionChoose: document.querySelector<HTMLButtonElement>("#focus-suggestion-choose")!,
  focusSuggestionDismiss: document.querySelector<HTMLButtonElement>("#focus-suggestion-dismiss")!,
  conversationWorkspace: document.querySelector<HTMLElement>("#conversation-workspace")!,
  roomStream: document.querySelector<HTMLElement>("#room-stream")!,
  roomStreamMessages: document.querySelector<HTMLElement>("#room-stream-messages")!,
  roomStreamClose: document.querySelector<HTMLButtonElement>("#room-stream-close")!,
  composer: document.querySelector<HTMLFormElement>("#composer")!,
  composerWrap: document.querySelector<HTMLElement>("#composer-wrap")!,
  input: document.querySelector<HTMLTextAreaElement>("#message-input")!,
  composerHint: document.querySelector<HTMLElement>("#composer-hint")!,
  messageCount: document.querySelector<HTMLElement>("#message-count")!,
  emojiButton: document.querySelector<HTMLButtonElement>("#emoji-button")!,
  emojiPicker: document.querySelector<HTMLElement>("#emoji-picker")!,
  sendButton: document.querySelector<HTMLButtonElement>(".send-button")!,
  guestGate: document.querySelector<HTMLElement>("#guest-gate")!,
  guestGateCopy: document.querySelector<HTMLElement>("#guest-gate-copy")!,
  joinButton: document.querySelector<HTMLButtonElement>("#join-button")!,
  identity: document.querySelector<HTMLElement>("#identity-card")!,
  authButton: document.querySelector<HTMLButtonElement>("#auth-button")!,
  profileButton: document.querySelector<HTMLButtonElement>("#profile-button")!,
  notificationButton: document.querySelector<HTMLButtonElement>("#notification-button")!,
  notificationBadge: document.querySelector<HTMLElement>("#notification-badge")!,
  replyStrip: document.querySelector<HTMLElement>("#reply-strip")!,
  sanction: document.querySelector<HTMLElement>("#sanction-banner")!,
  modalBackdrop: document.querySelector<HTMLElement>("#modal-backdrop")!,
  modalContent: document.querySelector<HTMLElement>("#modal-content")!,
  blueScreen: document.querySelector<HTMLElement>("#blue-screen")!,
  blueReason: document.querySelector<HTMLElement>("#blue-screen-reason")!,
  blueAction: document.querySelector<HTMLElement>("#blue-screen-action")!,
  blueModerator: document.querySelector<HTMLElement>("#blue-screen-moderator")!,
  blueReference: document.querySelector<HTMLElement>("#blue-screen-reference")!,
  blueDetails: document.querySelector<HTMLButtonElement>("#blue-screen-details")!,
  blueContinue: document.querySelector<HTMLButtonElement>("#blue-screen-continue")!,
  toast: document.querySelector<HTMLElement>("#toast-region")!,
  modal: document.querySelector<HTMLElement>("#modal")!,
  lobby: document.querySelector<HTMLElement>(".lobby-layout")!,
  gameButton: document.querySelector<HTMLButtonElement>("#game-button")!,
  gameCount: document.querySelector<HTMLElement>("#game-count")!,
  gameDrawer: document.querySelector<HTMLElement>("#game-drawer")!,
  gameDrawerClose: document.querySelector<HTMLButtonElement>("#game-drawer-close")!,
  gameRoomList: document.querySelector<HTMLElement>("#game-room-list")!,
  gameScreen: document.querySelector<HTMLElement>("#game-screen")!,
  gameHeroImage: document.querySelector<HTMLImageElement>("#game-hero-image")!,
  gamePhase: document.querySelector<HTMLElement>("#game-phase")!,
  gameTitle: document.querySelector<HTMLElement>("#game-title")!,
  gamePremise: document.querySelector<HTMLElement>("#game-premise")!,
  gameLeave: document.querySelector<HTMLButtonElement>("#game-leave")!,
  gameClassicToggle: document.querySelector<HTMLButtonElement>("#game-classic-toggle")!,
  gameDossier: document.querySelector<HTMLElement>("#game-dossier")!,
  gameBoard: document.querySelector<HTMLElement>("#game-board")!,
  gameMessages: document.querySelector<HTMLElement>("#game-messages")!,
  gameChatCount: document.querySelector<HTMLElement>("#game-chat-count")!,
  gameComposer: document.querySelector<HTMLFormElement>("#game-composer")!,
  gameInput: document.querySelector<HTMLTextAreaElement>("#game-input")!,
  gameSendButton: document.querySelector<HTMLButtonElement>("#game-send-button")!,
  gameComposerHint: document.querySelector<HTMLElement>("#game-composer-hint")!,
  appShell: document.querySelector<HTMLElement>(".app-shell")!,
  memberToggle: document.querySelector<HTMLButtonElement>("#members-toggle")!,
};

const homeMotionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
const seenGameMotion = new Set<string>();
let presenceMotionReady = false;
let homeUpdateTimer: number | null = null;
let homeSettleTimer: number | null = null;
let homeHasRevealed = false;
let homeInViewport = true;
let homeRoomAnimations: Animation[] = [];
let homeStoryGhost: HTMLElement | null = null;
let homeHeroTimers: number[] = [];

function homeStory() {
  return HOME_STORIES[state.homePreview.storyIndex] ?? HOME_STORIES[0]!;
}

function homeRoomTitle(roomId: RoomId) {
  return state.rooms.find((room) => room.id === roomId)?.title ?? (roomId === "reo-maori" ? "Te Reo Māori Room" : "New Zealand Lobby");
}

function setHomeActionLabel(button: HTMLButtonElement, label: string) {
  const textNode = button.childNodes[0];
  if (textNode) textNode.textContent = `${label} `;
}

function syncHomeMotionPolicy() {
  const paused = document.hidden || !state.homeVisible || !homeInViewport || state.classicMode || homeMotionPreference.matches;
  elements.home.dataset.motionPaused = String(paused);
  if (paused) showHomeHeroFinal();
  else if (homeHasRevealed && homeHeroTimers.length === 0) startHomeHeroCycle();
}

function homeMotionAllowed() {
  return !document.hidden && state.homeVisible && homeInViewport && !state.classicMode && !homeMotionPreference.matches;
}

function meaningfulMotionAllowed() {
  return !document.hidden && !state.classicMode && !homeMotionPreference.matches;
}

function cancelPresenceMotion() {
  elements.online.getAnimations().forEach((animation) => animation.cancel());
  elements.online.closest(".topbar-center")?.querySelector<HTMLElement>(".live-dot")
    ?.getAnimations().forEach((animation) => animation.cancel());
}

function playPresenceMotion(increased: boolean) {
  if (!meaningfulMotionAllowed()) return;
  cancelPresenceMotion();
  elements.online.animate([
    { transform: "scale(1)" },
    { transform: "scale(1.15)", offset: .48 },
    { transform: "scale(1)" },
  ], { duration: 250, easing: "ease-out" });
  if (!increased) return;
  const liveDot = elements.online.closest(".topbar-center")?.querySelector<HTMLElement>(".live-dot");
  liveDot?.getAnimations().forEach((animation) => animation.cancel());
  liveDot?.animate([
    { opacity: .72, transform: "scale(1)" },
    { opacity: 1, transform: "scale(1.35)", offset: .45 },
    { opacity: 1, transform: "scale(1)" },
  ], { duration: 250, easing: "ease-out" });
}

function gameMotionStorageKey(game: GameState, moment: string) {
  return `kiwichat:meaningful-motion:${game.room.id}:${state.viewer?.id ?? "viewer"}:${moment}`;
}

function claimGameMotion(game: GameState, moment: string) {
  const key = gameMotionStorageKey(game, moment);
  let seen = seenGameMotion.has(key);
  try {
    seen ||= sessionStorage.getItem(key) === "seen";
    if (!seen) sessionStorage.setItem(key, "seen");
  } catch {
    // Storage can be unavailable in hardened browsing modes; in-memory rendering still remains safe.
  }
  seenGameMotion.add(key);
  return !seen && meaningfulMotionAllowed();
}

function prepareGameMotion(previous: GameState | null, next: GameState | null) {
  state.gameClueFoundMotionPending.clear();
  state.gameResultMotionPendingRoomId = null;
  if (!previous || !next || previous.room.id !== next.room.id) return;
  if (previous.room.phase === "investigation" && next.room.phase === "investigation") {
    const previouslyInvestigated = new Set(previous.room.players.filter((player) => player.investigated).map((player) => player.user.id));
    for (const player of next.room.players) {
      if (player.investigated && !previouslyInvestigated.has(player.user.id) && meaningfulMotionAllowed()) {
        state.gameClueFoundMotionPending.add(player.user.id);
      }
    }
  }
  if (previous.room.phase !== "finished" && next.room.phase === "finished"
    && claimGameMotion(next, "case-result")) {
    state.gameResultMotionPendingRoomId = next.room.id;
  }
}

function clearHomeHeroTimers() {
  homeHeroTimers.forEach((timer) => window.clearTimeout(timer));
  homeHeroTimers = [];
}

function showHomeHeroFinal() {
  clearHomeHeroTimers();
  elements.home.dataset.heroStage = "settled";
}

function startHomeHeroCycle() {
  clearHomeHeroTimers();
  if (!homeMotionAllowed()) {
    showHomeHeroFinal();
    return;
  }
  elements.home.dataset.heroStage = "earth";
  homeHeroTimers = [
    window.setTimeout(() => { elements.home.dataset.heroStage = "turning"; }, 300),
    window.setTimeout(() => { elements.home.dataset.heroStage = "located"; }, 2_700),
    window.setTimeout(() => { elements.home.dataset.heroStage = "targeted"; }, 3_550),
    window.setTimeout(() => { elements.home.dataset.heroStage = "extracting"; }, 4_450),
    window.setTimeout(() => { elements.home.dataset.heroStage = "settled"; }, 5_650),
    window.setTimeout(startHomeHeroCycle, 9_600),
  ];
}

function replayHomeHeroCycle(userInitiated = false) {
  if (!homeMotionAllowed()) {
    showHomeHeroFinal();
    if (userInitiated) elements.homePreviewStatus.textContent = "Aotearoa is shown without motion in your current display mode.";
    return;
  }
  elements.homeOrbit.classList.remove("is-trembling");
  void elements.homeOrbit.offsetWidth;
  elements.homeOrbit.classList.add("is-trembling");
  window.setTimeout(() => elements.homeOrbit.classList.remove("is-trembling"), 460);
  startHomeHeroCycle();
  if (userInitiated) elements.homePreviewStatus.textContent = "Replaying the globe-to-Aotearoa locator.";
}

function cancelHomeRoomMotion() {
  homeRoomAnimations.forEach((animation) => animation.cancel());
  homeRoomAnimations = [];
  homeStoryGhost?.remove();
  homeStoryGhost = null;
}

function prepareHomeStoryGhost() {
  cancelHomeRoomMotion();
  const ghost = elements.homeStoryContent.cloneNode(true) as HTMLElement;
  ghost.removeAttribute("id");
  ghost.querySelectorAll<HTMLElement>("[id]").forEach((child) => child.removeAttribute("id"));
  ghost.classList.add("home-story-ghost");
  ghost.setAttribute("aria-hidden", "true");
  ghost.inert = true;
  elements.homeStoryStage.append(ghost);
  homeStoryGhost = ghost;
  return ghost;
}

function trackHomeAnimation(target: Element, keyframes: Keyframe[], options: KeyframeAnimationOptions) {
  const animation = target.animate(keyframes, options);
  homeRoomAnimations.push(animation);
  return animation;
}

function animateHomeRoomSource(source: HTMLButtonElement) {
  if (!homeMotionAllowed()) return;
  trackHomeAnimation(source, [
    { transform: "translateX(0) scale(1)" },
    { transform: "translateX(-6px) scale(.994)" },
    { transform: "translateX(0) scale(1)" },
  ], { duration: 140, easing: "cubic-bezier(.16,1,.3,1)" });
}

function animateHomeRoomLink(roomId: RoomId, ghost: HTMLElement | null) {
  if (!homeMotionAllowed()) {
    ghost?.remove();
    if (homeStoryGhost === ghost) homeStoryGhost = null;
    return;
  }

  const compact = window.matchMedia("(max-width: 760px)").matches;
  const route = document.querySelector<SVGPathElement>(`[data-room-signal="${roomId}"]`);
  if (route && !compact) {
    trackHomeAnimation(route, [
      { opacity: 0, strokeDasharray: ".01 .99", strokeDashoffset: "1" },
      { opacity: 1, strokeDasharray: ".2 .8", strokeDashoffset: ".52", offset: .5 },
      { opacity: 0, strokeDasharray: ".01 .99", strokeDashoffset: "0" },
    ], { duration: 280, easing: "cubic-bezier(.16,1,.3,1)" });
  }

  if (ghost) {
    const outgoing = trackHomeAnimation(ghost, [
      { clipPath: "inset(0 0 0 0)", opacity: 1, transform: "translateY(0)" },
      { clipPath: "inset(0 0 100% 0)", opacity: 0, transform: `translateY(${compact ? -5 : -8}px)` },
    ], { duration: compact ? 140 : 180, easing: "cubic-bezier(.4,0,1,1)" });
    outgoing.onfinish = () => {
      ghost.remove();
      if (homeStoryGhost === ghost) homeStoryGhost = null;
    };
  }

  trackHomeAnimation(elements.homeStoryContent, [
    { clipPath: "inset(100% 0 0 0)", opacity: .58, transform: `translateY(${compact ? 5 : 8}px)` },
    { clipPath: "inset(0 0 0 0)", opacity: 1, transform: "translateY(0)" },
  ], { duration: compact ? 220 : 250, delay: compact ? 20 : 80, easing: "cubic-bezier(.16,1,.3,1)", fill: "backwards" });

  if (!compact) {
    trackHomeAnimation(elements.homeSafetyContent, [
      { opacity: .72, transform: "translateY(3px)" },
      { opacity: 1, transform: "translateY(0)" },
    ], { duration: 170, delay: 280, easing: "cubic-bezier(.16,1,.3,1)", fill: "backwards" });
  }

  [elements.homeEnterButton, elements.homeStoryEnterButton, elements.homeEnterBottomButton]
    .filter((button) => button.getClientRects().length > 0)
    .forEach((button) => trackHomeAnimation(button, [
      { transform: "scale(.985)", filter: "brightness(1)" },
      { transform: "scale(1)", filter: "brightness(1.12)" },
      { transform: "scale(1)", filter: "brightness(1)" },
    ], { duration: 150, delay: compact ? 170 : 370, easing: "cubic-bezier(.16,1,.3,1)" }));
}

function revealHomeOnce() {
  if (homeHasRevealed || !state.homeVisible) return;
  homeHasRevealed = true;
  window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
    elements.home.dataset.revealed = "true";
    startHomeHeroCycle();
  }));
}

const HOME_ORBIT_SLOTS = ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r"] as const;
const HOME_SIGNAL_SCENES = ["fern", "wave", "mountain", "fern", "wave", "mountain"] as const;

function homeSignalIcon(kind: (typeof HOME_SIGNAL_SCENES)[number]) {
  const icon = svgElement("svg", { class: `home-signal-icon signal-${kind}`, viewBox: "0 0 24 24", "aria-hidden": "true" });
  if (kind === "fern") {
    icon.append(
      svgElement("path", { d: "M5 21C8 17 11 11 19 3" }),
      svgElement("path", { d: "M8 17 4 16M9.5 14.5l.2-4M11.5 12.2 7 11M13.5 9.9l.4-4M15.5 7.7l-3.7-.8M17.2 5.6l.2-3.1" }),
    );
  } else if (kind === "wave") {
    icon.append(
      svgElement("circle", { cx: "17.5", cy: "6.5", r: "2" }),
      svgElement("path", { d: "M2.5 14c2.1-2.7 4.3-2.7 6.4 0s4.3 2.7 6.4 0 4.2-2.7 6.2 0M5 18c2-1.5 4-1.5 6 0s4 1.5 6 0" }),
    );
  } else {
    icon.append(
      svgElement("circle", { cx: "18.5", cy: "5.5", r: "1.8" }),
      svgElement("path", { d: "M2.5 19 9 10l3.1 4.1L16.5 7l5 12h-19Z" }),
      svgElement("path", { d: "m7.2 12.5 1.8 2 1.2-1.5M14.8 9.8l1.7 2.3 1.1-1.4" }),
    );
  }
  return icon;
}

function renderHomeOrbit(total: number) {
  const visibleRooms = state.rooms.map((room) => ({
    memberPreviews: (room.memberPreviews ?? []).filter((member) => !state.blockedIds.has(member.id)),
  }));
  const members = homeOrbitMembers(visibleRooms);
  const fragment = document.createDocumentFragment();
  const visibleSlotCount = Math.max(6, members.length);
  HOME_ORBIT_SLOTS.slice(0, visibleSlotCount).forEach((slot, index) => {
    const member = members[index];
    const node = document.createElement("span");
    node.className = `home-orbit-node orbit-slot-${slot} ${member ? "is-member avatar" : "is-signal"}`;
    if (member) {
      node.dataset.avatar = member.avatar;
      node.dataset.presenceStatus = member.presenceStatus ?? "online";
      node.classList.toggle("is-busy", member.presenceStatus === "busy");
      node.title = member.presenceStatus === "busy" ? `${member.nickname} is busy` : `${member.nickname} is online`;
    } else {
      node.append(homeSignalIcon(HOME_SIGNAL_SCENES[index % HOME_SIGNAL_SCENES.length]!));
    }
    fragment.append(node);
  });
  elements.homeOrbitNodes.replaceChildren(fragment);
  elements.homeOrbit.dataset.orbitDensity = members.length > 12 ? "dense" : members.length > 6 ? "busy" : "calm";
  if (members.length) {
    const namedPreview = members.slice(0, 6).map((member) => member.nickname).join(", ");
    const remaining = members.length - 6;
    const names = remaining > 0 ? `${namedPreview}, and ${remaining} ${remaining === 1 ? "other" : "others"}` : namedPreview;
    const presence = `${total} ${total === 1 ? "person is" : "people are"} connected across the public rooms.`;
    elements.homeOrbit.setAttribute("aria-label", `Replay the globe-to-Aotearoa locator. Live registered members shown: ${names}. ${presence}`);
    elements.homeOrbitCaption.textContent = `${members.length} live ${members.length === 1 ? "member" : "members"}`;
  } else {
    const presence = `${total} ${total === 1 ? "person is" : "people are"} connected across the public rooms.`;
    elements.homeOrbit.setAttribute("aria-label", `Replay the globe-to-Aotearoa locator. No registered member names are shown. ${presence}`);
    elements.homeOrbitCaption.textContent = total > 0 ? `${total} connected across public rooms` : "Waiting for the next hello";
  }
}

function renderHome(announcement?: string) {
  const story = homeStory();
  const nzRoom = state.rooms.find((room) => room.id === "nz-lobby");
  const maoriRoom = state.rooms.find((room) => room.id === "reo-maori");
  const nzCount = nzRoom?.onlineCount ?? 0;
  const maoriCount = maoriRoom?.onlineCount ?? 0;
  const total = nzCount + maoriCount;
  const selectedRoomTitle = homeRoomTitle(state.homePreview.selectedRoom);

  document.body.classList.toggle("home-active", state.homeVisible);
  elements.home.classList.toggle("hidden", !state.homeVisible);
  elements.lobby.classList.toggle("hidden", state.homeVisible || Boolean(state.game));
  elements.home.dataset.phase = state.homePreview.phase;
  elements.home.dataset.activeModule = state.homePreview.activeModule;
  elements.home.dataset.selectedRoom = state.homePreview.selectedRoom;
  elements.home.dataset.roomLinkPending = String(state.homePreview.roomLinkPending);
  if (state.homePreview.attentionModule) elements.home.dataset.attentionModule = state.homePreview.attentionModule;
  else delete elements.home.dataset.attentionModule;
  elements.homeTotalOnline.textContent = String(total);
  elements.homeTotalOnlineLabel.textContent = total === 1 ? "person across the public rooms" : "people across the public rooms";
  elements.homeNzCount.textContent = String(nzCount);
  elements.homeMaoriCount.textContent = String(maoriCount);
  renderHomeOrbit(total);
  elements.homeStoryRoom.textContent = story.roomLabel;
  elements.homeStoryQuote.textContent = `“${story.quote}”`;
  elements.homeStoryInitials.textContent = story.author.slice(0, 2).toUpperCase();
  elements.homeStoryAuthor.textContent = story.author;
  elements.homeSafetyTitle.textContent = story.safetyTitle;
  elements.homeSafetyCopy.textContent = story.safetyCopy;
  elements.homeRoomSelection.textContent = `The preview is showing the ${selectedRoomTitle}.`;
  setHomeActionLabel(elements.homeEnterButton, `Enter the ${selectedRoomTitle}`);
  setHomeActionLabel(elements.homeEnterBottomButton, `Enter the ${selectedRoomTitle}`);
  setHomeActionLabel(elements.homeStoryEnterButton, `Enter the ${selectedRoomTitle}`);

  document.querySelectorAll<HTMLElement>("[data-home-module]").forEach((module) => {
    const moduleName = module.dataset.homeModule as HomeModule;
    module.classList.toggle("is-active", moduleName === state.homePreview.activeModule);
    module.classList.toggle("is-attention", moduleName === state.homePreview.attentionModule);
  });
  document.querySelectorAll<HTMLButtonElement>("[data-home-select]").forEach((button) => {
    button.setAttribute("aria-pressed", String(button.dataset.homeSelect === state.homePreview.activeModule));
  });
  document.querySelectorAll<HTMLButtonElement>("[data-home-room]").forEach((button) => {
    button.setAttribute("aria-pressed", String(button.dataset.homeRoom === state.homePreview.selectedRoom));
  });

  if (state.homeVisible) {
    document.title = "ManyHellos — Public chat, community and games";
    elements.online.textContent = String(total);
    elements.onlineLabel.textContent = "across public rooms";
  }
  syncHomeMotionPolicy();
  revealHomeOnce();
  if (announcement) elements.homePreviewStatus.textContent = announcement;
}

function clearHomeTransition() {
  if (homeUpdateTimer !== null) window.clearTimeout(homeUpdateTimer);
  if (homeSettleTimer !== null) window.clearTimeout(homeSettleTimer);
  homeUpdateTimer = null;
  homeSettleTimer = null;
  cancelHomeRoomMotion();
}

function homeAnnouncement(event: HomePreviewEvent) {
  const story = homeStory();
  switch (event.type) {
    case "selectRoom":
      return `${homeRoomTitle(event.roomId)} selected. Its illustrative conversation, live count, safety note and enter buttons are now connected.`;
    case "selectRooms":
      return "Public room choices selected.";
    case "selectConversation":
      return `Conversation preview selected for ${story.roomLabel}.`;
    case "selectGame":
      return "Mystery game preview selected. Other homepage modules have not changed.";
    case "openSafety":
      return `Safety preview selected for ${story.roomLabel}.`;
    case "changeExample":
      return `${story.label} loaded for ${story.roomLabel}. This message is illustrative.`;
    default:
      return undefined;
  }
}

function dispatchHomePreview(event: HomePreviewEvent, announce = false) {
  const isSelection = ["selectRoom", "selectRooms", "selectConversation", "selectGame", "openSafety", "changeExample"].includes(event.type);
  if (isSelection) clearHomeTransition();
  const roomSource = event.type === "selectRoom"
    ? document.querySelector<HTMLButtonElement>(`[data-home-room="${event.roomId}"]`)
    : null;
  const roomSourceHadFocus = roomSource === document.activeElement;
  const storyGhost = event.type === "selectRoom" ? prepareHomeStoryGhost() : null;
  state.homePreview = reduceHomePreview(state.homePreview, event);
  renderHome(announce ? homeAnnouncement(event) : undefined);
  if (roomSource) animateHomeRoomSource(roomSource);

  if (!isSelection) return;
  if (!homeMotionAllowed()) {
    storyGhost?.remove();
    if (homeStoryGhost === storyGhost) homeStoryGhost = null;
    if (event.type === "selectRoom") state.homePreview = reduceHomePreview(state.homePreview, { type: "beginUpdate" });
    state.homePreview = reduceHomePreview(state.homePreview, { type: "settle" });
    renderHome();
    if (event.type === "selectRoom" && roomSourceHadFocus && window.matchMedia("(max-width: 760px)").matches) {
      document.querySelector<HTMLButtonElement>("[data-home-select=conversation]")?.focus({ preventScroll: true });
    }
    return;
  }

  homeUpdateTimer = window.setTimeout(() => {
    state.homePreview = reduceHomePreview(state.homePreview, { type: "beginUpdate" });
    renderHome();
    if (event.type === "selectRoom") {
      animateHomeRoomLink(event.roomId, storyGhost);
      if (roomSourceHadFocus && window.matchMedia("(max-width: 760px)").matches) {
        document.querySelector<HTMLButtonElement>("[data-home-select=conversation]")?.focus({ preventScroll: true });
      }
    }
  }, 70);
  homeSettleTimer = window.setTimeout(() => {
    state.homePreview = reduceHomePreview(state.homePreview, { type: "settle" });
    renderHome();
    homeUpdateTimer = null;
    homeSettleTimer = null;
  }, 560);
}

function setHomeVisible(visible: boolean, focus = true) {
  state.homeVisible = visible;
  elements.appShell.classList.toggle("visitor-room-mode", !state.viewer && !visible);
  if (visible) {
    elements.gameDrawer.classList.add("hidden");
    elements.gameButton.setAttribute("aria-expanded", "false");
    state.roomStreamOpen = false;
    setMemberDrawer(false);
  } else {
    clearHomeTransition();
    state.homePreview = reduceHomePreview(state.homePreview, { type: "settle" });
  }
  renderHome();
  if (visible) renderRoomStream();
  if (!focus) return;
  window.requestAnimationFrame(() => {
    const target = visible ? document.querySelector<HTMLElement>("#home-title") : elements.roomTitle;
    target?.setAttribute("tabindex", "-1");
    target?.focus({ preventScroll: true });
  });
}

function enterRoomFromHome(roomId: RoomId = state.roomId) {
  setHomeVisible(false, false);
  if (roomId !== state.roomId) switchRoom(roomId);
  else renderRoomContext();
  window.requestAnimationFrame(() => {
    elements.roomTitle.setAttribute("tabindex", "-1");
    elements.roomTitle.focus({ preventScroll: true });
  });
}

function renderClassicMode() {
  document.body.classList.toggle("classic-mode", state.classicMode);
  elements.classicToggle.setAttribute("aria-pressed", String(state.classicMode));
  elements.classicToggle.textContent = state.classicMode ? "Classic view: On" : "Classic view";
  elements.classicToggle.title = state.classicMode ? "Show artistic fonts and message effects" : "Hide artistic fonts and message effects";
  elements.gameClassicToggle.setAttribute("aria-pressed", String(state.classicMode));
  elements.gameClassicToggle.textContent = state.classicMode ? "Classic view: On" : "Classic view";
  if (state.classicMode) cancelPresenceMotion();
  syncHomeMotionPolicy();
}

elements.classicToggle.addEventListener("click", () => {
  state.classicMode = !state.classicMode;
  localStorage.setItem("kiwichat:classic-mode", String(state.classicMode));
  renderClassicMode();
  renderMessages();
  renderGame();
  toast(state.classicMode ? "Classic view on." : "Classic view off.");
});
elements.gameClassicToggle.addEventListener("click", () => elements.classicToggle.click());
renderClassicMode();

function resolvedColorTheme() {
  return state.colorTheme === "system"
    ? (systemColorPreference.matches ? "light" : "night")
    : state.colorTheme;
}

function renderColorTheme() {
  const resolved = resolvedColorTheme();
  document.documentElement.dataset.colorTheme = resolved;
  document.documentElement.style.colorScheme = resolved === "light" ? "light" : "dark";
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = resolved === "light" ? "#eaf0ee" : "#030b16";
  const icon = elements.themeButton.querySelector<HTMLElement>(".theme-icon")!;
  const label = elements.themeButton.querySelector<HTMLElement>(".theme-label")!;
  icon.textContent = state.colorTheme === "system" ? "◐" : resolved === "light" ? "☀" : "☾";
  label.textContent = state.colorTheme === "system" ? "Auto" : resolved === "light" ? "Light" : "Night";
  elements.themeButton.setAttribute(
    "aria-label",
    `Appearance: ${state.colorTheme === "system" ? `System, currently ${resolved}` : resolved}. Choose appearance.`,
  );
}

function renderChatBackground() {
  document.documentElement.dataset.chatBackground = state.chatBackground;
  const title = chatBackgroundTitle(state.chatBackground);
  elements.backgroundButton.setAttribute("aria-label", `Change chat-room background. Current background: ${title}.`);
  elements.backgroundButton.title = `Background: ${title}`;
}

elements.themeButton.addEventListener("click", openAppearance);
elements.backgroundButton.addEventListener("click", openAppearance);
systemColorPreference.addEventListener("change", () => {
  if (state.colorTheme === "system") renderColorTheme();
});
renderColorTheme();
renderChatBackground();

function text(tag: string, value: string, className?: string) {
  const element = document.createElement(tag);
  element.textContent = value;
  if (className) element.className = className;
  return element;
}

function svgElement(tag: string, attributes: Record<string, string>) {
  const element = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
  return element;
}

function avatarArt(avatarValue: string, fallback: string, className: string, color?: string | null) {
  const element = text("span", avatarGlyphs[avatarValue] ?? fallback, className);
  if (avatarLabels[avatarValue]) {
    element.dataset.avatar = avatarValue;
    element.title = `${avatarLabels[avatarValue]} avatar`;
  }
  element.setAttribute("aria-hidden", "true");
  if (color) element.style.setProperty("--user-color", color);
  return element;
}

function avatar(user: User, className = "avatar") {
  return avatarArt(user.avatar, user.nickname.slice(0, 1).toUpperCase(), className, user.color);
}

function reactionPackForAvatar(avatar: string) {
  if (avatar.startsWith("prism-")) return "jellystar";
  if (avatar.startsWith("mech-")) return "mechbit";
  if (avatar.startsWith("neoclay-")) return "stackieclay";
  if (avatar.startsWith("panelpop-")) return "stackiecomic";
  if (avatar.startsWith("popchibi-")) return "stackie";
  if (avatar.startsWith("daydream-")) return "baodream";
  if (avatar.startsWith("afterglow-")) return "bao";
  if (avatar.startsWith("orbit-")) return "luma";
  if (avatar.startsWith("chibi-")) return "sundrop";
  if (avatar.startsWith("pocket-")) return "glowbean";
  if (avatar.startsWith("anime-")) return "anime";
  return "harbour";
}

function reactionArt(emoji: string, packOverride?: StickerPackId) {
  const visual = reactionVisuals[emoji];
  const element = text("span", emoji, "reaction-art");
  if (visual) {
    element.dataset.reaction = visual.art;
    element.dataset.reactionPack = packOverride ?? reactionPackForAvatar(state.viewer?.avatar ?? "");
  }
  element.setAttribute("aria-hidden", "true");
  return element;
}

function reactionLabel(emoji: string) {
  return reactionVisuals[emoji]?.label ?? emoji;
}

function closeEmojiPicker() {
  elements.emojiPicker.classList.add("hidden");
  elements.emojiButton.setAttribute("aria-expanded", "false");
}

function insertComposerEmoji(emoji: string) {
  const start = elements.input.selectionStart ?? elements.input.value.length;
  const end = elements.input.selectionEnd ?? start;
  elements.input.setRangeText(emoji, start, end, "end");
  elements.input.dispatchEvent(new Event("input", { bubbles: true }));
  closeEmojiPicker();
  elements.input.focus();
}

function viewerStickerPacks() {
  if (!state.viewer) return [STICKER_PACKS[0]];
  const elevated = state.viewer.role === "owner" || state.viewer.role === "admin";
  return unlockedStickerPacks(state.viewer.level, elevated, state.viewer.isGuest);
}

function stickerAccessSummary(unlockedCount: number) {
  if (state.viewer?.role === "owner" || state.viewer?.role === "admin") return "All 12 packs · staff access";
  if (!state.viewer || state.viewer.isGuest) return "1 guest pack · create an account to unlock 6";
  if (state.viewer.level < 10) return `${unlockedCount} of 12 packs · 3 more at level 10`;
  if (state.viewer.level < 15) return `${unlockedCount} of 12 packs · all packs at level 15`;
  return "All 12 packs unlocked · 96 original stickers";
}

function sendComposerSticker(packId: StickerPackId, reactionId: StickerReactionId) {
  const sent = queueRoomMessage(stickerToken(packId, reactionId));
  if (sent) {
    clearReply();
    closeEmojiPicker();
    renderMessages(true);
  }
}

function renderStickerPackSection(pack: (typeof STICKER_PACKS)[number]) {
  const section = document.createElement("div");
  section.className = "emoji-picker-group sticker-picker-group";
  const label = text("p", pack.title, "sticker-pack-label");
  const grid = document.createElement("div");
  grid.className = "emoji-picker-grid signature-grid sticker-grid";
  grid.setAttribute("role", "group");
  grid.setAttribute("aria-label", `${pack.title} standalone stickers`);
  for (const reaction of STICKER_REACTIONS) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "emoji-choice signature-emoji sticker-choice";
    button.setAttribute("aria-label", `Send ${pack.title} ${reaction.label} sticker`);
    button.append(reactionArt(reaction.fallback, pack.id));
    button.addEventListener("click", () => sendComposerSticker(pack.id, reaction.id));
    grid.append(button);
  }
  section.append(label, grid);
  return section;
}

function renderEverydayEmojiSection() {
  const everyday = document.createElement("div");
  everyday.className = "emoji-picker-catalog";
  everyday.setAttribute("aria-label", `${COMPOSER_EMOJI_COUNT} everyday emoji`);
  for (const group of COMPOSER_EMOJI_GROUPS) {
    const section = document.createElement("div");
    section.className = "emoji-picker-group";
    section.append(text("strong", group.label, "emoji-picker-heading"));
    const grid = document.createElement("div");
    grid.className = "emoji-picker-grid";
    for (const emoji of group.emojis) {
      const button = text("button", emoji, "emoji-choice plain-emoji") as HTMLButtonElement;
      button.type = "button";
      button.setAttribute("aria-label", `Insert ${emoji}`);
      button.addEventListener("click", () => insertComposerEmoji(emoji));
      grid.append(button);
    }
    section.append(grid);
    everyday.append(section);
  }
  return everyday;
}

function renderEmojiPicker() {
  const unlocked = viewerStickerPacks();
  const unlockedIds = new Set(unlocked.map((pack) => pack.id));
  const avatarPack = reactionPackForAvatar(state.viewer?.avatar ?? "") as StickerPackId;
  if (!selectedStickerPackId || !unlockedIds.has(selectedStickerPackId)) {
    selectedStickerPackId = unlockedIds.has(avatarPack) ? avatarPack : unlocked[0]!.id;
  }

  const shell = document.createElement("div");
  shell.className = "emoji-picker-shell";
  const heading = document.createElement("div");
  heading.className = "sticker-picker-heading";
  heading.append(
    text("strong", "Emoji & stickers", "emoji-picker-heading"),
    text(
      "span",
      emojiPickerView === "everyday" ? `${COMPOSER_EMOJI_COUNT} available` : stickerAccessSummary(unlocked.length),
      "sticker-access-summary",
    ),
  );

  const modeTabs = document.createElement("div");
  modeTabs.className = "expression-mode-tabs";
  modeTabs.setAttribute("role", "tablist");
  modeTabs.setAttribute("aria-label", "Choose how to send an expression");
  const modeButtons = ([
    { view: "everyday", label: "Text emoji", detail: "Add to your message" },
    { view: "stickers", label: "Big stickers", detail: "Send on their own" },
  ] as const).map((mode) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "expression-mode-tab";
    button.dataset.expressionMode = mode.view;
    button.id = `expression-mode-${mode.view}`;
    button.setAttribute("role", "tab");
    button.setAttribute("aria-controls", "expression-picker-panel");
    button.setAttribute("aria-selected", String(emojiPickerView === mode.view));
    button.tabIndex = emojiPickerView === mode.view ? 0 : -1;
    button.append(text("strong", mode.label), text("span", mode.detail));
    button.addEventListener("click", () => {
      emojiPickerView = mode.view;
      renderEmojiPicker();
      elements.emojiPicker.querySelector<HTMLButtonElement>(`[data-expression-mode="${mode.view}"]`)?.focus();
    });
    modeTabs.append(button);
    return button;
  });
  modeTabs.addEventListener("keydown", (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const activeIndex = emojiPickerView === "everyday" ? 0 : 1;
    const direction = event.key === "ArrowRight" ? 1 : -1;
    modeButtons[(activeIndex + direction + modeButtons.length) % modeButtons.length]?.click();
  });

  const useNote = text(
    "p",
    emojiPickerView === "everyday"
      ? "Choose an emoji to insert at the cursor. Add words, then press Send."
      : "Choose a pack, then tap a sticker. It sends immediately as its own message.",
    "expression-use-note",
  );

  const packTabs = document.createElement("div");
  packTabs.className = "sticker-pack-tabs";
  packTabs.setAttribute("role", "group");
  packTabs.setAttribute("aria-label", "Standalone sticker packs");

  const packIconReaction = STICKER_REACTIONS.find((reaction) => reaction.id === "kiwichat") ?? STICKER_REACTIONS[0]!;
  for (const pack of STICKER_PACKS) {
    const unlockedPack = unlockedIds.has(pack.id);
    const tab = document.createElement("button");
    tab.type = "button";
    tab.className = "sticker-pack-tab";
    tab.disabled = !unlockedPack;
    tab.dataset.pack = pack.id;
    tab.setAttribute("aria-label", unlockedPack ? pack.title : `${pack.title}, locked until level ${pack.minLevel}`);
    tab.setAttribute("aria-pressed", String(emojiPickerView === "stickers" && pack.id === selectedStickerPackId));
    tab.append(reactionArt(packIconReaction.fallback, pack.id));
    if (!unlockedPack) {
      tab.append(text("span", "🔒", "sticker-pack-lock"));
      tab.title = state.viewer?.isGuest ? "Create an account to unlock this pack" : `Unlocks at level ${pack.minLevel}`;
    } else {
      tab.title = pack.title;
    }
    tab.addEventListener("click", () => {
      emojiPickerView = "stickers";
      selectedStickerPackId = pack.id;
      renderEmojiPicker();
      elements.emojiPicker.querySelector<HTMLButtonElement>(`.sticker-pack-tab[data-pack="${pack.id}"]`)?.focus();
    });
    packTabs.append(tab);
  }

  const content = emojiPickerView === "everyday"
    ? renderEverydayEmojiSection()
    : renderStickerPackSection(stickerPackById(selectedStickerPackId)!);
  content.id = "expression-picker-panel";
  content.setAttribute("role", "tabpanel");
  content.setAttribute("aria-labelledby", `expression-mode-${emojiPickerView}`);
  shell.append(heading, modeTabs, useNote);
  if (emojiPickerView === "stickers") shell.append(packTabs);
  shell.append(content);
  elements.emojiPicker.replaceChildren(shell);
}

elements.emojiButton.addEventListener("click", () => {
  const opening = elements.emojiPicker.classList.contains("hidden");
  if (opening) renderEmojiPicker();
  elements.emojiPicker.classList.toggle("hidden", !opening);
  elements.emojiButton.setAttribute("aria-expanded", String(opening));
  if (opening) {
    const selectedMode = elements.emojiPicker.querySelector<HTMLButtonElement>("[data-expression-mode][aria-selected='true']");
    (selectedMode ?? elements.emojiPicker.querySelector<HTMLButtonElement>("button"))?.focus();
  }
});
document.addEventListener("pointerdown", (event) => {
  if (elements.emojiPicker.classList.contains("hidden")) return;
  if (!(event.target instanceof Node) || elements.emojiPicker.contains(event.target) || elements.emojiButton.contains(event.target)) return;
  closeEmojiPicker();
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && !elements.emojiPicker.classList.contains("hidden")) {
    closeEmojiPicker();
    elements.emojiButton.focus();
  }
});

function titleBadge(user: User, className = "member-title") {
  if (user.isGuest) {
    const badge = text("span", "[Guest]", className);
    badge.title = "Temporary guest identity";
    return badge;
  }
  const badge = text("span", `[${user.title}]`, `${className}${user.customTitle ? " custom-title" : ""}`);
  badge.title = user.customTitle ? "Custom title" : `Level ${user.level} system title`;
  return badge;
}

function roleLabel(user: User) {
  if (user.isGuest) return "GUEST";
  if (user.role === "owner") return "OWNER";
  if (user.role === "admin") return "ADMIN";
  return `LVL ${user.level}`;
}

function formatTime(timestamp: number) {
  return new Intl.DateTimeFormat("en-NZ", { hour: "numeric", minute: "2-digit", timeZone: "Pacific/Auckland" }).format(timestamp);
}

function remaining(expiresAt: number) {
  const minutes = Math.max(0, Math.ceil((expiresAt - Date.now()) / 60_000));
  if (minutes >= 60) return `${Math.ceil(minutes / 60)}h`;
  return `${minutes}m`;
}

function toast(message: string, kind: "ok" | "error" = "ok") {
  elements.toast.replaceChildren();
  const item = text("div", message, `toast ${kind}`);
  elements.toast.append(item);
  window.setTimeout(() => item.remove(), 4200);
}

function currentRoom() {
  return state.rooms.find((room) => room.id === state.roomId) ?? fallbackRooms.find((room) => room.id === state.roomId)!;
}

function baseComposerHint() {
  return state.roomId === "reo-maori" ? "Te reo Māori only · Enter to send" : "Enter to send · Shift+Enter for a new line";
}

function viewerCanModerate() {
  return state.viewer?.role === "owner" || state.viewer?.role === "admin";
}

function viewerHasAccount() {
  return Boolean(state.viewer && !state.viewer.isGuest);
}

function slowModeCooldownMs(now = Date.now()) {
  if (!state.slowMode || state.slowMode.expiresAt <= now || viewerCanModerate()) return 0;
  return Math.max(0, state.slowModeNextAt - now);
}

function chatRateLimitCooldownMs(scope: "room" | "game", now = Date.now()) {
  const until = scope === "room" ? state.chatRateLimitedUntil : state.gameChatRateLimitedUntil;
  return Math.max(0, until - now);
}

function renderChatRateLimitState() {
  if (state.chatRateLimitTimer !== null) window.clearTimeout(state.chatRateLimitTimer);
  state.chatRateLimitTimer = null;
  updateComposerState();
  updateGameComposerState();
  const active = [chatRateLimitCooldownMs("room"), chatRateLimitCooldownMs("game")].filter((value) => value > 0);
  if (active.length) {
    state.chatRateLimitTimer = window.setTimeout(renderChatRateLimitState, Math.min(1_000, Math.min(...active)));
  }
}

function applyChatRateLimit(scope: "room" | "game", retryAfterMs: number) {
  const until = Date.now() + Math.max(1_000, retryAfterMs);
  if (scope === "room") state.chatRateLimitedUntil = Math.max(state.chatRateLimitedUntil, until);
  else state.gameChatRateLimitedUntil = Math.max(state.gameChatRateLimitedUntil, until);
  renderChatRateLimitState();
}

function clearChatRateLimitState() {
  state.chatRateLimitedUntil = 0;
  state.gameChatRateLimitedUntil = 0;
  if (state.chatRateLimitTimer !== null) window.clearTimeout(state.chatRateLimitTimer);
  state.chatRateLimitTimer = null;
}

function switchRoom(roomId: RoomId) {
  if (roomId === state.roomId) return;
  if (state.game) {
    toast("Return from the mystery table before switching chat rooms.", "error");
    return;
  }
  if (state.pending.size && !window.confirm("Switch rooms and clear messages that have not been sent?")) return;
  const direction = roomId === "reo-maori" ? "forward" : "back";
  const commitRoomSwitch = () => {
    state.roomId = roomId;
    localStorage.setItem("kiwichat:room", roomId);
    state.announcement = currentRoom().announcement;
    state.messages.clear();
    state.roomNotices = [];
    state.pending.clear();
    state.effectPending.clear();
    state.liveMessageMotionPending.clear();
    state.roomNoticeMotionPending.clear();
    state.gameClueFoundMotionPending.clear();
    state.gameResultMotionPendingRoomId = null;
    presenceMotionReady = false;
    state.members = [];
    state.onlineCount = 0;
    state.guestCount = 0;
    state.atGameCount = 0;
    state.replyTo = null;
    state.slowMode = null;
    state.slowModeNextAt = 0;
    if (state.slowModeTimer !== null) window.clearTimeout(state.slowModeTimer);
    state.slowModeTimer = null;
    state.catchupAfter = null;
    state.catchupMode = "recent";
    state.roomState = null;
    state.returnStartSeq = null;
    state.returnViewOpened = false;
    state.readCursorSent = 0;
    state.unreadCount = 0;
    state.roomStreamOpen = false;
    state.recentRoomActivity = [];
    state.focusSuggestionVisible = false;
    loadFocusView();
    elements.gameDrawer.classList.add("hidden");
    renderAll();
    connect();
  };

  type ViewTransitionDocument = Document & {
    startViewTransition?: (update: () => void) => { finished: Promise<void> };
  };
  const viewDocument = document as ViewTransitionDocument;
  const motionReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!state.classicMode && !motionReduced && viewDocument.startViewTransition) {
    document.documentElement.dataset.roomDirection = direction;
    const transition = viewDocument.startViewTransition(commitRoomSwitch);
    void transition.finished.finally(() => delete document.documentElement.dataset.roomDirection);
    return;
  }
  commitRoomSwitch();
}

function renderRoomContext() {
  const room = currentRoom();
  document.body.classList.toggle("room-reo-maori", room.id === "reo-maori");
  document.title = `ManyHellos — ${room.title}`;
  elements.chatPanel.setAttribute("aria-label", `${room.title} chat`);
  elements.roomTitle.textContent = room.title;
  elements.roomEyebrow.textContent = room.eyebrow;
  elements.roomLanguage.textContent = room.languageLabel;
  elements.onlineLabel.textContent = room.id === "reo-maori" ? "in te reo Māori" : "in this room";
  elements.input.placeholder = room.id === "reo-maori" ? "Tuhia ki te reo Māori…" : "Say something to the room…";
  elements.composerHint.textContent = baseComposerHint();
  elements.guestGateCopy.textContent = room.id === "reo-maori"
    ? "Read first, then start a temporary guest session to join the kōrero in te reo Māori."
    : "Read what people are saying, then chat with a temporary guest name. No registered account is needed.";
  elements.joinButton.textContent = "Chat as a guest";
  elements.roomNoteLabel.textContent = room.id === "reo-maori" ? "TE REO MĀORI SPACE" : "ROOM RULE";
  elements.roomNoteCopy.textContent = room.id === "reo-maori"
    ? "Anyone can join. Please use te reo Māori; learners and short replies are welcome."
    : "Public text only. No links, social accounts, private messages, uploads, or bots.";
  elements.gameButton.classList.toggle("hidden", room.id !== "nz-lobby");
  elements.roomTabs.replaceChildren();
  for (const option of state.rooms) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "room-tab";
    button.setAttribute("aria-current", String(option.id === state.roomId));
    button.append(
      text("span", option.title, "room-tab-title"),
      text("span", `${option.onlineCount} online`, "room-tab-count"),
    );
    if (option.id !== state.roomId) button.addEventListener("click", () => switchRoom(option.id));
    elements.roomTabs.append(button);
  }
  elements.roomTools.replaceChildren();
  const wall = document.createElement("button");
  wall.type = "button";
  wall.className = "room-tool";
  wall.append(text("span", "Message wall", "room-tool-title"), text("span", "Public", "room-tool-meta"));
  wall.addEventListener("click", () => void openCommunityWall());
  elements.roomTools.append(wall);
  const feedback = document.createElement("button");
  feedback.type = "button";
  feedback.className = "room-tool";
  feedback.append(text("span", "Feedback", "room-tool-title"), text("span", "Private", "room-tool-meta"));
  feedback.addEventListener("click", openFeedbackBox);
  elements.roomTools.append(feedback);
}

async function api<T>(pathname: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(pathname, {
    ...options,
    headers: options.body ? { "content-type": "application/json", ...(options.headers ?? {}) } : options.headers,
  });
  const body = await response.json() as { error?: string } & T;
  if (!response.ok) throw new Error(body.error ?? "Request failed.");
  return body;
}

function lastSequence() {
  return Math.max(0, ...state.messages.keys());
}

function currentLocalClearStorageKey() {
  return localClearStorageKey(state.viewer?.id, state.roomId);
}

function localClearSequence() {
  const key = currentLocalClearStorageKey();
  const cached = state.clearedThroughByView.get(key);
  if (cached !== undefined) return cached;
  const stored = parseLocalClearSequence(localStorage.getItem(key));
  if (stored > 0) state.clearedThroughByView.set(key, stored);
  return stored;
}

function saveLocalClearSequence(through: number) {
  const key = currentLocalClearStorageKey();
  state.clearedThroughByView.set(key, through);
  localStorage.setItem(key, String(through));
}

function restoreCurrentRoomView() {
  const key = currentLocalClearStorageKey();
  state.clearedThroughByView.delete(key);
  localStorage.removeItem(key);
  renderMessages(false);
  elements.messageAnnouncer.textContent = "Earlier loaded messages are visible again.";
  toast("Earlier loaded messages restored.");
}

function clearCurrentRoomView() {
  const through = lastSequence();
  const hasRoomNotices = state.roomNotices.some((notice) => notice.roomId === state.roomId);
  if (through <= localClearSequence() && !hasRoomNotices) return;
  if (through > localClearSequence()) saveLocalClearSequence(through);
  state.roomNotices = state.roomNotices.filter((notice) => notice.roomId !== state.roomId);
  clearReply();
  state.unreadCount = 0;
  renderMessages(false);
  elements.messageAnnouncer.textContent = "Chat view cleared on this device. New messages will still appear.";
  toast("View cleared on this device, including after refresh. Server history was not deleted.");
}

function localClearNoticeElement() {
  const notice = document.createElement("section");
  notice.className = "local-clear-notice";
  notice.setAttribute("aria-label", "Locally cleared chat view");
  const copy = document.createElement("div");
  copy.append(
    text("strong", "This view is clear"),
    text("p", "Earlier loaded messages are hidden only on this device. New messages will appear below."),
  );
  const restore = text("button", "Restore messages", "secondary") as HTMLButtonElement;
  restore.type = "button";
  restore.addEventListener("click", restoreCurrentRoomView);
  notice.append(copy, restore);
  return notice;
}

function updateClearViewControl() {
  const clearedThrough = localClearSequence();
  const canClear = lastSequence() > clearedThrough || state.roomNotices.some((notice) => notice.roomId === state.roomId);
  elements.clearViewButton.disabled = !canClear;
  elements.clearViewButton.setAttribute("aria-label", canClear
    ? "Clear this device's current chat view"
    : clearedThrough > 0
      ? "Chat view is clear; restore messages from the notice"
      : "No loaded messages to clear");
}

function focusStorageKey(userId: string) {
  return `kiwichat:focus-view:${userId}:${state.roomId}`;
}

function focusSuggestionStorageKey(userId: string) {
  return `kiwichat:focus-suggestion-dismissed:${userId}:${state.roomId}`;
}

function focusSuggestionDismissed() {
  if (!state.viewer || state.viewer.isGuest) return true;
  return Number(localStorage.getItem(focusSuggestionStorageKey(state.viewer.id)) ?? 0) > Date.now();
}

function dismissFocusSuggestion() {
  if (state.viewer && !state.viewer.isGuest) {
    localStorage.setItem(focusSuggestionStorageKey(state.viewer.id), String(Date.now() + 7 * 24 * 60 * 60_000));
  }
  state.focusSuggestionVisible = false;
  renderFocusControl();
}

function recordRoomActivity(message: Message) {
  if (!viewerHasAccount() || state.focusEnabled || focusSuggestionDismissed()) return;
  const now = Date.now();
  state.recentRoomActivity = recentRoomActivity([
    ...state.recentRoomActivity,
    { at: now, authorId: message.author.id },
  ], now);
  if (shouldSuggestFocus(state.recentRoomActivity, now)) {
    state.focusSuggestionVisible = true;
    renderFocusControl();
  }
}

function loadFocusView() {
  state.focusEnabled = false;
  state.focusIds.clear();
  state.roomStreamOpen = false;
  state.focusSuggestionVisible = false;
  if (!state.viewer || state.viewer.isGuest) return;
  try {
    const saved = JSON.parse(localStorage.getItem(focusStorageKey(state.viewer.id)) ?? "null") as { enabled?: unknown; ids?: unknown } | null;
    state.focusEnabled = saved?.enabled === true;
    state.focusIds = new Set(Array.isArray(saved?.ids) ? saved.ids.filter((id): id is string => typeof id === "string").slice(0, 20) : []);
  } catch {
    localStorage.removeItem(focusStorageKey(state.viewer.id));
  }
}

function cacheFocusView() {
  if (!state.viewer || state.viewer.isGuest) return;
  localStorage.setItem(focusStorageKey(state.viewer.id), JSON.stringify({ enabled: state.focusEnabled, ids: [...state.focusIds] }));
}

function applyRoomState(next: RoomMemberState | null | undefined) {
  if (!next || next.roomId !== state.roomId) return;
  state.roomState = next;
  state.focusEnabled = next.focusEnabled;
  state.focusIds = new Set(next.focusIds);
  if (state.focusEnabled) {
    state.focusSuggestionVisible = false;
    state.recentRoomActivity = [];
  }
  cacheFocusView();
  renderFocusControl();
  updateNewMessageBar();
}

async function saveFocusView(enabled = state.focusEnabled, ids = [...state.focusIds]) {
  if (!state.viewer || state.viewer.isGuest) return;
  const result = await api<{ roomState: RoomMemberState }>("/api/focus", {
    method: "POST",
    body: JSON.stringify({ roomId: state.roomId, enabled, ids }),
  });
  applyRoomState(result.roomState);
}

function applyInbox(inbox: Partial<NotificationInbox> | null | undefined) {
  state.notifications = inbox?.items ?? [];
  state.savedNotifications = inbox?.savedItems ?? [];
  state.notificationUnreadCount = Math.max(0, Number(inbox?.unreadCount ?? 0));
  state.notificationSavedCount = Math.max(0, Number(inbox?.savedCount ?? state.savedNotifications.length));
  state.notificationSavedLimit = Math.max(1, Number(inbox?.savedLimit ?? 200));
  state.notificationRetentionDays = Math.max(1, Number(inbox?.retentionDays ?? 45));
}

function isMessageVisible(message: Message) {
  return isAfterLocalClear(message, localClearSequence())
    && shouldShowLobbyMessage(message, state.viewer, state.blockedIds, state.focusEnabled, state.focusIds);
}

function mergeMessages(messages: Message[], animate = false, forceBottom?: boolean) {
  const wasNearBottom = elements.messages.scrollHeight - elements.messages.scrollTop - elements.messages.clientHeight < 120;
  let newVisible = 0;
  for (const message of messages) {
    if (message.roomId !== state.roomId) continue;
    const isNew = !state.messages.has(message.seq);
    state.messages.set(message.seq, message);
    state.pending.delete(message.clientId);
    if (isNew && isMessageVisible(message)) newVisible += 1;
    if (animate && message.author.textEffect !== "none") state.effectPending.add(message.seq);
    if (animate && isNew && message.author.id !== state.viewer?.id && isMessageVisible(message) && meaningfulMotionAllowed()) {
      state.liveMessageMotionPending.add(message.seq);
    }
  }
  if (animate && !wasNearBottom) state.unreadCount += newVisible;
  else if (wasNearBottom) state.unreadCount = 0;
  renderMessages(forceBottom ?? wasNearBottom);
  if (animate && newVisible) {
    const latest = messages.at(-1);
    if (latest && latest.author.id !== state.viewer?.id && isMessageVisible(latest)) {
      const mentioned = mentionsNickname(latest.body, state.viewer?.nickname);
      elements.messageAnnouncer.textContent = `${mentioned ? "You were mentioned by" : "New message from"} ${latest.author.nickname}: ${latest.body ?? "Message removed"}`;
      if (mentioned) toast(`${latest.author.nickname} mentioned you in ${currentRoom().title}.`);
    }
  }
}

function addRoomNotice(notice: RoomNotice) {
  if (notice.roomId !== state.roomId || state.blockedIds.has(notice.user.id)) return;
  if (state.roomNotices.some((item) => item.id === notice.id)) return;
  const cutoff = Date.now() - ROOM_NOTICE_RETENTION_MS;
  state.roomNotices = [...state.roomNotices, notice]
    .filter((item, index, items) => (
      item.roomId === state.roomId
      && item.createdAt >= cutoff
      && items.findIndex((candidate) => candidate.id === item.id) === index
    ))
    .slice(-ROOM_NOTICE_LIMIT);
  if (meaningfulMotionAllowed()) state.roomNoticeMotionPending.add(notice.id);
  elements.messageAnnouncer.textContent = `${notice.user.nickname} ${notice.action === "joined" ? "entered" : "left"} the room.`;
  renderMessages();
}

function renderMembers() {
  elements.online.textContent = String(state.onlineCount);
  elements.members.replaceChildren();
  const sorted = [...state.members].sort((a, b) => {
    const priority = { owner: 0, admin: 1, member: 2 };
    return priority[a.role] - priority[b.role] || a.nickname.localeCompare(b.nickname);
  });
  elements.lobby.classList.toggle("visitor-empty-roster", !state.viewer && sorted.length === 0);
  for (const user of sorted) {
    const button = document.createElement("button");
    button.className = "member";
    button.dataset.presenceStatus = user.presenceStatus ?? "online";
    button.classList.toggle("focus-selected", state.focusEnabled && state.focusIds.has(user.id));
    button.type = "button";
    button.append(avatar(user));
    const details = document.createElement("span");
    const nameLine = document.createElement("span");
    nameLine.className = "member-name-line";
    nameLine.append(text("span", "", "member-presence-dot"), titleBadge(user), text("strong", user.nickname));
    details.append(nameLine, text("small", `${user.presenceStatus === "busy" ? "Busy · " : ""}Level ${user.level}`));
    const badge = text("span", roleLabel(user), `role-badge ${user.role}`);
    button.append(details, badge);
    button.addEventListener("click", () => openMember(user));
    elements.members.append(button);
  }
  const away: string[] = [];
  if (state.atGameCount) away.push(`${state.atGameCount} playing the mystery`);
  if (state.guestCount) away.push(`${state.guestCount} ${state.guestCount === 1 ? "guest" : "guests"} reading`);
  elements.guestCount.textContent = away.length ? `+ ${away.join(" · ")}` : "";
}

function richMessageBody(value: string, className: string) {
  const body = document.createElement("p");
  body.className = className;
  const parts = stickerTextParts(value);
  const stickerOnly = parts.some((part) => part.type === "sticker")
    && parts.every((part) => part.type === "sticker" || !part.value.trim());
  body.classList.toggle("sticker-only-message", stickerOnly);
  for (const part of parts) {
    if (part.type === "text") {
      body.append(document.createTextNode(part.value));
      continue;
    }
    const pack = stickerPackById(part.packId);
    const reaction = stickerReactionById(part.reactionId);
    if (!pack || !reaction) continue;
    const sticker = text("span", "", "message-sticker");
    sticker.setAttribute("role", "img");
    sticker.setAttribute("aria-label", `${pack.title} ${reaction.label} sticker`);
    const art = reactionArt(reaction.fallback, pack.id);
    art.classList.add("message-sticker-art");
    sticker.append(art);
    body.append(sticker);
  }
  return body;
}

async function recallOwnMessage(message: Message, button: HTMLButtonElement) {
  if (!state.viewer || state.viewer.id !== message.author.id || message.deletedAt || message.recalledAt) return;
  button.disabled = true;
  try {
    const result = await api<{ message: Message }>("/api/messages/recall", {
      method: "POST",
      body: JSON.stringify({ messageSeq: message.seq }),
    });
    state.messages.set(result.message.seq, result.message);
    renderMessages(false);
    toast("Message recalled.");
  } catch (error) {
    button.disabled = false;
    toast(error instanceof Error ? error.message : "Message could not be recalled.", "error");
  }
}

function scheduleRecallExpiry(messages: readonly Message[]) {
  if (state.recallExpiryTimer !== null) window.clearTimeout(state.recallExpiryTimer);
  state.recallExpiryTimer = null;
  if (!state.viewer) return;
  const now = Date.now();
  const nextExpiry = messages
    .filter((message) => message.author.id === state.viewer?.id && !message.deletedAt && !message.recalledAt)
    .map((message) => message.createdAt + MESSAGE_RECALL_WINDOW_MS)
    .filter((expiresAt) => expiresAt >= now)
    .sort((left, right) => left - right)[0];
  if (nextExpiry == null) return;
  state.recallExpiryTimer = window.setTimeout(() => renderMessages(false), Math.max(50, nextExpiry - now + 25));
}

function roomNoticeElement(notice: RoomNotice) {
  const item = document.createElement("div");
  item.className = `room-system-notice is-${notice.action}`;
  if (state.roomNoticeMotionPending.delete(notice.id)) item.classList.add("motion-room-notice-arrival");
  item.dataset.noticeId = notice.id;
  item.setAttribute("role", "status");
  item.setAttribute("aria-label", `${notice.user.nickname} ${notice.action === "joined" ? "entered" : "left"} the room`);
  const mark = text("span", notice.action === "joined" ? "↘" : "↗", "room-system-notice-mark");
  mark.setAttribute("aria-hidden", "true");
  const copy = document.createElement("span");
  copy.className = "room-system-notice-copy";
  copy.append(
    text("strong", notice.user.nickname),
    document.createTextNode(notice.action === "joined" ? "entered the room" : "left the room"),
  );
  item.append(mark, copy, text("time", formatTime(notice.createdAt)));
  return item;
}

function visibleRoomHighlights() {
  return state.highlights.filter((highlight) => (
    highlight.message.roomId === state.roomId
    && highlight.message.body
    && !highlight.message.deletedAt
    && !highlight.message.recalledAt
    && !state.blockedIds.has(highlight.message.author.id)
  ));
}

function messageIsHighlighted(seq: number) {
  return state.highlights.some((highlight) => highlight.message.seq === seq);
}

function messageElement(message: Message, continuation = false) {
  const article = document.createElement("article");
  const effect = messageEffects.has(message.author.textEffect) ? message.author.textEffect : "none";
  article.className = `message effect-${effect}`;
  if (effect === "outfit") {
    const robes = outfitRobes.has(message.author.appearance.robes) ? message.author.appearance.robes : "coastal-blue";
    article.classList.add(`outfit-${robes}`);
  }
  article.classList.toggle("message-continuation", continuation);
  const mentioned = mentionsNickname(message.body, state.viewer?.nickname) && message.author.id !== state.viewer?.id;
  if (mentioned) article.classList.add("mentioned-you");
  if (state.effectPending.delete(message.seq)) article.classList.add("effect-enter");
  if (state.liveMessageMotionPending.delete(message.seq)) article.classList.add("motion-message-arrival");
  article.dataset.seq = String(message.seq);
  article.dataset.timeLabel = formatTime(message.createdAt);
  if (!continuation) article.append(avatar(message.author, "message-avatar"));
  const content = document.createElement("div");
  content.className = "message-content";
  let meta: HTMLDivElement | null = null;
  if (continuation) {
    content.append(text("span", `${message.author.nickname}, ${formatTime(message.createdAt)}`, "sr-only"));
  } else {
    meta = document.createElement("div");
    meta.className = "message-meta";
    const name = text("button", message.author.nickname, "message-name");
    name.setAttribute("type", "button");
    if (message.author.color) name.style.color = message.author.color;
    name.addEventListener("click", () => openMember(message.author));
    meta.append(titleBadge(message.author), name, text("span", roleLabel(message.author), `role-badge ${message.author.role}`));
    if (mentioned) meta.append(text("span", "MENTIONED YOU", "mention-badge"));
    meta.append(text("time", formatTime(message.createdAt)));
    content.append(meta);
  }

  if (message.replyTo) {
    const replied = state.messages.get(message.replyTo);
    const repliedIsHidden = replied ? state.blockedIds.has(replied.author.id) : false;
    const quote = document.createElement("button");
    quote.className = "reply-quote";
    quote.type = "button";
    if (repliedIsHidden) {
      quote.append(text("span", "Blocked message", "reply-quote-body"));
      quote.setAttribute("aria-label", "Go to blocked quoted message");
    } else if (replied?.body) {
      const fallback = stickerTextFallback(replied.body).slice(0, 140);
      quote.append(
        text("span", `${replied.author.nickname} · ${formatTime(replied.createdAt)}`, "reply-quote-meta"),
        text("span", fallback, "reply-quote-body"),
      );
      quote.setAttribute("aria-label", `Quoted message from ${replied.author.nickname}: ${fallback}`);
    } else {
      quote.append(text("span", "Earlier message", "reply-quote-body"));
      quote.setAttribute("aria-label", "Go to earlier quoted message");
    }
    quote.addEventListener("click", () => document.querySelector(`[data-seq="${message.replyTo}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }));
    content.append(quote);
  }

  if (messageIsHighlighted(message.seq)) {
    content.append(text("span", "ROOM HIGHLIGHT", "message-highlight-badge"));
  }

  const unavailable = Boolean(message.deletedAt || message.recalledAt);
  const body = message.recalledAt
    ? text("p", message.author.id === state.viewer?.id ? "You recalled this message" : `${message.author.nickname} recalled a message`, "message-body deleted recalled")
    : message.deletedAt
      ? text("p", "Message removed by a moderator", "message-body deleted")
      : richMessageBody(message.body ?? "", `message-body font-${message.author.fontStyle}`);
  content.append(body);

  if (!unavailable) {
    const actions = document.createElement("div");
    actions.className = "message-actions";
    actions.id = `message-actions-${message.seq}`;
    actions.hidden = true;
    const reply = text("button", "Quote reply", "action-link quote-reply-action");
    reply.setAttribute("type", "button");
    reply.setAttribute("aria-label", `Quote ${message.author.nickname}'s message in your reply`);
    reply.addEventListener("click", () => {
      actions.hidden = true;
      article.classList.remove("message-actions-open");
      tools.setAttribute("aria-expanded", "false");
      tools.setAttribute("aria-label", `Show actions for ${message.author.nickname}'s message`);
      setReply(message);
    });
    actions.append(reply);
    if (state.viewer && state.viewer.id !== message.author.id) {
      const mention = text("button", `@${message.author.nickname}`, "action-link");
      mention.setAttribute("type", "button");
      mention.setAttribute("aria-label", `Mention ${message.author.nickname} in the public chat`);
      mention.addEventListener("click", () => mentionUser(message.author));
      const hide = text("button", "Block", "action-link");
      hide.setAttribute("type", "button");
      hide.setAttribute("aria-label", `Block ${message.author.nickname} and hide all their messages`);
      hide.addEventListener("click", () => setBlocked(message.author, true));
      const report = text("button", "Report", "action-link");
      report.setAttribute("type", "button");
      report.addEventListener("click", () => openReport(message));
      actions.append(mention, hide, report);
    }
    if (state.viewer?.unlocks.reactions) {
      const reactions = state.viewer.unlocks.kiwiReactions ? [...baseReactions, ...kiwiReactions] : baseReactions;
      for (const emoji of reactions) {
        const names = message.reactions[emoji] ?? [];
        const reaction = document.createElement("button");
        reaction.className = `reaction ${names.includes(state.viewer.nickname) ? "active" : ""}`;
        reaction.setAttribute("type", "button");
        reaction.setAttribute("aria-label", names.length
          ? `${reactionLabel(emoji)}, ${names.length} reaction${names.length === 1 ? "" : "s"}`
          : `React with ${reactionLabel(emoji)}`);
        reaction.title = names.join(", ") || `React with ${reactionLabel(emoji)}`;
        reaction.append(reactionArt(emoji));
        if (names.length) reaction.append(text("span", String(names.length), "reaction-count"));
        reaction.addEventListener("click", () => sendSocket({ type: "reaction", seq: message.seq, emoji }));
        actions.append(reaction);
      }
    } else {
      for (const [emoji, names] of Object.entries(message.reactions)) {
        const reaction = document.createElement("span");
        reaction.className = "reaction static";
        reaction.setAttribute("aria-label", `${reactionLabel(emoji)}, ${names.length} reaction${names.length === 1 ? "" : "s"}`);
        reaction.append(reactionArt(emoji), text("span", String(names.length), "reaction-count"));
        actions.append(reaction);
      }
    }
    const messageAge = Date.now() - message.createdAt;
    if (state.viewer?.id === message.author.id && messageAge >= 0 && messageAge <= MESSAGE_RECALL_WINDOW_MS) {
      const recall = text("button", "Recall", "action-link recall-action") as HTMLButtonElement;
      recall.type = "button";
      recall.title = "Available for 1 minute after sending";
      recall.setAttribute("aria-label", "Recall this message for everyone");
      recall.addEventListener("click", () => void recallOwnMessage(message, recall));
      actions.append(recall);
    }
    const canRemove = state.viewer?.role === "owner"
      || (state.viewer?.role === "admin" && message.author.role === "member" && message.authorRoleAtPost === "member");
    if (canRemove) {
      const remove = text("button", "Remove", "action-link remove-action");
      remove.setAttribute("type", "button");
      remove.setAttribute("aria-label", `Remove message from ${message.author.nickname}`);
      remove.addEventListener("click", () => openRemoveMessage(message));
      actions.append(remove);
    }
    if (viewerCanModerate()) {
      const highlighted = messageIsHighlighted(message.seq);
      const feature = text("button", highlighted ? "Archive highlight" : "Feature", "action-link highlight-action");
      feature.setAttribute("type", "button");
      feature.setAttribute("aria-label", highlighted
        ? `Archive this room highlight from ${message.author.nickname}`
        : `Feature this message from ${message.author.nickname} in the room highlights`);
      feature.addEventListener("click", () => {
        actions.hidden = true;
        article.classList.remove("message-actions-open");
        tools.setAttribute("aria-expanded", "false");
        sendSocket({ type: "highlight_message", seq: message.seq, highlighted: !highlighted });
      });
      actions.append(feature);
    }

    const visibleReactions = Object.entries(message.reactions).filter(([, names]) => names.length > 0);
    if (visibleReactions.length) {
      const summary = document.createElement("div");
      summary.className = "message-reaction-summary";
      summary.setAttribute("aria-label", "Reactions to this message");
      for (const [emoji, names] of visibleReactions) {
        const interactive = Boolean(state.viewer?.unlocks.reactions);
        const reaction = document.createElement(interactive ? "button" : "span");
        reaction.className = `reaction summary-reaction ${names.includes(state.viewer?.nickname ?? "") ? "active" : ""}`;
        if (reaction instanceof HTMLButtonElement) {
          reaction.type = "button";
          reaction.addEventListener("click", () => sendSocket({ type: "reaction", seq: message.seq, emoji }));
        }
        reaction.setAttribute("aria-label", `${reactionLabel(emoji)}, ${names.length} reaction${names.length === 1 ? "" : "s"}`);
        reaction.title = names.join(", ");
        reaction.append(reactionArt(emoji), text("span", String(names.length), "reaction-count"));
        summary.append(reaction);
      }
      content.append(summary);
    }

    const tools = document.createElement("button");
    tools.className = "message-tools-toggle";
    tools.type = "button";
    tools.setAttribute("aria-controls", actions.id);
    tools.setAttribute("aria-expanded", "false");
    tools.setAttribute("aria-label", `Show actions for ${message.author.nickname}'s message`);
    tools.title = "Message actions";
    const toolsIcon = svgElement("svg", { viewBox: "0 0 24 24", "aria-hidden": "true" });
    toolsIcon.append(
      svgElement("circle", { cx: "5", cy: "12", r: "1.8" }),
      svgElement("circle", { cx: "12", cy: "12", r: "1.8" }),
      svgElement("circle", { cx: "19", cy: "12", r: "1.8" }),
    );
    tools.append(toolsIcon);
    tools.addEventListener("click", () => {
      const expanded = actions.hidden;
      actions.hidden = !expanded;
      tools.setAttribute("aria-expanded", String(expanded));
      tools.setAttribute("aria-label", `${expanded ? "Hide" : "Show"} actions for ${message.author.nickname}'s message`);
      article.classList.toggle("message-actions-open", expanded);
    });
    content.append(tools);
    content.append(actions);
  }
  article.append(content);
  return article;
}

function roomStreamMessageElement(message: Message) {
  const article = document.createElement("article");
  article.className = "room-stream-message";
  article.dataset.streamSeq = String(message.seq);
  const meta = document.createElement("div");
  meta.className = "room-stream-meta";
  const name = text("button", message.author.nickname, "room-stream-name");
  name.setAttribute("type", "button");
  if (message.author.color) name.style.color = message.author.color;
  name.addEventListener("click", () => openMember(message.author));
  meta.append(titleBadge(message.author), name, text("time", formatTime(message.createdAt)));
  article.append(meta);
  if (message.replyTo) {
    const replied = state.messages.get(message.replyTo);
    const repliedIsBlocked = replied ? state.blockedIds.has(replied.author.id) : false;
    const quoteLabel = repliedIsBlocked
      ? "Reply to a blocked message"
      : replied?.body ? `Reply to ${replied.author.nickname}: ${stickerTextFallback(replied.body).slice(0, 72)}` : "Reply to an earlier message";
    article.append(text("p", quoteLabel, "room-stream-reply"));
  }
  article.append(message.recalledAt
    ? text("p", `${message.author.nickname} recalled a message`, "room-stream-body deleted recalled")
    : message.deletedAt
      ? text("p", "Message removed by a moderator", "room-stream-body deleted")
      : richMessageBody(message.body ?? "", `room-stream-body font-${message.author.fontStyle}`));
  const reactionSummary = Object.entries(message.reactions)
    .filter(([, names]) => names.length > 0)
    .map(([emoji, names]) => `${emoji} ${names.length}`)
    .join("  ");
  if (reactionSummary) article.append(text("p", reactionSummary, "room-stream-reactions"));
  return article;
}

function renderRoomStream(forceBottom = false) {
  const open = Boolean(state.viewer && state.focusEnabled && state.roomStreamOpen);
  elements.roomStream.classList.toggle("hidden", !open);
  elements.conversationWorkspace.classList.toggle("stream-open", open);
  elements.roomStreamButton.setAttribute("aria-expanded", String(open));
  elements.roomStreamButton.textContent = open ? "Hide room stream" : "Open room stream";
  if (!open) {
    elements.roomStreamMessages.replaceChildren();
    return;
  }
  const nearBottom = elements.roomStreamMessages.scrollHeight
    - elements.roomStreamMessages.scrollTop
    - elements.roomStreamMessages.clientHeight < 100;
  const clearedThrough = localClearSequence();
  const messages = roomStreamMessages(state.messages.values(), state.blockedIds)
    .filter((message) => isAfterLocalClear(message, clearedThrough));
  elements.roomStreamMessages.replaceChildren();
  if (!messages.length) {
    elements.roomStreamMessages.append(text(
      "p",
      clearedThrough > 0 ? "View cleared on this device. New messages will appear here." : "No loaded public messages yet.",
      "room-stream-empty",
    ));
  } else {
    for (const message of messages) elements.roomStreamMessages.append(roomStreamMessageElement(message));
  }
  if (forceBottom || nearBottom) elements.roomStreamMessages.scrollTop = elements.roomStreamMessages.scrollHeight;
}

function pendingMessageElement(clientId: string, pending: PendingMessage) {
  const article = document.createElement("article");
  article.className = "message pending-message";
  article.dataset.clientId = clientId;
  if (state.viewer) article.append(avatar(state.viewer, "message-avatar"));
  const content = document.createElement("div");
  content.className = "message-content";
  const meta = document.createElement("div");
  meta.className = "message-meta";
  meta.append(
    ...(state.viewer ? [titleBadge(state.viewer)] : []),
    text("strong", state.viewer?.nickname ?? "You", "message-name"),
    text("span", pending.status === "saved" ? "Saved · syncing" : pending.status === "waiting" ? "Waiting for connection" : pending.status === "failed" ? "Not sent" : "Sending…", "pending-status"),
    text("time", formatTime(pending.createdAt)),
  );
  content.append(meta);
  if (pending.replyTo) {
    const replied = state.messages.get(pending.replyTo);
    content.append(text("div", replied?.body ? `↪ ${replied.author.nickname}: ${stickerTextFallback(replied.body).slice(0, 90)}` : "↪ Earlier message", "reply-quote pending-quote"));
  }
  content.append(richMessageBody(pending.body, "message-body"));
  if (pending.status === "failed") {
    content.append(text("p", pending.error ?? "The message was not accepted.", "pending-error"));
    const actions = document.createElement("div");
    actions.className = "message-actions pending-actions";
    const retry = text("button", "Edit & retry", "action-link");
    retry.setAttribute("type", "button");
    retry.addEventListener("click", () => editPendingMessage(clientId));
    const dismiss = text("button", "Dismiss", "action-link remove-action");
    dismiss.setAttribute("type", "button");
    dismiss.addEventListener("click", () => {
      state.pending.delete(clientId);
      renderMessages();
    });
    actions.append(retry, dismiss);
    content.append(actions);
  }
  article.append(content);
  return article;
}

function updateNewMessageBar() {
  const returnUnread = state.roomState?.unreadCount ?? 0;
  const hasMoreHistory = state.catchupMode === "unread" && state.catchupAfter !== null;
  elements.newMessageBar.classList.toggle("hidden", state.unreadCount === 0 && returnUnread === 0 && !hasMoreHistory);
  elements.historyPageButton.classList.toggle("hidden", !hasMoreHistory);
  elements.newMessageButton.classList.toggle("hidden", state.unreadCount === 0 && returnUnread === 0);
  if (state.unreadCount > 0) {
    elements.newMessageButton.textContent = state.unreadCount === 1 ? "1 new message ↓" : `${state.unreadCount} new messages ↓`;
    elements.newMessageButton.setAttribute("aria-label", "Jump to the newest messages");
    return;
  }
  const details: string[] = [];
  if (state.roomState?.mentionUnreadCount) details.push(`@ you ${state.roomState.mentionUnreadCount}`);
  if (state.roomState?.focusUnreadCount) details.push(`Focus ${state.roomState.focusUnreadCount}`);
  elements.newMessageButton.textContent = `↑ ${returnUnread} new ${returnUnread === 1 ? "message" : "messages"}${details.length ? ` · ${details.join(" · ")}` : ""}`;
  elements.newMessageButton.setAttribute("aria-label", `${returnUnread} messages since you left. Return to your previous place.`);
}

function unreadDivider() {
  const divider = document.createElement("div");
  divider.className = "unread-divider";
  divider.setAttribute("role", "separator");
  divider.setAttribute("aria-label", "Messages since you were last here");
  divider.append(text("span", "New since you were here"));
  return divider;
}

function scheduleReadCursor() {
  if (!state.viewer || state.viewer.isGuest || !state.roomState || document.visibilityState !== "visible" || !state.connectionReady) return;
  if (state.readCursorTimer !== null) window.clearTimeout(state.readCursorTimer);
  state.readCursorTimer = window.setTimeout(() => {
    state.readCursorTimer = null;
    if (!state.viewer || state.viewer.isGuest || !state.roomState || document.visibilityState !== "visible") return;
    const container = elements.messages.getBoundingClientRect();
    let latestVisible = state.roomState.lastReadSeq;
    for (const item of elements.messages.querySelectorAll<HTMLElement>(".message[data-seq]")) {
      const bounds = item.getBoundingClientRect();
      if (bounds.top < container.bottom - 24 && bounds.bottom > container.top + 8) {
        latestVisible = Math.max(latestVisible, Number(item.dataset.seq ?? 0));
      }
    }
    if (latestVisible <= Math.max(state.roomState.lastReadSeq, state.readCursorSent)) return;
    state.readCursorSent = latestVisible;
    sendSocket({ type: "read_cursor", seq: latestVisible });
  }, 350);
}

function quietRoomSignalElement() {
  const signal = document.createElement("div");
  signal.className = "quiet-room-signal";
  signal.setAttribute("role", "img");

  for (const className of ["signal-water", "signal-horizon", "signal-orbit signal-orbit-a", "signal-orbit signal-orbit-b", "signal-ping signal-ping-a", "signal-ping signal-ping-b"]) {
    const part = document.createElement("span");
    part.className = className;
    signal.append(part);
  }

  const mapWrap = document.createElement("span");
  mapWrap.className = "quiet-room-earth-wrap";
  const map = svgElement("svg", { class: "quiet-room-aotearoa-map", viewBox: "0 0 180 240", "aria-hidden": "true" });
  map.append(
    svgElement("path", { class: "quiet-aotearoa-route", d: "M149 18C143 52 137 82 119 111S72 175 18 215" }),
    svgElement("path", { class: "quiet-aotearoa-island", d: AOTEAROA_MAP_PATH }),
    svgElement("circle", { class: "quiet-aotearoa-fix", cx: "119", cy: "128", r: "5" }),
    svgElement("circle", { class: "quiet-aotearoa-fix-ring", cx: "119", cy: "128", r: "12" }),
  );
  mapWrap.append(map);
  signal.append(mapWrap);

  const members = homeOrbitMembers([{ memberPreviews: state.members
    .filter((member) => !state.blockedIds.has(member.id))
    .map((member) => ({ id: member.id, nickname: member.nickname, avatar: member.avatar, presenceStatus: member.presenceStatus })) }], 6);
  signal.setAttribute(
    "aria-label",
    members.length
      ? `Aotearoa with live room members shown: ${members.map((member) => member.nickname).join(", ")}.`
      : "Aotearoa with decorative harbour signals. No registered member names are shown online.",
  );
  for (const [index, suffix] of HOME_ORBIT_SLOTS.slice(0, 6).entries()) {
    const member = members[index];
    const person = document.createElement("span");
    person.className = `quiet-room-person room-person-${suffix} ${member ? "is-member avatar" : "is-signal"}`;
    person.setAttribute("aria-hidden", "true");
    if (member) {
      person.dataset.avatar = member.avatar;
      person.dataset.presenceStatus = member.presenceStatus ?? "online";
      person.classList.toggle("is-busy", member.presenceStatus === "busy");
      person.title = member.presenceStatus === "busy" ? `${member.nickname} is busy` : `${member.nickname} is online`;
    } else {
      person.append(homeSignalIcon(HOME_SIGNAL_SCENES[index]!));
    }
    signal.append(person);
  }
  return signal;
}

function quietRoomElement(plan: QuietRoomPlan) {
  const card = document.createElement("section");
  card.className = `quiet-room-card ${plan.kind}`;
  const starscape = document.createElement("div");
  starscape.className = "quiet-room-starscape";
  starscape.setAttribute("aria-hidden", "true");
  for (const className of ["quiet-room-stars quiet-room-stars-near", "quiet-room-stars quiet-room-stars-far"]) {
    const layer = document.createElement("span");
    layer.className = className;
    starscape.append(layer);
  }
  const signal = quietRoomSignalElement();
  const heading = text("h2", plan.title, "quiet-room-title");
  heading.id = `quiet-room-title-${state.roomId}`;
  card.setAttribute("aria-labelledby", heading.id);
  const description = text("p", plan.description, "quiet-room-copy");

  card.append(starscape, signal, heading, description);

  const highlights = visibleRoomHighlights().slice(0, 5);
  if (highlights.length) {
    const curated = document.createElement("section");
    curated.className = "quiet-room-highlights";
    const curatedHeading = text("h3", "Room highlights");
    const curatedDescription = text(
      "p",
      "A few conversations selected by the moderation team for people arriving between live chats.",
      "quiet-room-highlights-copy",
    );
    const list = document.createElement("div");
    list.className = "quiet-room-highlight-list";
    for (const highlight of highlights) {
      const item = document.createElement("article");
      item.className = "quiet-room-highlight";
      const meta = document.createElement("p");
      meta.className = "quiet-room-highlight-meta";
      meta.append(
        text("strong", highlight.message.author.nickname),
        text("time", formatTime(highlight.message.createdAt)),
      );
      const excerpt = stickerTextFallback(highlight.message.body ?? "").slice(0, 220);
      item.append(meta, text("p", excerpt, "quiet-room-highlight-body"));
      if (state.messages.has(highlight.message.seq)) {
        const find = text("button", "Find in room history", "quiet-room-highlight-find");
        find.setAttribute("type", "button");
        find.addEventListener("click", () => {
          document.querySelector(`[data-seq="${highlight.message.seq}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
        });
        item.append(find);
      }
      list.append(item);
    }
    curated.append(curatedHeading, curatedDescription, list);
    card.append(curated);
  }

  if (state.quietWallEntry) {
    const wall = document.createElement("div");
    wall.className = "quiet-room-wall";
    wall.append(text("p", "From the public message wall", "quiet-room-wall-label"));
    const quote = document.createElement("blockquote");
    quote.textContent = `“${state.quietWallEntry.body}”`;
    const signature = text("p", `— ${state.quietWallEntry.signature}`, "quiet-room-wall-signature");
    wall.append(quote, signature);
    card.append(wall);
  }

  const actions = document.createElement("div");
  actions.className = "quiet-room-actions";
  const primary = text("button", plan.actionLabel, "primary") as HTMLButtonElement;
  primary.type = "button";
  primary.addEventListener("click", () => {
    if (!state.viewer) {
      openGuestEntry();
      return;
    }
    elements.input.focus();
    elements.composerWrap.scrollIntoView({ block: "nearest" });
  });
  actions.append(primary);
  if (state.quietWallEntry) {
    const readWall = text("button", "Read the message wall", "quiet-room-wall-link") as HTMLButtonElement;
    readWall.type = "button";
    readWall.addEventListener("click", () => void openCommunityWall());
    actions.append(readWall);
  }
  card.append(
    actions,
    text("p", "18+ · Public conversation · Keep personal details private.", "quiet-room-boundary"),
  );
  return card;
}

function syncQuietRoomSignal() {
  const signal = elements.messages.querySelector<HTMLElement>(".quiet-room-signal");
  if (signal) signal.replaceWith(quietRoomSignalElement());
}

function applyQuietRoomWallEntries(entries: GuestbookEntry[]) {
  state.quietWallEntry = entries[0] ?? null;
  state.quietWallStatus = "loaded";
}

async function loadQuietRoomWallEntry() {
  if (state.quietWallStatus !== "idle") return;
  state.quietWallStatus = "loading";
  try {
    const result = await api<{ entries: GuestbookEntry[] }>("/api/guestbook");
    applyQuietRoomWallEntries(result.entries);
  } catch {
    state.quietWallEntry = null;
    state.quietWallStatus = "loaded";
  } finally {
    renderMessages(false);
  }
}

function renderMessages(forceBottom?: boolean) {
  const nearBottom = forceBottom ?? (elements.messages.scrollHeight - elements.messages.scrollTop - elements.messages.clientHeight < 120);
  const clearedThrough = localClearSequence();
  const noticeCutoff = Date.now() - ROOM_NOTICE_RETENTION_MS;
  state.roomNotices = state.roomNotices.filter((notice) => notice.createdAt >= noticeCutoff);
  const roomNotices = state.roomNotices.filter((notice) => (
    notice.roomId === state.roomId && !state.blockedIds.has(notice.user.id)
  ));
  for (const message of state.messages.values()) {
    if (!isMessageVisible(message)) state.effectPending.delete(message.seq);
  }
  const visible = [...state.messages.values()].filter(isMessageVisible).sort((a, b) => a.seq - b.seq);
  const conversationTraces = visible.filter(isConversationTrace);
  const plan = quietRoomPlan({
    roomId: state.roomId,
    now: Date.now(),
    visibleMessageTimes: conversationTraces.map((message) => message.createdAt),
    hasPending: state.pending.size > 0,
    focusEnabled: state.focusEnabled,
    isReturning: state.catchupMode === "unread" || state.returnStartSeq !== null,
    viewerSignedIn: Boolean(state.viewer),
  });
  elements.messages.replaceChildren();
  elements.messages.classList.toggle("quiet-room-state", clearedThrough === 0 && (plan.kind === "quiet-empty" || plan.kind === "quiet-history"));
  elements.composerWrap.classList.toggle(
    "quiet-room-guest-hidden",
    clearedThrough === 0 && !state.viewer && (plan.kind === "quiet-empty" || plan.kind === "quiet-history"),
  );
  if (clearedThrough > 0) elements.messages.append(localClearNoticeElement());
  if (clearedThrough === 0 && plan.kind === "focus-empty") {
    elements.messages.append(text("div", plan.description, "empty-state"));
    for (const notice of roomNotices) elements.messages.append(roomNoticeElement(notice));
  } else {
    let messageTarget = elements.messages;
    if (clearedThrough === 0 && (plan.kind === "quiet-empty" || plan.kind === "quiet-history")) {
      elements.messages.append(quietRoomElement(plan));
      if (plan.shouldLoadWall && state.quietWallStatus === "idle") void loadQuietRoomWallEntry();
      if (plan.historyLabel) {
        const history = document.createElement("section");
        history.className = "quiet-room-history";
        const historyHeading = document.createElement("div");
        historyHeading.className = "quiet-room-history-heading";
        historyHeading.append(text("h3", plan.historyLabel), text("span", "Saved · not live", "quiet-room-history-status"));
        history.append(historyHeading);
        elements.messages.append(history);
        messageTarget = history;
      }
    } else if (clearedThrough === 0 && !visible.length && !state.pending.size) {
      elements.messages.append(text("div", "Loading your room history…", "empty-state"));
    }

    const displayMessages = plan.kind === "quiet-empty" || plan.kind === "quiet-history" ? conversationTraces : visible;
    let dividerInserted = false;
    let previousMessage: Message | null = null;
    const appendMessage = (message: Message) => {
      if (!dividerInserted && state.returnStartSeq !== null && message.seq >= state.returnStartSeq) {
        messageTarget.append(unreadDivider());
        dividerInserted = true;
        previousMessage = null;
      }
      const continuation = Boolean(
        previousMessage
        && previousMessage.author.id === message.author.id
        && !previousMessage.deletedAt
        && !previousMessage.recalledAt
        && !message.deletedAt
        && !message.recalledAt
        && message.createdAt >= previousMessage.createdAt
        && message.createdAt - previousMessage.createdAt <= MESSAGE_GROUP_WINDOW_MS
      );
      messageTarget.append(messageElement(message, continuation));
      previousMessage = message;
    };
    if (plan.kind === "quiet-empty" || plan.kind === "quiet-history") {
      for (const message of displayMessages) appendMessage(message);
      for (const notice of roomNotices) elements.messages.append(roomNoticeElement(notice));
    } else {
      const timeline = [
        ...displayMessages.map((message) => ({ kind: "message" as const, createdAt: message.createdAt, message })),
        ...roomNotices.map((notice) => ({ kind: "notice" as const, createdAt: notice.createdAt, notice })),
      ].sort((left, right) => left.createdAt - right.createdAt || (
        left.kind === right.kind ? 0 : left.kind === "message" ? -1 : 1
      ));
      for (const item of timeline) {
        if (item.kind === "message") appendMessage(item.message);
        else {
          messageTarget.append(roomNoticeElement(item.notice));
          previousMessage = null;
        }
      }
    }
    for (const [clientId, pending] of state.pending) messageTarget.append(pendingMessageElement(clientId, pending));
  }
  // Arrival motion belongs to this render, including messages hidden by the current view.
  state.liveMessageMotionPending.clear();
  const keepWelcomeAtTop = clearedThrough === 0 && (plan.kind === "quiet-empty" || (plan.kind === "quiet-history" && !state.viewer));
  if (keepWelcomeAtTop) {
    elements.messages.scrollTop = 0;
  } else if (nearBottom) {
    elements.messages.scrollTop = elements.messages.scrollHeight;
    scheduleReadCursor();
  }
  updateNewMessageBar();
  updateClearViewControl();
  scheduleRecallExpiry(visible);
  renderRoomStream();
}

function renderNotificationButton() {
  const signedIn = Boolean(state.viewer && !state.viewer.isGuest);
  elements.notificationButton.classList.toggle("hidden", !signedIn);
  const unread = state.notificationUnreadCount;
  elements.notificationBadge.textContent = unread > 99 ? "99+" : String(unread);
  elements.notificationBadge.classList.toggle("hidden", unread === 0);
  elements.notificationButton.classList.toggle("has-unread", unread > 0);
  elements.notificationButton.setAttribute("aria-label", unread
    ? `Open your inbox, ${unread} unread notification${unread === 1 ? "" : "s"}`
    : "Open your inbox, no unread notifications");
}

function renderIdentity() {
  const registered = Boolean(state.viewer && !state.viewer.isGuest);
  elements.appShell.classList.toggle("visitor-room-mode", !state.viewer && !state.homeVisible);
  elements.authButton.classList.toggle("hidden", registered);
  elements.authButton.classList.toggle("primary", Boolean(state.viewer));
  elements.authButton.classList.toggle("secondary", !state.viewer);
  elements.authButton.textContent = state.viewer?.isGuest ? "Create account" : "Sign in";
  elements.profileButton.classList.toggle("hidden", !registered);
  renderNotificationButton();
  elements.composer.classList.toggle("hidden", !state.viewer);
  elements.guestGate.classList.toggle("hidden", Boolean(state.viewer));
  if (!state.viewer) {
    elements.identity.className = "identity-card guest-card";
    delete elements.identity.dataset.theme;
    elements.identity.replaceChildren(text("span", "YOUR OPTIONS", "eyebrow"), text("div", "?", "identity-avatar"), text("h2", "Reading as a visitor"), text("p", "The room is public. Read first, then choose Chat as a guest when you are ready."));
    const account = text("button", "Sign in or create an account", "secondary wide");
    account.addEventListener("click", () => openAuth("login"));
    elements.identity.append(account);
    return;
  }
  const user = state.viewer;
  if (user.isGuest) {
    elements.identity.className = "identity-card guest-card active-guest-card";
    delete elements.identity.dataset.theme;
    const guestAvatar = text("div", user.nickname.slice(-2), "identity-avatar");
    const create = text("button", "Create an account", "primary wide");
    create.addEventListener("click", () => openAuth("register"));
    const signOut = text("button", "End guest session", "danger-link wide");
    signOut.addEventListener("click", async () => {
      await api("/api/auth/logout", { method: "POST" });
      location.reload();
    });
    elements.identity.replaceChildren(
      text("span", "TEMPORARY IDENTITY", "eyebrow"),
      guestAvatar,
      text("h2", user.nickname),
      text("p", "You can post in public chat with this guest name for up to 24 hours. Ending the session does not remove your public posts."),
      text("p", "Create an account to unlock XP, profiles, reactions, games, saved settings, the message wall and notifications.", "guest-lock-copy"),
      create,
      signOut,
    );
    return;
  }
  elements.profileButton.textContent = avatarGlyphs[user.avatar] ?? user.nickname.slice(0, 1).toUpperCase();
  if (avatarLabels[user.avatar]) elements.profileButton.dataset.avatar = user.avatar;
  else delete elements.profileButton.dataset.avatar;
  elements.profileButton.setAttribute("aria-label", `Open profile and settings · ${avatarLabels[user.avatar] ?? "custom"} avatar`);
  elements.profileButton.style.setProperty("--user-color", user.color ?? "#74f2ce");
  elements.identity.className = "identity-card";
  elements.identity.dataset.theme = user.profileTheme;
  const characterStage = document.createElement("div");
  characterStage.className = "identity-character-stage";
  characterStage.dataset.appearanceSchema = "character-v2";
  characterStage.dataset.form = user.appearance.form;
  characterStage.dataset.hair = user.appearance.hair;
  characterStage.dataset.robes = user.appearance.robes;
  characterStage.dataset.relic = user.appearance.relic;
  characterStage.dataset.scene = user.appearance.scene;
  characterStage.setAttribute("aria-label", "Full character appearance");
  characterStage.append(
    text("span", `LV ${user.level}`, "identity-level-seal"),
    characterFigure(user, "identity"),
  );
  const heading = document.createElement("h2");
  heading.textContent = user.nickname;
  const nameLine = document.createElement("div");
  nameLine.className = "identity-name-line";
  nameLine.append(titleBadge(user, "identity-title-prefix"), heading);
  const profileCopy = document.createElement("div");
  profileCopy.className = "identity-profile-copy";
  profileCopy.append(nameLine);
  const profileHead = document.createElement("div");
  profileHead.className = "identity-profile-head";
  profileHead.append(characterStage, profileCopy);
  const role = text("span", roleLabel(user), `role-badge ${user.role}`);
  const presence = text("button", presenceStatusLabel(state.presenceStatus), `presence-status-button status-${state.presenceStatus}`) as HTMLButtonElement;
  presence.type = "button";
  presence.setAttribute("aria-label", `Online status: ${presenceStatusLabel(state.presenceStatus)}. Change status.`);
  presence.addEventListener("click", openPresenceStatus);
  const statusRow = document.createElement("div");
  statusRow.className = "identity-status-row";
  statusRow.append(role, presence);
  const next = user.nextLevelXp;
  const start = user.levelStartXp;
  const progress = next == null ? 100 : Math.min(100, ((user.xp - start) / (next - start)) * 100);
  const meter = document.createElement("div");
  meter.className = "xp-meter";
  meter.setAttribute("role", "progressbar");
  meter.setAttribute("aria-label", `Level ${user.level} growth`);
  meter.setAttribute("aria-valuemin", "0");
  meter.setAttribute("aria-valuemax", "100");
  meter.setAttribute("aria-valuenow", String(Math.round(progress)));
  const fill = document.createElement("span");
  fill.style.width = `${progress}%`;
  meter.append(fill);
  const xpText = text("div", `${user.xp} XP${next != null ? ` · ${next - user.xp} to next level` : " · max title"}`, "xp-text");
  const growthHead = document.createElement("div");
  growthHead.className = "identity-growth-head";
  growthHead.append(
    text("span", "Growth"),
    text("strong", next == null ? "MAX" : `${Math.round(progress)}%`),
  );
  const growth = document.createElement("section");
  growth.className = "identity-growth";
  growth.setAttribute("aria-label", "Character growth");
  growth.append(growthHead, meter, xpText);
  const record = document.createElement("dl");
  record.className = "identity-record-grid";
  const recordItems = [
    ["Realm", user.systemTitle],
    ["Level", String(user.level)],
    ["Joined", String(user.joinedYear)],
    ["Chat avatar", avatarLabels[user.avatar] ?? "Official"],
  ] as const;
  for (const [label, value] of recordItems) {
    const item = document.createElement("div");
    item.append(text("dt", label), text("dd", value));
    record.append(item);
  }
  const badges = document.createElement("div");
  badges.className = "badge-row";
  for (const badge of user.badges) badges.append(text("span", badge));
  const profile = text("button", "Character profile & settings", "secondary wide identity-profile-action");
  profile.addEventListener("click", openProfile);
  const levels = text("button", "Level atlas · all 50", "level-link");
  levels.addEventListener("click", openLevelGuide);
  elements.identity.replaceChildren(profileHead, statusRow, record, growth, badges, profile, levels);
}

function renderFocusControl() {
  const signedIn = viewerHasAccount();
  if (!signedIn || !state.focusEnabled) state.roomStreamOpen = false;
  elements.viewToolbar.classList.toggle("hidden", !signedIn);
  elements.allViewButton.setAttribute("aria-pressed", String(!state.focusEnabled));
  elements.focusViewButton.setAttribute("aria-pressed", String(state.focusEnabled));
  elements.focusViewCount.textContent = String(state.focusIds.size);
  elements.focusEditButton.textContent = state.focusIds.size ? "Edit people" : "Choose people";
  elements.roomStreamButton.classList.toggle("hidden", !signedIn || !state.focusEnabled);
  elements.blockListCount.textContent = String(state.blockedUsers.length);
  const showSuggestion = signedIn
    && !state.focusEnabled
    && state.focusSuggestionVisible
    && !focusSuggestionDismissed();
  elements.focusSuggestion.classList.toggle("hidden", !showSuggestion);
  renderRoomStream();
}

async function chooseMessageView(focus: boolean) {
  if (!state.viewer || state.viewer.isGuest) return;
  if (focus && !state.focusIds.size) {
    openFocusView();
    return;
  }
  elements.allViewButton.disabled = true;
  elements.focusViewButton.disabled = true;
  try {
    await saveFocusView(focus, [...state.focusIds]);
    if (focus) {
      state.focusSuggestionVisible = false;
      state.recentRoomActivity = [];
    }
    renderMembers();
    renderMessages();
    toast(focus ? "Focus view is on. Your messages and public @mentions stay visible." : "All public messages are visible.");
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not change the message view.", "error");
  } finally {
    elements.allViewButton.disabled = false;
    elements.focusViewButton.disabled = false;
    renderFocusControl();
  }
}

function setRoomStreamOpen(open: boolean) {
  state.roomStreamOpen = Boolean(open && viewerHasAccount() && state.focusEnabled);
  renderFocusControl();
  if (state.roomStreamOpen) {
    renderRoomStream(true);
    elements.roomStreamClose.focus();
  } else elements.roomStreamButton.focus();
}

function setBlueScreenOpen(open: boolean) {
  elements.blueScreen.classList.toggle("hidden", !open);
  elements.modalBackdrop.inert = open;
  elements.appShell.inert = open || !elements.modalBackdrop.classList.contains("hidden");
  if (open) elements.blueContinue.focus();
}

function showBlueScreen(sanction: Sanction) {
  elements.blueReason.textContent = sanction.reason;
  elements.blueAction.textContent = `Removed from ManyHellos · ${remaining(sanction.expiresAt)} remaining`;
  elements.blueModerator.textContent = sanction.actorNickname;
  elements.blueReference.textContent = `MOD-${String(sanction.id).padStart(6, "0")}`;
  setBlueScreenOpen(true);
}

function clearSanctionRetry(resetAttempt = true) {
  if (state.sanctionRetryTimer !== null) {
    window.clearTimeout(state.sanctionRetryTimer);
    state.sanctionRetryTimer = null;
  }
  if (resetAttempt) state.sanctionRetryUsed = false;
}

function renderSanction() {
  if (state.sanctionTimer !== null) {
    window.clearTimeout(state.sanctionTimer);
    state.sanctionTimer = null;
  }
  const sanction = state.sanction;
  const active = sanction && (sanction.expiresAt > Date.now() || state.sanctionSyncing);
  elements.sanction.classList.toggle("hidden", !active);
  elements.composer.classList.toggle("sanctioned", Boolean(active));
  updateComposerState();
  if (!active || !sanction) return;
  if (!state.sanctionSyncing) {
    const sanctionId = sanction.id;
    state.sanctionTimer = window.setTimeout(() => {
      if (state.sanction?.id !== sanctionId) return;
      clearSanctionRetry();
      state.sanctionSyncing = true;
      renderSanction();
      sendSocket({ type: "sanction_status" });
    }, Math.max(0, sanction.expiresAt - Date.now()) + 50);
  }
  elements.sanction.replaceChildren();
  const title = sanction.type === "black_room" ? "Your chat access is temporarily restricted" : sanction.type === "kick" ? "You have been removed from ManyHellos" : "You are muted";
  const timing = state.sanctionSyncing ? "Checking current restriction…" : `${remaining(sanction.expiresAt)} remaining`;
  elements.sanction.append(text("strong", title), text("p", `${sanction.reason} · ${timing}`));
  const appeal = text("button", "View penalty & appeal", "secondary");
  appeal.addEventListener("click", () => openAppeal(sanction));
  elements.sanction.append(appeal);
}

function phaseLabel(phase: GamePhase) {
  if (phase === "waiting") return "Waiting at the wharf";
  if (phase === "investigation") return "Investigation open";
  if (phase === "accusation") return "Final accusation";
  return "Case closed";
}

function gamePhaseTrack(game: GameState) {
  const phases: Array<{ id: GamePhase; label: string }> = [
    { id: "waiting", label: "Gather" },
    { id: "investigation", label: "Investigate" },
    { id: "accusation", label: "Accuse" },
    { id: "finished", label: "Reveal" },
  ];
  const current = phases.findIndex((phase) => phase.id === game.room.phase);
  const track = document.createElement("ol");
  track.className = "game-phase-track";
  track.setAttribute("aria-label", "Game progress");
  phases.forEach((phase, index) => {
    const item = document.createElement("li");
    item.dataset.status = index < current ? "complete" : index === current ? "current" : "upcoming";
    if (index === current) item.setAttribute("aria-current", "step");
    item.append(text("span", String(index + 1)), text("strong", phase.label));
    track.append(item);
  });
  const wrapper = document.createElement("div");
  wrapper.className = "game-progress-wrap";
  wrapper.append(track, text("span", `Scenario v${game.scenario.schemaVersion}`, "game-format-badge"));
  return wrapper;
}

function renderGameLobby() {
  const room = state.gameRooms[0] ?? null;
  const count = room?.playerCount ?? state.atGameCount;
  elements.gameCount.textContent = count ? String(count) : "";
  elements.gameCount.classList.toggle("hidden", !count);
  const longLabel = elements.gameButton.querySelector<HTMLElement>(".game-long")!;
  const shortLabel = elements.gameButton.querySelector<HTMLElement>(".game-short")!;
  longLabel.textContent = state.game ? "Back to game" : "Mystery table";
  shortLabel.textContent = state.game ? "Back to game" : "Mystery";
  elements.gameButton.classList.toggle("active", Boolean(state.game || room));
  elements.gameButton.classList.toggle("has-active-game", Boolean(state.game));

  elements.gameRoomList.replaceChildren();
  const art = document.createElement("img");
  art.className = "game-drawer-art";
  art.src = room?.imageUrl ?? "/game-assets/harbour-ledger-twilight.webp";
  art.alt = "Illustrated harbour town at twilight";
  art.width = 1672;
  art.height = 940;
  art.loading = "lazy";
  art.decoding = "async";
  elements.gameRoomList.append(art);

  if (state.game) {
    elements.gameRoomList.append(
      text("h3", state.game.scenario.title),
      text("p", `${phaseLabel(state.game.room.phase)} · ${state.game.room.playerCount}/${state.game.room.maxPlayers} players`, "drawer-meta"),
      text("p", "You are already seated. Close this panel to return to your sealed brief and table talk.", "drawer-copy"),
    );
    const returnButton = text("button", "Return to the table", "primary wide");
    returnButton.addEventListener("click", () => {
      elements.gameDrawer.classList.add("hidden");
      elements.gameButton.setAttribute("aria-expanded", "false");
      elements.gameScreen.focus({ preventScroll: true });
    });
    elements.gameRoomList.append(returnButton);
    return;
  }

  if (room) {
    elements.gameRoomList.append(
      text("h3", room.title),
      text("p", `${phaseLabel(room.phase)} · ${room.playerCount}/${room.maxPlayers} players`, "drawer-meta"),
      text("p", `Hosted by ${room.host.nickname}. Roles and clues are assigned only after the host starts.`, "drawer-copy"),
    );
    const canUseGame = viewerHasAccount();
    const action = text("button", canUseGame ? (room.joinable ? "Take a seat" : "Table unavailable") : "Create an account to play", "primary wide");
    action.toggleAttribute("disabled", Boolean(canUseGame && !room.joinable));
    action.addEventListener("click", () => {
      if (!viewerHasAccount()) return openAuth("register");
      sendSocket({ type: "game_join", roomId: room.id });
    });
    elements.gameRoomList.append(action, text("p", "The table closes when the case ends.", "drawer-footnote"));
    return;
  }

  elements.gameRoomList.append(
    text("h3", "The Missing Harbour Ledger"),
    text("p", "A short original social-deduction case: discuss, investigate one location, then accuse. Three to six people, about 10–15 minutes.", "drawer-copy"),
  );
  if (!viewerHasAccount()) {
    const signIn = text("button", "Create an account to play", "primary wide");
    signIn.addEventListener("click", () => openAuth("register"));
    elements.gameRoomList.append(signIn);
  } else if (state.viewer?.role === "owner" || state.viewer?.role === "admin") {
    const create = text("button", "Open tonight's table", "primary wide");
    create.addEventListener("click", () => sendSocket({ type: "game_create" }));
    elements.gameRoomList.append(create, text("p", "An admin opens one table at a time during local testing.", "drawer-footnote"));
  } else {
    elements.gameRoomList.append(text("p", "No table is open. An admin can start one when enough people are ready.", "drawer-empty"));
  }
}

function gameMessageElement(message: GameMessage) {
  const article = document.createElement("article");
  article.className = "game-message";
  article.append(avatar(message.author, "game-message-avatar"));
  const content = document.createElement("div");
  const meta = document.createElement("div");
  meta.className = "game-message-meta";
  const name = text("button", message.author.nickname, "message-name");
  name.setAttribute("type", "button");
  name.addEventListener("click", () => openMember(message.author));
  meta.append(titleBadge(message.author), name, text("time", formatTime(message.createdAt)));
  content.append(meta, text("p", message.body, `game-message-body font-${message.author.fontStyle}`));
  if (state.viewer && state.viewer.id !== message.author.id) {
    const actions = document.createElement("div");
    actions.className = "game-message-actions";
    const hide = text("button", "Block player", "action-link");
    hide.setAttribute("type", "button");
    hide.addEventListener("click", () => setBlocked(message.author, true));
    const report = text("button", "Report player", "action-link");
    report.setAttribute("type", "button");
    report.addEventListener("click", () => openReport(undefined, message.author, message.id));
    actions.append(hide, report);
    content.append(actions);
  }
  article.append(content);
  return article;
}

function renderGameMessages(game: GameState) {
  const nearBottom = elements.gameMessages.scrollHeight - elements.gameMessages.scrollTop - elements.gameMessages.clientHeight < 100;
  const visible = game.messages.filter((message) => !state.blockedIds.has(message.author.id));
  elements.gameMessages.replaceChildren();
  if (!visible.length) {
    elements.gameMessages.append(text("p", game.room.phase === "waiting" ? "The table is open. Say kia ora while everyone takes a seat." : "No table talk yet. Ask a precise question or share one useful detail.", "game-chat-empty"));
  } else {
    for (const message of visible) elements.gameMessages.append(gameMessageElement(message));
  }
  elements.gameChatCount.textContent = `${game.room.playerCount} at table`;
  if (nearBottom) elements.gameMessages.scrollTop = elements.gameMessages.scrollHeight;
}

function appendGameRoster(container: HTMLElement, game: GameState) {
  const roster = document.createElement("div");
  roster.className = "game-roster";
  for (const player of game.room.players) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "game-player";
    if (state.gameClueFoundMotionPending.delete(player.user.id)) row.classList.add("motion-clue-found");
    const identity = document.createElement("span");
    identity.className = "game-player-name";
    identity.append(titleBadge(player.user), text("strong", player.user.nickname));
    row.append(avatar(player.user), identity);
    const status = game.room.phase === "waiting"
      ? (player.user.id === game.room.hostUserId ? "Host" : "Ready")
      : game.room.phase === "investigation"
        ? (player.investigated ? "Clue found" : "Searching")
        : game.room.phase === "accusation"
          ? (player.voted ? "Locked" : "Deciding")
          : "Revealed";
    row.append(text("span", status));
    row.addEventListener("click", () => openMember(player.user));
    roster.append(row);
  }
  container.append(roster);
}

function renderGameBoard(game: GameState) {
  elements.gameBoard.replaceChildren(
    text("h2", game.room.phase === "finished" ? "Case result" : "Case board"),
    gamePhaseTrack(game),
  );

  if (game.room.phase === "waiting") {
    elements.gameBoard.append(text("p", `${game.room.playerCount} of ${game.room.minPlayers} minimum seats filled.`, "game-board-copy"));
    appendGameRoster(elements.gameBoard, game);
    const rules = document.createElement("ol");
    rules.className = "game-rules";
    for (const rule of game.scenario.rules) rules.append(text("li", rule));
    elements.gameBoard.append(rules);
    if (state.viewer?.id === game.room.hostUserId) {
      const start = text("button", game.room.playerCount >= game.room.minPlayers ? "Seal roles and begin" : `Waiting for ${game.room.minPlayers - game.room.playerCount} more`, "primary wide");
      start.toggleAttribute("disabled", game.room.playerCount < game.room.minPlayers);
      start.addEventListener("click", () => sendSocket({ type: "game_start" }));
      elements.gameBoard.append(start);
    } else {
      elements.gameBoard.append(text("p", "The host can start when three players are seated.", "drawer-footnote"));
    }
    return;
  }

  if (game.room.phase === "investigation") {
    elements.gameBoard.append(text("p", "Choose one place. Your result is private; decide what to share in table talk.", "game-board-copy"));
    const actions = document.createElement("div");
    actions.className = "case-actions";
    for (const [index, action] of game.actions.entries()) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = game.you?.action === action.id ? "selected" : "";
      button.dataset.evidenceStatus = game.you?.action === action.id ? "reviewed" : game.you?.action ? "closed" : "available";
      button.toggleAttribute("disabled", Boolean(game.you?.action));
      button.append(
        text("span", `Evidence ${String(index + 1).padStart(2, "0")}`, "case-action-label"),
        text("strong", action.title),
        text("span", action.description),
      );
      button.addEventListener("click", () => sendSocket({ type: "game_investigate", choice: action.id }));
      actions.append(button);
    }
    elements.gameBoard.append(actions);
    appendGameRoster(elements.gameBoard, game);
    return;
  }

  if (game.room.phase === "accusation") {
    elements.gameBoard.append(text("p", game.you?.voted ? "Your accusation is locked. Waiting for the remaining players." : "Choose the player you believe moved the ledger. You cannot change this choice.", "game-board-copy"));
    const choices = document.createElement("div");
    choices.className = "accusation-list";
    for (const player of game.room.players.filter((entry) => entry.user.id !== state.viewer?.id)) {
      const button = document.createElement("button");
      button.type = "button";
      button.toggleAttribute("disabled", Boolean(game.you?.voted));
      const identity = document.createElement("span");
      identity.className = "game-player-name";
      identity.append(titleBadge(player.user), text("strong", player.user.nickname));
      button.append(avatar(player.user), identity, text("span", "Accuse"));
      button.addEventListener("click", () => sendSocket({ type: "game_vote", targetUserId: player.user.id }));
      choices.append(button);
    }
    elements.gameBoard.append(choices);
    appendGameRoster(elements.gameBoard, game);
    return;
  }

  const result = game.result;
  if (!result) return;
  const revealResult = state.gameResultMotionPendingRoomId === game.room.id;
  state.gameResultMotionPendingRoomId = null;
  const outcome = text("div", result.winner === "crew" ? "The harbour crew solved it" : "The ledger keeper escaped", `game-outcome ${result.winner}`);
  const culprit = text("p", `${result.culprit.nickname} was the Ledger Keeper.`, "culprit-reveal");
  if (revealResult) culprit.classList.add("motion-culprit-reveal");
  elements.gameBoard.append(
    outcome,
    text("p", result.summary, "game-board-copy"),
    culprit,
  );
  const votes = document.createElement("div");
  votes.className = "vote-reveal";
  for (const [index, vote] of result.votes.entries()) {
    const row = text("p", `${vote.voter.nickname} accused ${vote.target.nickname}`);
    if (revealResult) {
      row.classList.add("motion-vote-reveal");
      row.style.setProperty("--motion-delay", `${300 + index * 80}ms`);
    }
    votes.append(row);
  }
  elements.gameBoard.append(votes);
}

function renderGameDossier(game: GameState) {
  elements.gameDossier.replaceChildren(text("h2", "Your sealed brief"));
  if (!game.you) {
    elements.gameDossier.append(
      text("p", "Your role stays sealed until the host begins.", "game-board-copy"),
      text("p", "This room is temporary. No XP is awarded for game actions, and one account occupies only one seat even across multiple tabs.", "dossier-note"),
    );
    return;
  }
  const alignment = text("span", game.you.alignment === "crew" ? "Harbour crew" : "Hidden keeper", `alignment ${game.you.alignment}`);
  elements.gameDossier.append(
    alignment,
    text("h3", game.you.title),
    text("p", game.you.brief, "role-brief"),
  );
  const clue = document.createElement("section");
  clue.className = "sealed-clue evidence-card";
  if (claimGameMotion(game, "opening-clue")) clue.classList.add("motion-card-reveal");
  clue.append(text("span", "Private evidence · 01", "evidence-number"), text("h4", "Opening clue"), text("p", game.you.clue));
  elements.gameDossier.append(clue);
  if (game.you.actionResult) {
    const result = document.createElement("section");
    result.className = "investigation-result evidence-card";
    if (claimGameMotion(game, "investigation-clue")) result.classList.add("motion-card-reveal");
    result.append(text("span", "Private evidence · 02", "evidence-number"), text("h4", "Your investigation"), text("p", game.you.actionResult));
    elements.gameDossier.append(result);
  }
  elements.gameDossier.append(text("p", "Only you can see this panel. Reconnecting restores the same role and clues.", "dossier-note"));
}

function updateGameComposerState() {
  const active = Boolean(state.game && state.game.room.phase !== "finished");
  const connected = state.connectionReady && state.socket?.readyState === WebSocket.OPEN;
  const cooldownMs = chatRateLimitCooldownMs("game");
  const waiting = cooldownMs > 0;
  elements.gameInput.disabled = !active;
  elements.gameSendButton.disabled = !active || !connected || waiting || !elements.gameInput.value.trim();
  elements.gameSendButton.textContent = waiting
    ? `Wait ${Math.max(1, Math.ceil(cooldownMs / 1_000))}s`
    : "Send to table";
  elements.gameComposerHint.textContent = waiting
    ? `You're chatting too quickly · try again in ${Math.max(1, Math.ceil(cooldownMs / 1_000))}s`
    : "Enter to send · Shift+Enter for a new line";
  elements.gameComposerHint.classList.toggle("rate-limited", waiting);
}

function renderGame() {
  const game = state.game;
  const active = Boolean(game);
  elements.lobby.classList.toggle("hidden", active);
  elements.gameScreen.classList.toggle("hidden", !active);
  document.body.classList.toggle("game-active", active);
  renderGameLobby();
  if (!game) return;

  elements.gameDrawer.classList.add("hidden");
  elements.gameButton.setAttribute("aria-expanded", "false");
  elements.gameHeroImage.src = game.scenario.imageUrl;
  elements.gamePhase.textContent = phaseLabel(game.room.phase);
  elements.gameTitle.textContent = game.scenario.title;
  elements.gamePremise.textContent = game.scenario.premise;
  elements.gameLeave.textContent = game.room.phase === "waiting" && state.viewer?.id === game.room.hostUserId
    ? "Close table"
    : game.room.phase === "finished" ? "Return to lobby" : "Leave table";
  elements.gameComposer.classList.toggle("hidden", game.room.phase === "finished");
  renderGameDossier(game);
  renderGameBoard(game);
  renderGameMessages(game);
  updateGameComposerState();
}

function renderSlowMode() {
  if (state.slowModeTimer !== null) window.clearTimeout(state.slowModeTimer);
  state.slowModeTimer = null;
  const now = Date.now();
  if (state.slowMode && state.slowMode.expiresAt <= now) {
    state.slowMode = null;
    state.slowModeNextAt = 0;
  }
  const canModerate = viewerCanModerate();
  elements.slowModeButton.classList.toggle("hidden", !canModerate);
  elements.slowModeButton.textContent = state.slowMode ? `Slow: ${state.slowMode.intervalSeconds}s` : "Slow mode";
  elements.slowModeButton.setAttribute("aria-pressed", String(Boolean(state.slowMode)));
  elements.slowModeBanner.classList.toggle("hidden", !state.slowMode);
  if (state.slowMode) {
    const minutesLeft = Math.max(1, Math.ceil((state.slowMode.expiresAt - now) / 60_000));
    elements.slowModeBanner.textContent = `Slow mode · one message every ${state.slowMode.intervalSeconds} seconds · ${minutesLeft} min left. ${state.slowMode.reason}`;
    const cooldown = slowModeCooldownMs(now);
    const nextChange = cooldown > 0 ? cooldown : Math.min(60_000, state.slowMode.expiresAt - now);
    state.slowModeTimer = window.setTimeout(renderSlowMode, Math.max(250, nextChange));
  }
  updateComposerState();
}

function renderAll() {
  renderRoomContext();
  elements.announcement.textContent = state.announcement;
  renderSlowMode();
  renderMembers();
  renderMessages();
  renderIdentity();
  renderFocusControl();
  renderSanction();
  renderGame();
  renderHome();
}

function setConnection(label: string, connected: boolean) {
  state.connectionReady = connected;
  elements.connection.lastChild!.textContent = label;
  elements.connection.classList.toggle("connected", connected);
  for (const pending of state.pending.values()) {
    if (!connected && pending.status === "sending") pending.status = "waiting";
  }
  updateComposerState();
  updateGameComposerState();
  if (state.pending.size) renderMessages();
}

function replayUncertainMessages() {
  for (const [clientId, pending] of state.pending) {
    if (pending.status !== "sending" && pending.status !== "waiting") continue;
    pending.status = "sending";
    sendSocket({ type: "message", clientId, body: pending.body, replyTo: pending.replyTo });
  }
  for (const [clientId, body] of state.gamePending) sendSocket({ type: "game_chat", clientId, body });
  if (state.pending.size) renderMessages();
}

function editPendingMessage(clientId: string) {
  const pending = state.pending.get(clientId);
  if (!pending) return;
  elements.input.value = pending.body;
  elements.input.style.height = "auto";
  elements.input.style.height = `${Math.min(elements.input.scrollHeight, 140)}px`;
  const replied = pending.replyTo ? state.messages.get(pending.replyTo) : null;
  if (replied) setReply(replied);
  else clearReply();
  state.pending.delete(clientId);
  renderMessages();
  updateComposerState();
  elements.input.focus();
}

function connect() {
  if (state.reconnectTimer !== null) {
    window.clearTimeout(state.reconnectTimer);
    state.reconnectTimer = null;
  }
  if (state.socket) state.socket.close();
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const socket = new WebSocket(`${protocol}//${location.host}/ws?room=${encodeURIComponent(state.roomId)}&after=${reconnectCursor(state.catchupAfter, lastSequence())}&status=${encodeURIComponent(state.presenceStatus)}`);
  state.socket = socket;
  setConnection("Connecting", false);
  socket.addEventListener("message", (event) => {
    if (state.socket !== socket) return;
    handleSocket(JSON.parse(event.data as string) as Record<string, unknown>);
  });
  socket.addEventListener("close", (event) => {
    if (state.socket !== socket) return;
    const kickedUntil = event.code === 4003 && state.sanction?.type === "kick" ? state.sanction.expiresAt : 0;
    setConnection(kickedUntil ? "Removed" : "Reconnecting", false);
    const now = Date.now();
    const wait = kickedUntil
      ? Math.max(1000, kickedUntil - now)
      : reconnectDelay(state.reconnectAttempt++, now, state.reconnectNotBefore);
    state.reconnectTimer = window.setTimeout(connect, wait);
  });
  socket.addEventListener("error", () => socket.close());
}

function sendSocket(payload: unknown) {
  if (state.socket?.readyState === WebSocket.OPEN) state.socket.send(JSON.stringify(payload));
}

function handleSocket(payload: Record<string, unknown>) {
  if (payload.type === "welcome") {
    state.reconnectAttempt = 0;
    state.reconnectNotBefore = 0;
    presenceMotionReady = false;
    state.liveMessageMotionPending.clear();
    state.roomNoticeMotionPending.clear();
    state.gameClueFoundMotionPending.clear();
    state.gameResultMotionPendingRoomId = null;
    setConnection("Live", true);
    const nextViewer = (payload.viewer as User | null) ?? null;
    const viewerChanged = state.viewer?.id !== nextViewer?.id;
    state.viewer = nextViewer;
    state.presenceStatus = nextViewer && !nextViewer.isGuest
      ? parsePresenceStatus(payload.presenceStatus)
      : "online";
    if (nextViewer && !nextViewer.isGuest) localStorage.setItem("kiwichat:presence-status", state.presenceStatus);
    state.rooms = (payload.rooms as RoomSummary[]) ?? fallbackRooms;
    const welcomedRoom = (payload.room as RoomSummary | undefined)?.id ?? state.roomId;
    const roomChanged = welcomedRoom !== state.roomId;
    if (roomChanged) {
      state.roomId = welcomedRoom;
      localStorage.setItem("kiwichat:room", welcomedRoom);
      state.messages.clear();
      state.highlights = [];
      state.roomNotices = [];
      state.pending.clear();
      state.catchupAfter = null;
    }
    if (viewerChanged || roomChanged) {
      state.recentRoomActivity = [];
      state.focusSuggestionVisible = false;
      state.roomStreamOpen = false;
    }
    state.announcement = String(payload.announcement ?? "");
    state.highlights = ((payload.highlights as RoomHighlight[] | undefined) ?? [])
      .filter((highlight) => highlight.message?.roomId === state.roomId);
    state.blockedIds = new Set((payload.blockedIds as string[]) ?? []);
    state.blockedUsers = (payload.blockedUsers as User[]) ?? [];
    applyInbox(payload.notificationInbox as Partial<NotificationInbox> | undefined);
    if (viewerChanged || roomChanged) loadFocusView();
    const memberState = (payload.roomState as RoomMemberState | null | undefined) ?? null;
    state.roomState = memberState;
    applyRoomState(memberState);
    for (const blockedId of state.blockedIds) state.focusIds.delete(blockedId);
    state.progression = (payload.progression as ProgressionLevel[]) ?? [];
    state.sanction = (payload.sanction as Sanction | null) ?? null;
    state.sanctionSyncing = false;
    clearSanctionRetry();
    state.slowMode = (payload.slowMode as RoomSlowMode | null) ?? null;
    state.slowModeNextAt = Date.now() + Math.max(0, Number(payload.slowModeRetryAfterMs) || 0);
    state.gameRooms = (payload.gameRooms as GameRoomSummary[]) ?? [];
    state.game = (payload.game as GameState | null) ?? null;
    state.catchupMode = (payload.catchupMode as typeof state.catchupMode | undefined) ?? "recent";
    state.returnViewOpened = state.catchupMode !== "unread";
    state.returnStartSeq = state.catchupMode === "unread" ? memberState?.firstUnreadSeq ?? null : null;
    state.readCursorSent = memberState?.lastReadSeq ?? 0;
    mergeMessages(payload.messages as Message[], false, state.catchupMode === "unread" ? false : undefined);
    state.catchupAfter = payload.hasMore ? pageCursor(payload.nextAfter, payload.messages as Message[]) : null;
    if (state.catchupAfter !== null && state.catchupMode === "recovery") sendSocket({ type: "resume", after: state.catchupAfter });
    else replayUncertainMessages();
    sendActivity();
    renderAll();
  } else if (payload.type === "catchup") {
    state.catchupMode = (payload.catchupMode as typeof state.catchupMode | undefined) ?? state.catchupMode;
    mergeMessages(payload.messages as Message[]);
    state.catchupAfter = payload.hasMore ? pageCursor(payload.nextAfter, payload.messages as Message[]) : null;
    updateNewMessageBar();
    if (state.catchupAfter !== null && state.catchupMode === "recovery") sendSocket({ type: "resume", after: state.catchupAfter });
    else replayUncertainMessages();
  } else if (payload.type === "presence") {
    const previousOnlineCount = state.onlineCount;
    state.onlineCount = Number(payload.onlineCount ?? 0);
    state.guestCount = Number(payload.guestCount ?? 0);
    state.atGameCount = Number(payload.atGameCount ?? 0);
    state.members = payload.members as PresentUser[];
    state.rooms = (payload.rooms as RoomSummary[]) ?? state.rooms;
    renderMembers();
    renderRoomContext();
    renderGameLobby();
    syncQuietRoomSignal();
    dispatchHomePreview({ type: "presenceUpdated" });
    if (presenceMotionReady && state.onlineCount !== previousOnlineCount) {
      playPresenceMotion(state.onlineCount > previousOnlineCount);
    }
    presenceMotionReady = true;
  } else if (payload.type === "presence_status") {
    state.presenceStatus = parsePresenceStatus(payload.status);
    localStorage.setItem("kiwichat:presence-status", state.presenceStatus);
    renderIdentity();
  } else if (payload.type === "room_notice") {
    const user = payload.user as { id?: unknown; nickname?: unknown } | null;
    const action = payload.action;
    const roomId = payload.roomId;
    const createdAt = Number(payload.createdAt);
    if (
      (action === "joined" || action === "left")
      && (roomId === "nz-lobby" || roomId === "reo-maori")
      && typeof payload.id === "string"
      && typeof user?.id === "string"
      && typeof user.nickname === "string"
      && user.nickname.length > 0
      && Number.isFinite(createdAt)
    ) {
      addRoomNotice({ id: payload.id, action, roomId, user: { id: user.id, nickname: user.nickname }, createdAt });
    }
  } else if (payload.type === "message") {
    const message = payload.message as Message;
    recordRoomActivity(message);
    mergeMessages([message], true);
    if (state.slowMode && message.author.id === state.viewer?.id && !viewerCanModerate()) {
      state.slowModeNextAt = Math.max(state.slowModeNextAt, message.createdAt + state.slowMode.intervalSeconds * 1000);
      renderSlowMode();
    }
  } else if (payload.type === "ack") {
    const pending = state.pending.get(String(payload.clientId));
    if (pending) {
      pending.status = "saved";
      renderMessages();
    }
    if (state.slowMode && payload.duplicate !== true && !viewerCanModerate()) {
      state.slowModeNextAt = Math.max(
        state.slowModeNextAt,
        Number(payload.createdAt ?? Date.now()) + state.slowMode.intervalSeconds * 1000,
      );
      renderSlowMode();
    }
  } else if (payload.type === "game_ack") {
    state.gamePending.delete(String(payload.clientId));
  } else if (payload.type === "game_rooms") {
    state.gameRooms = (payload.rooms as GameRoomSummary[]) ?? [];
    renderGameLobby();
  } else if (payload.type === "game_state") {
    const wasAtGame = Boolean(state.game);
    const nextGame = (payload.game as GameState | null) ?? null;
    prepareGameMotion(state.game, nextGame);
    state.game = nextGame;
    renderGame();
    if (payload.notice) toast(String(payload.notice));
    if (wasAtGame && !state.game) {
      state.gamePending.clear();
      connect();
    }
  } else if (payload.type === "game_message") {
    const message = payload.message as GameMessage;
    if (state.game && !state.game.messages.some((item) => item.id === message.id)) {
      state.game.messages.push(message);
      renderGameMessages(state.game);
    }
  } else if (payload.type === "reaction") {
    const message = state.messages.get(Number(payload.seq));
    if (message) {
      message.reactions = payload.reactions as Record<string, string[]>;
      renderMessages();
    }
  } else if (payload.type === "room_highlights") {
    state.highlights = ((payload.highlights as RoomHighlight[] | undefined) ?? [])
      .filter((highlight) => highlight.message?.roomId === state.roomId);
    renderMessages(false);
  } else if (payload.type === "xp") {
    state.viewer = payload.user as User;
    renderIdentity();
  } else if (payload.type === "user_updated") {
    const updated = payload.user as User;
    if (state.viewer?.id === updated.id) state.viewer = updated;
    state.members = state.members.map((member) => member.id === updated.id
      ? { ...updated, presenceStatus: member.presenceStatus }
      : member);
    state.blockedUsers = state.blockedUsers.map((member) => member.id === updated.id ? updated : member);
    for (const message of state.messages.values()) {
      if (message.author.id === updated.id) message.author = updated;
    }
    state.highlights = state.highlights.map((highlight) => highlight.message.author.id === updated.id
      ? { ...highlight, message: { ...highlight.message, author: updated } }
      : highlight);
    if (state.game) {
      state.game.room.players = state.game.room.players.map((player) => player.user.id === updated.id ? { ...player, user: updated } : player);
      state.game.messages = state.game.messages.map((message) => message.author.id === updated.id ? { ...message, author: updated } : message);
    }
    renderAll();
  } else if (payload.type === "message_deleted") {
    const deletedSeq = Number(payload.seq);
    state.highlights = state.highlights.filter((highlight) => highlight.message.seq !== deletedSeq);
    const message = state.messages.get(deletedSeq);
    if (message) {
      message.deletedAt = Date.now();
      message.body = null;
      renderMessages();
    } else renderMessages(false);
  } else if (payload.type === "message_recalled") {
    const recalledSeq = Number(payload.seq);
    state.highlights = state.highlights.filter((highlight) => highlight.message.seq !== recalledSeq);
    const message = state.messages.get(recalledSeq);
    if (message) {
      message.recalledAt = Number(payload.recalledAt) || Date.now();
      message.body = null;
      renderMessages();
    } else renderMessages(false);
  } else if (payload.type === "sanction") {
    state.sanction = payload.sanction as Sanction;
    state.sanctionSyncing = false;
    clearSanctionRetry();
    if (state.sanction.type === "kick") showBlueScreen(state.sanction);
    renderSanction();
  } else if (payload.type === "sanction_state") {
    state.sanction = (payload.sanction as Sanction | null) ?? null;
    state.sanctionSyncing = false;
    clearSanctionRetry();
    renderSanction();
  } else if (payload.type === "slow_mode") {
    state.slowMode = (payload.slowMode as RoomSlowMode | null) ?? null;
    state.slowModeNextAt = Date.now() + Math.max(0, Number(payload.retryAfterMs) || 0);
    renderSlowMode();
  } else if (payload.type === "room_state") {
    const beforeFocus = `${state.focusEnabled}:${[...state.focusIds].sort().join(",")}`;
    applyRoomState(payload.roomState as RoomMemberState);
    const afterFocus = `${state.focusEnabled}:${[...state.focusIds].sort().join(",")}`;
    if (beforeFocus !== afterFocus) {
      renderMembers();
      renderMessages(false);
    }
  } else if (payload.type === "notification_inbox") {
    applyInbox(payload as Partial<NotificationInbox>);
    renderNotificationButton();
    if (!elements.modalBackdrop.classList.contains("hidden") && elements.modalContent.querySelector(".notification-center")) openNotifications();
  } else if (payload.type === "error") {
    const message = String(payload.message ?? "Request rejected.");
    const clientId = typeof payload.clientId === "string" ? payload.clientId : null;
    const chatRateLimited = payload.code === "rate_limited" && payload.rateLimitReason !== "repeated";
    const retryAfterMs = Math.max(1_000, Number(payload.retryAfterMs) || 10_000);
    if (payload.requestType === "message" && clientId) {
      const pending = state.pending.get(clientId);
      if (pending) {
        pending.status = "failed";
        pending.error = message;
        renderMessages();
      }
      if (chatRateLimited) applyChatRateLimit("room", retryAfterMs);
    } else if (payload.requestType === "game_chat" && clientId) {
      const rejectedBody = state.gamePending.get(clientId);
      state.gamePending.delete(clientId);
      if (rejectedBody && !elements.gameInput.value.trim()) elements.gameInput.value = rejectedBody;
      if (chatRateLimited) applyChatRateLimit("game", retryAfterMs);
      else updateGameComposerState();
    }
    if (payload.code === "catchup_rate_limited") {
      state.reconnectNotBefore = Math.max(state.reconnectNotBefore, Date.now() + retryAfterMs);
      setConnection("Waiting to reconnect", false);
    }
    if (payload.code === "slow_mode") {
      state.slowModeNextAt = Date.now() + Math.max(1_000, Number(payload.retryAfterMs) || 1_000);
      renderSlowMode();
    }
    if (payload.requestType === "sanction_status" && state.sanctionSyncing && !state.sanctionRetryUsed && state.sanctionRetryTimer === null) {
      const retryAfterMs = Math.max(1_000, Number(payload.retryAfterMs) || 10_100);
      state.sanctionRetryUsed = true;
      state.sanctionRetryTimer = window.setTimeout(() => {
        state.sanctionRetryTimer = null;
        if (state.sanctionSyncing && state.sanction) sendSocket({ type: "sanction_status" });
      }, retryAfterMs);
    }
    toast(message, "error");
  }
}

function sendActivity() {
  if (!state.connectionReady) return;
  const active = Date.now() - lastInteraction < 60_000;
  sendSocket({ type: "activity", visible: document.visibilityState === "visible", active });
}

let lastInteraction = Date.now();
for (const event of ["pointerdown", "keydown", "scroll"] as const) document.addEventListener(event, () => { lastInteraction = Date.now(); }, { passive: true });
document.body.classList.toggle("document-hidden", document.hidden);
document.addEventListener("visibilitychange", () => {
  document.body.classList.toggle("document-hidden", document.hidden);
  sendActivity();
  if (document.visibilityState === "visible") scheduleReadCursor();
  if (document.visibilityState === "visible" && state.sanction) {
    if (state.sanction.expiresAt <= Date.now()) {
      if (!state.sanctionSyncing) {
        clearSanctionRetry();
        state.sanctionSyncing = true;
        renderSanction();
        sendSocket({ type: "sanction_status" });
      }
    } else {
      clearSanctionRetry();
      sendSocket({ type: "sanction_status" });
    }
  }
});
window.setInterval(sendActivity, 25_000);

function setReply(message: Message) {
  if (!state.viewer) return openAuth("login");
  state.replyTo = message.seq;
  elements.replyStrip.classList.remove("hidden");
  elements.replyStrip.querySelector("strong")!.textContent = message.author.nickname;
  const replyTime = elements.replyStrip.querySelector("time")!;
  replyTime.textContent = formatTime(message.createdAt);
  replyTime.dateTime = new Date(message.createdAt).toISOString();
  elements.replyStrip.querySelector("p")!.textContent = message.body
    ? stickerTextFallback(message.body).slice(0, 140)
    : "Message unavailable";
  elements.input.focus();
}

function mentionUser(user: User) {
  if (!state.viewer) return openAuth("login");
  const current = elements.input.value;
  const spacer = current && !/\s$/.test(current) ? " " : "";
  elements.input.value = `${current}${spacer}@${user.nickname} `;
  elements.input.dispatchEvent(new Event("input"));
  if (!elements.modalBackdrop.classList.contains("hidden")) {
    modalReturnFocus = elements.input;
    closeModal();
  } else elements.input.focus();
}

function clearReply() {
  state.replyTo = null;
  elements.replyStrip.classList.add("hidden");
}

function updateComposerState() {
  const sanctioned = Boolean(state.sanction && (state.sanction.expiresAt > Date.now() || state.sanctionSyncing));
  const connected = state.connectionReady && state.socket?.readyState === WebSocket.OPEN;
  const hasText = Boolean(elements.input.value.trim());
  const slowCooldownMs = slowModeCooldownMs();
  const rateCooldownMs = chatRateLimitCooldownMs("room");
  const cooldownMs = Math.max(slowCooldownMs, rateCooldownMs);
  const slowed = cooldownMs > 0;
  elements.input.disabled = sanctioned;
  elements.input.setAttribute("aria-describedby", "composer-hint message-count");
  elements.sendButton.disabled = sanctioned || slowed || !connected || !hasText || !state.viewer;
  elements.composerHint.textContent = rateCooldownMs > 0
    ? `You're chatting too quickly · try again in ${Math.max(1, Math.ceil(rateCooldownMs / 1000))}s`
    : slowCooldownMs > 0
      ? `Slow mode · send again in ${Math.max(1, Math.ceil(slowCooldownMs / 1000))}s`
    : state.slowMode && !viewerCanModerate()
      ? `Slow mode · one message every ${state.slowMode.intervalSeconds}s`
      : baseComposerHint();
elements.messageCount.textContent = `${elements.input.value.length} / 500`;
  elements.messageCount.classList.toggle("near-limit", elements.input.value.length >= 450);
}

function queueRoomMessage(body: string) {
  const normalized = body.trim();
  if (!normalized || !state.viewer || slowModeCooldownMs() > 0 || chatRateLimitCooldownMs("room") > 0 || !state.connectionReady || state.socket?.readyState !== WebSocket.OPEN) return false;
  const clientId = crypto.randomUUID().replaceAll("-", "");
  const pending: PendingMessage = { body: normalized, replyTo: state.replyTo, createdAt: Date.now(), status: "sending" };
  state.pending.set(clientId, pending);
  sendSocket({ type: "message", clientId, body: pending.body, replyTo: pending.replyTo });
  return true;
}

elements.composer.addEventListener("submit", (event) => {
  event.preventDefault();
  const body = elements.input.value.trim();
  if (!queueRoomMessage(body)) return;
  elements.input.value = "";
  elements.input.style.height = "auto";
  clearReply();
  updateComposerState();
  renderMessages(true);
});
elements.input.addEventListener("keydown", (event) => {
  if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    elements.composer.requestSubmit();
  }
});
elements.input.addEventListener("input", () => {
  elements.input.style.height = "auto";
  elements.input.style.height = `${Math.min(elements.input.scrollHeight, 140)}px`;
  updateComposerState();
});
elements.replyStrip.querySelector("button")!.addEventListener("click", clearReply);
elements.newMessageButton.addEventListener("click", () => {
  if (state.unreadCount === 0 && (state.roomState?.unreadCount ?? 0) > 0) {
    state.returnViewOpened = true;
    const firstUnread = [...elements.messages.querySelectorAll<HTMLElement>(".message[data-seq]")]
      .find((item) => Number(item.dataset.seq ?? 0) >= (state.roomState?.firstUnreadSeq ?? 0));
    const target = firstUnread ?? elements.messages.querySelector<HTMLElement>(".unread-divider");
    if (target) target.scrollIntoView({ block: "start", behavior: state.classicMode ? "auto" : "smooth" });
    else elements.messages.scrollTo({ top: 0, behavior: state.classicMode ? "auto" : "smooth" });
    scheduleReadCursor();
    return;
  }
  state.unreadCount = 0;
  elements.messages.scrollTo({ top: elements.messages.scrollHeight, behavior: state.classicMode ? "auto" : "smooth" });
  updateNewMessageBar();
  scheduleReadCursor();
});
elements.historyPageButton.addEventListener("click", () => {
  if (state.catchupAfter === null) return;
  elements.historyPageButton.disabled = true;
  sendSocket({ type: "resume", after: state.catchupAfter });
  window.setTimeout(() => { elements.historyPageButton.disabled = false; }, 600);
});
elements.messages.addEventListener("scroll", () => {
  const nearBottom = elements.messages.scrollHeight - elements.messages.scrollTop - elements.messages.clientHeight < 80;
  if (nearBottom && state.unreadCount) {
    state.unreadCount = 0;
    updateNewMessageBar();
  }
  scheduleReadCursor();
}, { passive: true });

elements.gameComposer.addEventListener("submit", (event) => {
  event.preventDefault();
  const body = elements.gameInput.value.trim();
  if (!body || !state.game || chatRateLimitCooldownMs("game") > 0 || !state.connectionReady || state.socket?.readyState !== WebSocket.OPEN) return;
  const clientId = crypto.randomUUID().replaceAll("-", "");
  state.gamePending.set(clientId, body);
  sendSocket({ type: "game_chat", clientId, body });
  elements.gameInput.value = "";
  updateGameComposerState();
});
elements.gameInput.addEventListener("input", updateGameComposerState);
elements.gameInput.addEventListener("keydown", (event) => {
  if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    elements.gameComposer.requestSubmit();
  }
});
elements.gameLeave.addEventListener("click", () => {
  if (!state.game) return;
  if ((state.game.room.phase === "investigation" || state.game.room.phase === "accusation")
    && !window.confirm("Leaving now closes this temporary table for everyone. Return to the lobby?")) return;
  sendSocket({ type: "game_leave" });
});
let gameDrawerReturnFocus: HTMLElement | null = null;

function setGameDrawer(open: boolean) {
  if (open) {
    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    gameDrawerReturnFocus = active;
  }
  elements.gameDrawer.classList.toggle("hidden", !open);
  elements.gameButton.setAttribute("aria-expanded", String(open));
  if (open) {
    renderGameLobby();
    elements.gameDrawerClose.focus();
  }
}

function openMysteryTable() {
  if (state.game) {
    setHomeVisible(false, false);
    setGameDrawer(false);
    elements.gameScreen.scrollTo({ top: 0, behavior: state.classicMode ? "auto" : "smooth" });
    return;
  }
  setGameDrawer(true);
}

elements.gameButton.addEventListener("click", () => {
  if (state.game) {
    openMysteryTable();
    return;
  }
  setGameDrawer(elements.gameDrawer.classList.contains("hidden"));
});
elements.gameDrawerClose.addEventListener("click", () => {
  const returnFocus = gameDrawerReturnFocus;
  setGameDrawer(false);
  gameDrawerReturnFocus = null;
  if (returnFocus?.isConnected && returnFocus.getClientRects().length) returnFocus.focus();
  else if (elements.mobileInfoButton.getClientRects().length) elements.mobileInfoButton.focus();
  else elements.gameButton.focus();
});

let modalReturnFocus: HTMLElement | null = null;
let activeWallNoteClose: (() => void) | null = null;

function openModal(content: HTMLElement) {
  activeWallNoteClose?.();
  activeWallNoteClose = null;
  const wasClosed = elements.modalBackdrop.classList.contains("hidden");
  if (wasClosed) modalReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  elements.gameDrawer.classList.add("hidden");
  elements.gameButton.setAttribute("aria-expanded", "false");
  elements.modalContent.replaceChildren(content);
  elements.modal.scrollTop = 0;
  const heading = elements.modalContent.querySelector<HTMLElement>("#modal-title, h2");
  if (heading) {
    heading.id = "modal-title";
    elements.modal.setAttribute("aria-labelledby", "modal-title");
    elements.modal.removeAttribute("aria-label");
  } else {
    elements.modal.removeAttribute("aria-labelledby");
    elements.modal.setAttribute("aria-label", "ManyHellos dialog");
  }
  const isCommunityWall = content.classList.contains("community-wall-view");
  const isCharacterStudio = content.classList.contains("character-studio");
  elements.modal.classList.toggle("modal-wide", content.classList.contains("level-guide") || content.classList.contains("moderation-desk") || isCommunityWall || isCharacterStudio);
  elements.modal.classList.toggle("modal-wall", isCommunityWall);
  elements.modal.classList.toggle("modal-character", isCharacterStudio);
  elements.modalBackdrop.classList.remove("hidden");
  elements.appShell.inert = true;
  document.body.classList.add("modal-open");
  window.setTimeout(() => {
    const firstControl = elements.modalContent.querySelector<HTMLElement>("input,button,textarea");
    (firstControl ?? document.querySelector<HTMLElement>("#modal-close"))?.focus();
  }, 0);
}

function closeModal({ then }: { then?: () => void } = {}) {
  if (elements.modalBackdrop.classList.contains("hidden")) {
    then?.();
    return;
  }
  activeWallNoteClose?.();
  activeWallNoteClose = null;
  const returnFocus = modalReturnFocus;
  elements.modalBackdrop.classList.add("hidden");
  elements.appShell.inert = !elements.blueScreen.classList.contains("hidden");
  document.body.classList.remove("modal-open");
  modalReturnFocus = null;
  window.setTimeout(() => {
    if (returnFocus?.isConnected && !returnFocus.closest("[inert]")) returnFocus.focus();
    else if (state.viewer && !elements.input.disabled) elements.input.focus();
    else elements.authButton.focus();
    then?.();
  }, 0);
}

document.querySelector("#modal-close")!.addEventListener("click", () => closeModal());
elements.modalBackdrop.addEventListener("click", (event) => { if (event.target === elements.modalBackdrop) closeModal(); });
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && state.roomStreamOpen && elements.modalBackdrop.classList.contains("hidden")) {
    event.preventDefault();
    setRoomStreamOpen(false);
    return;
  }
  if (elements.modalBackdrop.classList.contains("hidden")) return;
  if (event.key === "Escape") {
    event.preventDefault();
    if (activeWallNoteClose) {
      activeWallNoteClose();
      return;
    }
    closeModal();
    return;
  }
  if (event.key !== "Tab") return;
  const focusable = [...elements.modal.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')]
    .filter((item) => item.offsetParent !== null && !item.closest("[inert]"));
  if (!focusable.length) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

function field(label: string, type: string, name: string, attributes: Record<string, string> = {}) {
  const wrap = document.createElement("label");
  wrap.className = "field";
  wrap.append(text("span", label));
  const input = document.createElement("input");
  input.type = type;
  input.name = name;
  for (const [key, value] of Object.entries(attributes)) input.setAttribute(key, value);
  wrap.append(input);
  return wrap;
}

function openSlowModeControl() {
  if (!viewerCanModerate()) return;
  const section = document.createElement("div");
  section.className = "auth-view slow-mode-control";
  const heading = text("h2", `Slow mode · ${currentRoom().title}`);
  heading.id = "modal-title";
  const intro = text(
    "p",
    "Slow mode applies per account in this room. Members see a countdown; owners and admins remain able to post updates.",
    "modal-intro",
  );
  const form = document.createElement("form");
  form.className = "stack-form";

  const intervalWrap = document.createElement("label");
  intervalWrap.className = "field";
  intervalWrap.append(text("span", "Minimum time between messages"));
  const interval = document.createElement("select");
  interval.name = "intervalSeconds";
  for (const seconds of SLOW_MODE_INTERVALS) {
    const option = document.createElement("option");
    option.value = String(seconds);
    option.textContent = `${seconds} seconds`;
    option.selected = seconds === (state.slowMode?.intervalSeconds ?? 10);
    interval.append(option);
  }
  intervalWrap.append(interval);

  const durationWrap = document.createElement("label");
  durationWrap.className = "field";
  durationWrap.append(text("span", "Automatic expiry"));
  const duration = document.createElement("select");
  duration.name = "durationMinutes";
  for (const minutes of [15, 30, 60, 120]) {
    const option = document.createElement("option");
    option.value = String(minutes);
    option.textContent = `${minutes} minutes`;
    duration.append(option);
  }
  durationWrap.append(duration);

  const reasonWrap = document.createElement("label");
  reasonWrap.className = "field";
  reasonWrap.append(text("span", "Public reason"));
  const reason = document.createElement("textarea");
  reason.name = "reason";
  reason.required = true;
  reason.minLength = 3;
  reason.maxLength = 300;
  reason.placeholder = "Example: Giving the room a moment to cool down.";
  reason.value = state.slowMode?.reason ?? "";
  reasonWrap.append(reason);

  const error = text("p", "", "form-error");
  const actions = document.createElement("div");
  actions.className = "modal-actions";
  const enable = text("button", state.slowMode ? "Update slow mode" : "Turn on slow mode", "primary") as HTMLButtonElement;
  enable.type = "submit";
  actions.append(enable);
  if (state.slowMode) {
    const disable = text("button", "Turn off", "secondary") as HTMLButtonElement;
    disable.type = "button";
    disable.addEventListener("click", async () => {
      if (!form.reportValidity()) return;
      disable.disabled = true;
      error.textContent = "";
      try {
        const result = await api<{ slowMode: RoomSlowMode | null }>("/api/mod/slow-mode", {
          method: "POST",
          body: JSON.stringify({ roomId: state.roomId, enabled: false, reason: reason.value }),
        });
        state.slowMode = result.slowMode;
        state.slowModeNextAt = 0;
        closeModal();
        renderSlowMode();
        toast("Slow mode turned off.");
      } catch (requestError) {
        error.textContent = requestError instanceof Error ? requestError.message : "Could not change slow mode.";
        disable.disabled = false;
      }
    });
    actions.append(disable);
  }
  form.append(intervalWrap, durationWrap, reasonWrap, error, actions);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    enable.disabled = true;
    error.textContent = "";
    const data = new FormData(form);
    try {
      const result = await api<{ slowMode: RoomSlowMode | null }>("/api/mod/slow-mode", {
        method: "POST",
        body: JSON.stringify({
          roomId: state.roomId,
          enabled: true,
          intervalSeconds: Number(data.get("intervalSeconds")),
          durationMinutes: Number(data.get("durationMinutes")),
          reason: data.get("reason"),
        }),
      });
      state.slowMode = result.slowMode;
      state.slowModeNextAt = 0;
      closeModal();
      renderSlowMode();
      toast("Slow mode is active.");
    } catch (requestError) {
      error.textContent = requestError instanceof Error ? requestError.message : "Could not change slow mode.";
      enable.disabled = false;
    }
  });
  section.append(heading, intro, form);
  openModal(section);
}

function reconnectAfterAuthentication() {
  closeModal();
  clearChatRateLimitState();
  state.messages.clear();
  state.catchupAfter = null;
  state.pending.clear();
  state.gamePending.clear();
  connect();
}

function openVerificationPending(email: string) {
  const section = document.createElement("div");
  section.className = "auth-view";
  const heading = text("h2", "Check your email");
  heading.id = "modal-title";
  const message = text("p", "Your nickname becomes active only after you verify the email address. The link expires; you can safely request another without revealing whether an address is already registered.", "modal-intro");
  const form = document.createElement("form");
  form.className = "stack-form";
  const error = text("p", "", "form-error");
  const resend = text("button", "Resend verification email", "secondary wide") as HTMLButtonElement;
  resend.type = "submit";
  const signIn = text("button", "Back to sign in", "primary wide") as HTMLButtonElement;
  signIn.type = "button";
  signIn.addEventListener("click", () => openAuth("login"));
  form.append(error, resend, signIn);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    resend.disabled = true;
    error.textContent = "";
    try {
      const result = await api<{ message: string }>("/api/auth/verification/resend", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      error.textContent = result.message;
    } catch (requestError) {
      error.textContent = requestError instanceof Error ? requestError.message : "Could not request another email.";
    } finally {
      resend.disabled = false;
    }
  });
  section.append(heading, message, form);
  openModal(section);
}

function openPasswordRequest() {
  const section = document.createElement("div");
  section.className = "auth-view";
  const heading = text("h2", "Reset your password");
  heading.id = "modal-title";
  const form = document.createElement("form");
  form.className = "stack-form";
  form.append(field("Account email", "email", "email", { autocomplete: "email", required: "" }));
  const error = text("p", "", "form-error");
  const submit = text("button", "Email me a reset link", "primary wide") as HTMLButtonElement;
  submit.type = "submit";
  const back = text("button", "Back to sign in", "secondary wide") as HTMLButtonElement;
  back.type = "button";
  back.addEventListener("click", () => openAuth("login"));
  form.append(error, submit, back);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    submit.disabled = true;
    error.textContent = "";
    const email = String(new FormData(form).get("email") ?? "");
    try {
      const result = await api<{ message: string }>("/api/auth/password/request", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      error.textContent = result.message;
    } catch (requestError) {
      error.textContent = requestError instanceof Error ? requestError.message : "Could not request a reset link.";
    } finally {
      submit.disabled = false;
    }
  });
  section.append(
    heading,
    text("p", "For privacy, the response is the same whether or not an account exists.", "modal-intro"),
    form,
  );
  openModal(section);
}

function openMfaChallenge(input: { mode: "enroll" | "challenge"; qrCode?: string; secret?: string }) {
  const section = document.createElement("div");
  section.className = "auth-view mfa-view";
  const heading = text("h2", input.mode === "enroll" ? "Secure this staff account" : "Two-step verification");
  heading.id = "modal-title";
  section.append(
    heading,
    text("p", input.mode === "enroll"
      ? "Owner and administrator sessions require an authenticator app. Scan this code, then enter the current six-digit code."
      : "Enter the current code from the authenticator app linked to this staff account.", "modal-intro"),
  );
  if (input.mode === "enroll" && input.qrCode) {
    const qr = document.createElement("img");
    qr.className = "mfa-qr";
    qr.src = input.qrCode;
    qr.alt = "Authenticator setup QR code";
    section.append(qr);
    if (input.secret) section.append(text("p", `Manual setup key: ${input.secret}`, "mfa-secret"));
  }
  const form = document.createElement("form");
  form.className = "stack-form";
  form.append(field("Authenticator code", "text", "code", {
    autocomplete: "one-time-code",
    inputmode: "numeric",
    minlength: "6",
    maxlength: "8",
    pattern: "[0-9]{6,8}",
    required: "",
  }));
  const error = text("p", "", "form-error");
  const submit = text("button", input.mode === "enroll" ? "Enable and continue" : "Verify and continue", "primary wide") as HTMLButtonElement;
  submit.type = "submit";
  form.append(error, submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    submit.disabled = true;
    error.textContent = "";
    try {
      await api("/api/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify({ code: new FormData(form).get("code") }),
      });
      reconnectAfterAuthentication();
      toast("Staff sign-in verified.");
    } catch (requestError) {
      error.textContent = requestError instanceof Error ? requestError.message : "Could not verify this code.";
      submit.disabled = false;
    }
  });
  section.append(form);
  openModal(section);
}

function openPasswordRecovery() {
  const section = document.createElement("div");
  section.className = "auth-view";
  const heading = text("h2", "Choose a new password");
  heading.id = "modal-title";
  const form = document.createElement("form");
  form.className = "stack-form";
  form.append(
    field("New password", "password", "password", { autocomplete: "new-password", minlength: "10", maxlength: "128", required: "" }),
    field("Confirm new password", "password", "confirmPassword", { autocomplete: "new-password", minlength: "10", maxlength: "128", required: "" }),
  );
  const error = text("p", "", "form-error");
  const submit = text("button", "Change password and sign out all sessions", "primary wide") as HTMLButtonElement;
  submit.type = "submit";
  form.append(error, submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const data = new FormData(form);
    if (data.get("password") !== data.get("confirmPassword")) {
      error.textContent = "The passwords do not match.";
      return;
    }
    submit.disabled = true;
    error.textContent = "";
    try {
      await api("/api/auth/password/complete", {
        method: "POST",
        body: JSON.stringify({ password: data.get("password") }),
      });
      openAuth("login");
      toast("Password changed. Sign in again on each device.");
    } catch (requestError) {
      error.textContent = requestError instanceof Error ? requestError.message : "Could not change the password.";
      submit.disabled = false;
    }
  });
  section.append(
    heading,
    text("p", "For account safety, changing the password ends every ManyHellos session.", "modal-intro"),
    form,
  );
  openModal(section);
}

function openGuestEntry() {
  if (state.viewer) {
    closeModal();
    setHomeVisible(false);
    return;
  }
  const section = document.createElement("div");
  section.className = "auth-view guest-entry-view";
  const heading = text("h2", "Chat as a guest");
  heading.id = "modal-title";
  const form = document.createElement("form");
  form.className = "stack-form";
  form.noValidate = true;

  const age = document.createElement("label");
  age.className = "check-row";
  const ageInput = document.createElement("input");
  ageInput.type = "checkbox";
  ageInput.name = "accepted18";
  ageInput.required = true;
  age.append(ageInput, document.createTextNode("I confirm that I am 18 or older."));

  const rules = document.createElement("label");
  rules.className = "check-row";
  const rulesInput = document.createElement("input");
  rulesInput.type = "checkbox";
  rulesInput.name = "acceptedRules";
  rulesInput.required = true;
  rules.append(rulesInput, document.createTextNode("I agree to the public-room rules and will not share links or personal contact details."));

  const error = text("p", "", "form-error");
  error.id = "guest-entry-error";
  error.setAttribute("role", "alert");
  error.setAttribute("aria-live", "assertive");
  error.setAttribute("aria-atomic", "true");
  ageInput.setAttribute("aria-describedby", error.id);
  rulesInput.setAttribute("aria-describedby", error.id);
  const submit = text("button", "Start chatting", "primary wide") as HTMLButtonElement;
  submit.type = "submit";
  const account = text("button", "Create an account instead", "secondary wide") as HTMLButtonElement;
  account.type = "button";
  account.addEventListener("click", () => openAuth("register"));
  form.append(age, rules, error, submit, account);
  const validateConfirmations = (focusFirstInvalid: boolean) => {
    const ageMissing = !ageInput.checked;
    const rulesMissing = !rulesInput.checked;
    ageInput.setAttribute("aria-invalid", String(ageMissing));
    rulesInput.setAttribute("aria-invalid", String(rulesMissing));
    if (!ageMissing && !rulesMissing) {
      error.textContent = "";
      return true;
    }
    error.textContent = ageMissing && rulesMissing
      ? "Confirm that you are 18 or older and agree to the public-room rules before starting."
      : ageMissing
        ? "Confirm that you are 18 or older before starting."
        : "Agree to the public-room rules before starting.";
    if (focusFirstInvalid) (ageMissing ? ageInput : rulesInput).focus();
    return false;
  };
  for (const input of [ageInput, rulesInput]) {
    input.addEventListener("change", () => {
      if (error.textContent) validateConfirmations(false);
    });
  }
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!validateConfirmations(true)) return;
    submit.disabled = true;
    error.textContent = "";
    try {
      const result = await api<{ status: "authenticated"; user: User }>("/api/auth/guest", {
        method: "POST",
        body: JSON.stringify({
          accepted18: ageInput.checked,
          acceptedRules: rulesInput.checked,
          rulesVersion: COMMUNITY_RULES_VERSION,
          campaign: signupCampaign,
        }),
      });
      state.homeVisible = false;
      reconnectAfterAuthentication();
      toast(`You are chatting as ${result.user.nickname}. Create an account any time to unlock saved features.`);
    } catch (requestError) {
      error.textContent = requestError instanceof Error ? requestError.message : "Could not start the guest session.";
      submit.disabled = false;
    }
  });
  section.append(
    heading,
    text("p", "No email or password. ManyHellos gives you a Guest name and a session lasting up to 24 hours so you can join public text chat immediately.", "modal-intro"),
    text("p", "Ending the session does not erase public messages or their internal guest record. They remain reportable under the same community and privacy rules.", "guest-feature-note"),
    text("p", "Guest chat includes replies, reporting and blocking. XP, profiles, reactions, games, the message wall, private feedback and notifications need an account.", "guest-feature-note"),
    form,
  );
  openModal(section);
}

function openAuth(mode: "login" | "register") {
  const section = document.createElement("div");
  section.className = "auth-view";
  const heading = text("h2", mode === "login" ? "Welcome back" : "Join ManyHellos");
  heading.id = "modal-title";
  const intro = text(
    "p",
    mode === "login" ? `Sign in to return to ${currentRoom().title}.` : "Choose a nickname and join the public rooms.",
    "modal-intro",
  );
  const tabs = document.createElement("div");
  tabs.className = "tabs";
  const loginTab = text("button", "Sign in", mode === "login" ? "active" : "");
  const registerTab = text("button", "Create account", mode === "register" ? "active" : "");
  loginTab.addEventListener("click", () => openAuth("login"));
  registerTab.addEventListener("click", () => openAuth("register"));
  tabs.append(loginTab, registerTab);
  const form = document.createElement("form");
  form.className = "stack-form";
  form.append(field("Email", "email", "email", { autocomplete: "email", required: "" }));
  if (mode === "register") form.append(field("Nickname", "text", "nickname", { autocomplete: "nickname", maxlength: "24", required: "" }));
  form.append(field("Password", "password", "password", { autocomplete: mode === "login" ? "current-password" : "new-password", minlength: "10", required: "" }));
  if (mode === "register") {
    const agreement = document.createElement("div");
    agreement.className = "join-agreement";
    agreement.append(text("strong", "Before you join"));
    const points = document.createElement("ul");
    for (const point of [
      "You can disagree; you cannot demean, threaten or harass people.",
      "No hate, sexual exploitation, impersonation or sharing private information.",
      "Do not publish social-media accounts, contact details, links or advertising.",
    ]) points.append(text("li", point));
    agreement.append(points);
    form.append(agreement);
    for (const [name, label] of [
      ["accepted18", "I confirm that I am 18 or older."],
      ["acceptedRules", "I have read and agree to follow these community rules."],
    ] as const) {
      const check = document.createElement("label");
      check.className = "check-field";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.name = name;
      input.required = true;
      check.append(input, text("span", label));
      form.append(check);
    }
  }
  const error = text("p", "", "form-error");
  const submit = text("button", mode === "login" ? "Sign in" : "Create account", "primary wide");
  submit.setAttribute("type", "submit");
  form.append(error, submit);
  if (mode === "login") {
    const forgot = text("button", "Forgot your password?", "secondary wide");
    forgot.setAttribute("type", "button");
    forgot.addEventListener("click", openPasswordRequest);
    form.append(forgot);
  }
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    submit.setAttribute("disabled", "");
    error.textContent = "";
    const data = new FormData(form);
    try {
      const result = await api<{
        status: "authenticated" | "verification_required" | "mfa_required";
        mode?: "enroll" | "challenge";
        qrCode?: string;
        secret?: string;
      }>(`/api/auth/${mode === "login" ? "login" : "register"}`, {
        method: "POST",
        body: JSON.stringify({
          email: data.get("email"),
          nickname: data.get("nickname"),
          password: data.get("password"),
          accepted18: data.get("accepted18") === "on",
          acceptedRules: data.get("acceptedRules") === "on",
          rulesVersion: COMMUNITY_RULES_VERSION,
        }),
      });
      if (result.status === "verification_required") {
        openVerificationPending(String(data.get("email") ?? ""));
        return;
      }
      if (result.status === "mfa_required") {
        openMfaChallenge({
          mode: result.mode === "enroll" ? "enroll" : "challenge",
          qrCode: result.qrCode,
          secret: result.secret,
        });
        return;
      }
      reconnectAfterAuthentication();
      toast(mode === "login" ? "Signed in." : "Welcome to ManyHellos.");
    } catch (requestError) {
      error.textContent = requestError instanceof Error ? requestError.message : "Could not continue.";
      submit.removeAttribute("disabled");
    }
  });
  section.append(heading, intro, tabs, form);
  openModal(section);
}

function notificationTime(timestamp: number) {
  return new Intl.DateTimeFormat("en-NZ", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Pacific/Auckland",
  }).format(timestamp);
}

async function markNotificationsRead(ids?: number[]) {
  try {
    const inbox = await api<NotificationInbox>("/api/notifications/read", {
      method: "POST",
      body: JSON.stringify(ids ? { ids } : { all: true }),
    });
    applyInbox(inbox);
    renderNotificationButton();
    openNotifications();
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not update your inbox.", "error");
  }
}

async function setNotificationSaved(item: NotificationItem, saved: boolean) {
  try {
    const inbox = await api<NotificationInbox>("/api/notifications/save", {
      method: "POST",
      body: JSON.stringify({ id: item.id, saved }),
    });
    applyInbox(inbox);
    renderNotificationButton();
    openNotifications();
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not update this inbox message.", "error");
  }
}

async function deleteNotification(item: NotificationItem) {
  try {
    const inbox = await api<NotificationInbox>("/api/notifications/delete", {
      method: "POST",
      body: JSON.stringify({ id: item.id }),
    });
    applyInbox(inbox);
    renderNotificationButton();
    openNotifications();
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not delete this inbox message.", "error");
  }
}

async function openMentionMessage(item: NotificationItem) {
  if (!item.messageSeq) return;
  try {
    const result = await api<{ message: Message }>(`/api/messages/${item.messageSeq}`);
    if (result.message.roomId !== state.roomId) switchRoom(result.message.roomId);
    if (state.roomId !== result.message.roomId) return;
    state.messages.set(result.message.seq, result.message);
    if (item.readAt == null) {
      const inbox = await api<NotificationInbox>("/api/notifications/read", {
        method: "POST",
        body: JSON.stringify({ ids: [item.id] }),
      });
      applyInbox(inbox);
      renderNotificationButton();
    }
    closeModal();
    renderMessages(false);
    window.requestAnimationFrame(() => {
      const target = elements.messages.querySelector<HTMLElement>(`[data-seq="${item.messageSeq}"]`);
      target?.scrollIntoView({ block: "center", behavior: state.classicMode ? "auto" : "smooth" });
      target?.classList.add("inbox-target");
      if (target) window.setTimeout(() => target.classList.remove("inbox-target"), 2200);
    });
  } catch (error) {
    toast(error instanceof Error ? error.message : "That public message is no longer available.", "error");
  }
}

function openReportRestrictionAppeal() {
  const section = document.createElement("div");
  const heading = text("h2", "Appeal reporting restriction");
  heading.id = "modal-title";
  const form = document.createElement("form");
  form.className = "stack-form";
  form.append(textareaField("Your appeal", "body", "Explain why the reporting restriction should be reviewed."));
  const submit = text("button", "Submit appeal", "primary wide");
  submit.setAttribute("type", "submit");
  form.append(submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    submit.setAttribute("disabled", "true");
    try {
      await api("/api/report-restriction-appeals", { method: "POST", body: JSON.stringify({ body: new FormData(form).get("body") }) });
      closeModal();
      toast("Appeal submitted to the site owner.");
    } catch (error) {
      submit.removeAttribute("disabled");
      toast(error instanceof Error ? error.message : "Appeal failed.", "error");
    }
  });
  section.append(
    heading,
    text("p", "The site owner will review this appeal. You can appeal each reporting restriction once.", "modal-intro"),
    form,
  );
  openModal(section);
}

function openNotifications() {
  if (!state.viewer) return openAuth("login");
  const section = document.createElement("div");
  section.className = "notification-center";
  const heading = text("h2", "Your inbox");
  heading.id = "modal-title";
  section.append(
    heading,
    text("p", `Public @mentions and account updates stay for ${state.notificationRetentionDays} days. Saved mail stays until you remove it.`, "modal-intro"),
  );

  const tabs = document.createElement("div");
  tabs.className = "inbox-tabs";
  const allTab = text("button", `Inbox${state.notificationUnreadCount ? ` · ${state.notificationUnreadCount}` : ""}`, state.inboxView === "all" ? "active" : "");
  const savedTab = text("button", `Saved · ${state.notificationSavedCount}/${state.notificationSavedLimit}`, state.inboxView === "saved" ? "active" : "");
  for (const button of [allTab, savedTab]) button.setAttribute("type", "button");
  allTab.setAttribute("aria-pressed", String(state.inboxView === "all"));
  savedTab.setAttribute("aria-pressed", String(state.inboxView === "saved"));
  allTab.addEventListener("click", () => { state.inboxView = "all"; openNotifications(); });
  savedTab.addEventListener("click", () => { state.inboxView = "saved"; openNotifications(); });
  tabs.append(allTab, savedTab);
  section.append(tabs);

  if (state.inboxView === "all" && state.notificationUnreadCount > 0) {
    const toolbar = document.createElement("div");
    toolbar.className = "notification-toolbar";
    toolbar.append(
      text("span", `${state.notificationUnreadCount} unread`, "case-status status-open"),
    );
    const markAll = text("button", "Mark all read", "secondary");
    markAll.setAttribute("type", "button");
    markAll.addEventListener("click", () => markNotificationsRead());
    toolbar.append(markAll);
    section.append(toolbar);
  }

  const list = document.createElement("div");
  list.className = "notification-list";
  const shownItems = state.inboxView === "saved" ? state.savedNotifications : state.notifications;
  if (!shownItems.length) {
    list.append(text("p", state.inboxView === "saved" ? "No saved inbox messages yet." : "No inbox messages yet.", "empty-small"));
  }
  for (const item of shownItems) {
    const article = document.createElement("article");
    article.className = `notification-item${item.readAt == null ? " unread" : ""}`;
    const top = document.createElement("div");
    top.className = "notification-item-top";
    top.append(text("h3", item.title), text("time", notificationTime(item.createdAt)));
    article.append(top, text("p", item.body));
    const lifecycle = item.savedAt
      ? "Saved until you remove it"
      : item.expiresAt
        ? `Expires in ${Math.max(0, Math.ceil((item.expiresAt - Date.now()) / 86_400_000))} days`
        : "Inbox update";
    article.append(text("p", lifecycle, "notification-lifecycle"));
    const footer = document.createElement("div");
    footer.className = "notification-item-footer";
    if (item.reference) footer.append(text("span", item.reference.replace(":", " #"), "notification-reference"));
    if (item.kind === "mention" && item.messageSeq) {
      const view = text("button", "View public message", "action-link");
      view.setAttribute("type", "button");
      view.addEventListener("click", () => void openMentionMessage(item));
      footer.append(view);
    }
    if (canAppealReportingRestriction(item, state.notifications)) {
      const appeal = text("button", "Appeal restriction", "action-link");
      appeal.setAttribute("type", "button");
      appeal.addEventListener("click", openReportRestrictionAppeal);
      footer.append(appeal);
    }
    if (item.readAt == null) {
      const markRead = text("button", "Mark read", "action-link");
      markRead.setAttribute("type", "button");
      markRead.setAttribute("aria-label", `Mark ${item.title} as read`);
      markRead.addEventListener("click", () => markNotificationsRead([item.id]));
      footer.append(markRead);
    } else footer.append(text("span", "Read", "notification-read"));
    const save = text("button", item.savedAt ? "Unsave" : "Save", "action-link");
    save.setAttribute("type", "button");
    save.setAttribute("aria-label", `${item.savedAt ? "Remove" : "Add"} ${item.title} ${item.savedAt ? "from" : "to"} saved mail`);
    save.addEventListener("click", () => void setNotificationSaved(item, item.savedAt == null));
    const remove = text("button", "Delete", "action-link remove-action");
    remove.setAttribute("type", "button");
    remove.setAttribute("aria-label", `Delete ${item.title} from your inbox`);
    remove.addEventListener("click", () => void deleteNotification(item));
    footer.append(save, remove);
    article.append(footer);
    list.append(article);
  }
  section.append(list);
  openModal(section);
}

async function openSessionManager() {
  try {
    const result = await api<{ sessions: AccountSession[] }>("/api/auth/sessions");
    const section = document.createElement("div");
    section.className = "session-view";
    const heading = text("h2", "Sign-in sessions");
    heading.id = "modal-title";
    section.append(
      heading,
      text("p", "ManyHellos stores no browser fingerprint. Each row is one signed-in browser session; revoke anything you do not recognise.", "modal-intro"),
    );
    const list = document.createElement("div");
    list.className = "session-list";
    const revoke = async (payload: { sessionId?: string; scope?: "others" | "all" }) => {
      const response = await api<{ endedCurrent: boolean }>("/api/auth/sessions/revoke", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (response.endedCurrent) {
        location.reload();
        return;
      }
      await openSessionManager();
      toast("Session access revoked.");
    };
    for (const session of result.sessions) {
      const row = document.createElement("article");
      row.className = "session-row";
      row.append(
        text("strong", session.current ? "This browser" : "Web session"),
        text("p", `Last used ${notificationTime(session.lastSeenAt)} · expires ${notificationTime(session.expiresAt)}`, "setting-help"),
        text("p", session.assuranceLevel === "aal2" ? "Two-step verified" : "Password verified", "setting-help"),
      );
      const button = text("button", session.current ? "Sign out this browser" : "Revoke session", session.current ? "danger-link" : "secondary") as HTMLButtonElement;
      button.type = "button";
      button.addEventListener("click", () => void revoke({ sessionId: session.id }));
      row.append(button);
      list.append(row);
    }
    const others = text("button", "Revoke all other sessions", "secondary wide") as HTMLButtonElement;
    others.type = "button";
    others.disabled = result.sessions.every((session) => session.current);
    others.addEventListener("click", () => void revoke({ scope: "others" }));
    const all = text("button", "Sign out everywhere", "danger-link wide") as HTMLButtonElement;
    all.type = "button";
    all.addEventListener("click", () => void revoke({ scope: "all" }));
    section.append(list, others, all);
    openModal(section);
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not load sign-in sessions.", "error");
  }
}

function avatarCollectionIdFor(avatarValue: string) {
  return avatarCollections.find((collection) => collection.avatars.includes(avatarValue as never))?.id ?? avatarCollections[0]!.id;
}

function openProfile() {
  openCharacterStudio("form");
}

function openCharacterStudio(activeSlot: CharacterAppearanceSlot = "form") {
  if (!state.viewer) return openAuth("login");
  if (state.viewer.isGuest) return openAuth("register");
  const user = state.viewer;
  const section = document.createElement("div");
  section.className = "character-studio";

  const heading = text("h2", "Character profile");
  heading.id = "modal-title";
  const headingCopy = document.createElement("div");
  headingCopy.className = "character-studio-heading-copy";
  headingCopy.append(
    heading,
    text("p", "Build the identity people recognise in public rooms. Appearance is expressive only and never changes reach, safety or moderation rights.", "modal-intro"),
  );
  const accountSettings = text("button", "Account & chat settings", "secondary character-settings-link") as HTMLButtonElement;
  accountSettings.type = "button";
  accountSettings.addEventListener("click", () => openProfileSettings());
  const header = document.createElement("header");
  header.className = "character-studio-header";
  header.append(headingCopy, accountSettings);

  const stagePanel = document.createElement("section");
  stagePanel.className = "character-stage-panel";
  stagePanel.setAttribute("aria-label", "Equipped character preview");
  const stage = document.createElement("div");
  stage.className = "character-stage";
  stage.dataset.form = user.appearance.form;
  stage.dataset.hair = user.appearance.hair;
  stage.dataset.robes = user.appearance.robes;
  stage.dataset.relic = user.appearance.relic;
  stage.dataset.scene = user.appearance.scene;
  const scene = document.createElement("img");
  scene.className = "character-scene-image";
  scene.src = characterSceneSources[user.appearance.scene];
  scene.alt = "";
  scene.decoding = "async";
  const stageStatus = document.createElement("div");
  stageStatus.className = "character-stage-status";
  stageStatus.append(
    text("span", `Level ${user.level}`, "character-level-mark"),
    text("span", "5-piece look", "character-equipped-count"),
  );
  const figure = characterFigure(user, "studio");
  const characterName = document.createElement("div");
  characterName.className = "character-stage-name";
  characterName.append(titleBadge(user, "character-stage-title"), text("strong", user.nickname));
  const stageMeta = text(
    "p",
    `${characterAppearanceLabel("form", user.appearance.form)} · ${characterAppearanceLabel("robes", user.appearance.robes)} · ${characterAppearanceLabel("scene", user.appearance.scene)}`,
    "character-stage-meta",
  );
  stage.append(scene, stageStatus, figure, characterName, stageMeta);

  const equipped = document.createElement("dl");
  equipped.className = "character-equipped-list";
  for (const slot of characterAppearanceSlots) {
    const item = document.createElement("div");
    item.append(text("dt", slot.label), text("dd", characterAppearanceLabel(slot.key, user.appearance[slot.key])));
    equipped.append(item);
  }
  stagePanel.append(stage, equipped);

  const controls = document.createElement("section");
  controls.className = "character-loadout";
  const controlsHeading = text("h3", "Appearance loadout");
  const saveStatus = text("span", "Saved to your account", "character-save-status");
  saveStatus.setAttribute("role", "status");
  const controlsHeader = document.createElement("div");
  controlsHeader.className = "character-loadout-header";
  controlsHeader.append(controlsHeading, saveStatus);

  const slotList = document.createElement("div");
  slotList.className = "character-slot-list";
  slotList.setAttribute("role", "tablist");
  slotList.setAttribute("aria-label", "Character appearance slots");
  for (const slot of characterAppearanceSlots) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `character-slot${slot.key === activeSlot ? " selected" : ""}`;
    button.setAttribute("role", "tab");
    button.setAttribute("aria-selected", String(slot.key === activeSlot));
    button.dataset.slot = slot.key;
    button.append(
      text("strong", slot.label),
      text("span", characterAppearanceLabel(slot.key, user.appearance[slot.key])),
    );
    button.addEventListener("click", () => openCharacterStudio(slot.key));
    slotList.append(button);
  }

  const active = characterAppearanceSlots.find((slot) => slot.key === activeSlot)!;
  const editor = document.createElement("section");
  editor.className = "character-slot-editor";
  editor.setAttribute("role", "tabpanel");
  editor.append(text("h4", active.label), text("p", active.description, "setting-help"));
  const options = document.createElement("div");
  options.className = `character-option-grid character-options-${activeSlot}`;

  for (const option of characterAppearanceOptions[activeSlot]) {
      const selected = user.appearance[activeSlot] === option.value;
      const progressionSlot = activeSlot as CharacterAppearanceProgressionSlot;
      const minLevel = characterAppearanceMinLevel(progressionSlot, option.value);
      const elevated = user.role === "owner" || user.role === "admin";
      const unlocked = canUseCharacterAppearance(user.level, elevated, progressionSlot, option.value);
      const button = document.createElement("button");
      button.type = "button";
      button.className = `character-option${selected ? " selected" : ""}${unlocked ? "" : " locked"}`;
      button.dataset.slot = activeSlot;
      button.dataset.option = option.value;
      button.setAttribute("aria-pressed", String(selected));
      button.disabled = !unlocked;
      if (!unlocked) button.setAttribute("aria-label", `${option.label}. Unlocks at level ${minLevel}.`);
      if (activeSlot === "scene") {
        const image = document.createElement("img");
        image.src = characterSceneSources[option.value as CharacterAppearance["scene"]];
        image.alt = "";
        image.loading = "lazy";
        button.append(image);
      } else if (activeSlot === "robes") {
        const image = document.createElement("img");
        image.className = "character-option-art character-option-outfit-art";
        image.src = characterRobeSources[option.value as CharacterAppearance["robes"]];
        image.alt = "";
        image.loading = "lazy";
        button.append(image);
      } else if (activeSlot === "hair" && characterHairSources[option.value as CharacterAppearance["hair"]]) {
        const image = document.createElement("img");
        image.className = "character-option-art character-option-hair-art";
        image.src = characterHairSources[option.value as CharacterAppearance["hair"]]!;
        image.alt = "";
        image.loading = "lazy";
        button.append(image);
      } else if (activeSlot === "relic" && characterRelicSources[option.value as CharacterAppearance["relic"]]) {
        const image = document.createElement("img");
        image.className = "character-option-art character-option-relic-art";
        image.src = characterRelicSources[option.value as CharacterAppearance["relic"]]!;
        image.alt = "";
        image.loading = "lazy";
        button.append(image);
      } else {
        button.append(text("span", "", "character-option-swatch"));
      }
      button.append(text("strong", option.label), text("span", option.detail));
      if (!unlocked) button.append(text("span", `Level ${minLevel}`, "character-option-lock"));
      button.addEventListener("click", () => void saveCharacterAppearance({ [activeSlot]: option.value }, activeSlot));
      options.append(button);
  }
  editor.append(options);
  controls.append(controlsHeader, slotList, editor);

  const layout = document.createElement("div");
  layout.className = "character-studio-layout";
  layout.append(stagePanel, controls);
  section.append(header, layout);
  openModal(section);
}

async function saveCharacterAppearance(
  update: Partial<CharacterAppearance>,
  activeSlot: CharacterAppearanceSlot,
) {
  try {
    const result = await api<{ user: User }>("/api/profile/appearance", { method: "POST", body: JSON.stringify(update) });
    state.viewer = result.user;
    renderIdentity();
    openCharacterStudio(activeSlot);
    toast("Character appearance saved.");
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not save the character appearance.", "error");
  }
}

function openProfileSettings(requestedAvatarCollection?: string) {
  if (!state.viewer) return openAuth("login");
  if (state.viewer.isGuest) return openAuth("register");
  const user = state.viewer;
  const section = document.createElement("div");
  const heading = text("h2", "Your profile");
  heading.id = "modal-title";
  section.append(
    heading,
    text("p", `${user.nickname} · Level ${user.level} ${user.title} · ${user.xp} XP`, "modal-intro"),
    text("p", `Joined ${user.joinedYear}${user.foundingMember ? " · Founding Kiwi" : ""}`, "tenure-card"),
  );
  const presenceButton = text("button", `Online status · ${presenceStatusLabel(state.presenceStatus)}`, `secondary wide presence-profile-button status-${state.presenceStatus}`) as HTMLButtonElement;
  presenceButton.type = "button";
  presenceButton.addEventListener("click", openPresenceStatus);
  const guide = text("button", "View the complete 1–50 XP table", "secondary wide");
  guide.addEventListener("click", openLevelGuide);
  const appearance = text("button", "Open character appearance", "primary wide") as HTMLButtonElement;
  appearance.type = "button";
  appearance.addEventListener("click", openProfile);

  const avatarSourceOptions = document.createElement("div");
  avatarSourceOptions.className = "chat-avatar-source-options";
  const avatarSourceButton = (source: AvatarSource, avatarValue: string, title: string, detail: string) => {
    const selected = user.avatarSource === source;
    const button = document.createElement("button");
    button.type = "button";
    button.className = `chat-avatar-source-option${selected ? " selected" : ""}`;
    button.setAttribute("aria-pressed", String(selected));
    button.append(
      avatarArt(avatarValue, user.nickname.slice(0, 1).toUpperCase(), "avatar-option-art chat-avatar-source-art", user.color),
      text("strong", title),
      text("span", detail),
    );
    button.addEventListener("click", () => void saveChatAvatar({ avatarSource: source }, requestedAvatarCollection));
    return button;
  };
  avatarSourceOptions.append(
    avatarSourceButton(
      "character",
      characterPortraitForRobes(user.appearance.robes),
      "Use character portrait",
      `${characterAppearanceLabel("robes", user.appearance.robes)} · follows your complete character`,
    ),
    avatarSourceButton(
      "system",
      user.systemAvatar,
      "Use system avatar",
      `${avatarLabels[user.systemAvatar] ?? "Current avatar"} · stays independent`,
    ),
  );

  const avatarCollectionId = requestedAvatarCollection ?? avatarCollectionIdFor(user.systemAvatar);
  const avatarCollection = avatarCollections.find((collection) => collection.id === avatarCollectionId) ?? avatarCollections[0]!;
  const avatarCollectionField = document.createElement("label");
  avatarCollectionField.className = "character-collection-field chat-avatar-collection-field";
  avatarCollectionField.append(text("span", "System avatar collection"));
  const avatarCollectionSelect = document.createElement("select");
  avatarCollectionSelect.setAttribute("aria-label", "System avatar collection");
  for (const collection of avatarCollections) {
    const option = document.createElement("option");
    option.value = collection.id;
    option.textContent = collection.title.replace(/^Collection \d+ · /u, "");
    option.selected = collection.id === avatarCollection.id;
    avatarCollectionSelect.append(option);
  }
  avatarCollectionSelect.addEventListener("change", () => openProfileSettings(avatarCollectionSelect.value));
  avatarCollectionField.append(avatarCollectionSelect);

  const systemAvatarOptions = document.createElement("div");
  systemAvatarOptions.className = "avatar-options chat-avatar-options";
  for (const value of avatarCollection.avatars) {
    const label = avatarLabels[value] ?? value;
    const selected = user.avatarSource === "system" && user.systemAvatar === value;
    const button = document.createElement("button");
    button.type = "button";
    button.className = selected ? "selected" : "";
    button.setAttribute("aria-pressed", String(selected));
    button.setAttribute("aria-label", `Use ${label} as your chat avatar`);
    button.append(
      avatarArt(value, value.slice(0, 2).toUpperCase(), "avatar-option-art", user.color),
      text("span", label, "avatar-option-label"),
    );
    button.addEventListener("click", () => void saveChatAvatar({ avatar: value, avatarSource: "system" }, avatarCollection.id));
    systemAvatarOptions.append(button);
  }
  section.append(
    appearance,
    text("p", "Your character look and chat avatar are separate. Changing one no longer replaces the other.", "setting-help"),
    text("h3", "Chat avatar"),
    text("p", "Use a portrait cropped from your complete character, or keep an independent system avatar for chat, member lists and notifications.", "setting-help"),
    avatarSourceOptions,
    avatarCollectionField,
    text("p", avatarCollection.description, "character-collection-description"),
    systemAvatarOptions,
    presenceButton,
    guide,
  );

  section.append(text("h3", "Title"), text("p", `System title: ${user.systemTitle}`, "setting-help"));
  if (user.unlocks.customTitle) {
    const titleForm = document.createElement("form");
    titleForm.className = "stack-form custom-title-form";
    const titleField = field("Custom title", "text", "customTitle", {
      minlength: "2",
      maxlength: "20",
      placeholder: "e.g. Storm Seer",
      autocomplete: "off",
    });
    const titleInput = titleField.querySelector<HTMLInputElement>("input")!;
    titleInput.value = user.customTitle ?? "";
    const actions = document.createElement("div");
    actions.className = "modal-actions";
    const save = text("button", "Save custom title", "primary");
    save.setAttribute("type", "submit");
    const useSystem = text("button", `Use ${user.systemTitle}`, "secondary");
    useSystem.setAttribute("type", "button");
    useSystem.toggleAttribute("disabled", !user.customTitle);
    useSystem.addEventListener("click", () => saveCustomTitle(null));
    actions.append(save, useSystem);
    titleForm.append(
      titleField,
      text("p", "Use 2–20 English or te reo Māori letters, numbers, spaces, apostrophes, & or -. Titles are public and can be reported.", "setting-help"),
      actions,
    );
    titleForm.addEventListener("submit", (event) => {
      event.preventDefault();
      void saveCustomTitle(titleInput.value);
    });
    section.append(titleForm);
  } else {
    section.append(text("p", "Custom titles unlock at Level 30. Your system title changes every five levels.", "notice"));
  }

  if (user.unlocks.nicknameColor) {
    section.append(text("h3", "Nickname colour"));
    const colors = document.createElement("div");
    colors.className = "color-options";
    for (const color of ["#74f2ce", "#8cc8ff", "#ffd166", "#ff8fab", "#c7a0ff"]) {
      const button = text("button", "", user.color === color ? "selected" : "");
      button.style.background = color;
      button.setAttribute("aria-label", `Use colour ${color}`);
      button.addEventListener("click", async () => {
        try {
          const result = await api<{ user: User }>("/api/profile/color", { method: "POST", body: JSON.stringify({ color }) });
          state.viewer = result.user;
          closeModal();
          renderIdentity();
        } catch (error) { toast(error instanceof Error ? error.message : "Could not save.", "error"); }
      });
      colors.append(button);
    }
    section.append(colors);
  } else section.append(text("p", "Nickname colours unlock at level 5.", "notice"));

  section.append(text("h3", "Profile theme"));
  if (user.unlocks.profileTheme) {
    const themes = document.createElement("div");
    themes.className = "choice-options";
    for (const value of ["classic", "harbour", "bush", "sunset"]) {
      const button = text("button", value[0]!.toUpperCase() + value.slice(1), user.profileTheme === value ? "selected" : "");
      button.setAttribute("type", "button");
      button.addEventListener("click", () => saveProfileStyle({ profileTheme: value }));
      themes.append(button);
    }
    section.append(themes);
  } else section.append(text("p", "Profile themes unlock at level 8.", "notice"));

  section.append(text("h3", "Chat font"));
  const fontChoices = [
    { value: "standard", label: "Standard" },
    { value: "rounded", label: "Friendly" },
    { value: "clear", label: "Clear" },
    { value: "typewriter", label: "Typewriter" },
    { value: "editorial", label: "Editorial" },
    { value: "marker", label: "Handwritten" },
  ];
  const fonts = document.createElement("div");
  fonts.className = "choice-options font-options";
  for (const { value, label } of fontChoices) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `font-choice${user.fontStyle === value ? " selected" : ""}`;
    button.setAttribute("aria-pressed", String(user.fontStyle === value));
    button.append(text("strong", label), text("span", "Kia ora · Tēnā koe · 123", `font-sample font-${value}`));
    button.addEventListener("click", () => saveProfileStyle({ fontStyle: value }));
    fonts.append(button);
  }
  section.append(
    fonts,
    text("p", "Choose from fonts available on this device. Classic view always uses Standard.", "setting-help"),
  );

  section.append(text("h3", "Message effect"));
  const effects = document.createElement("div");
  effects.className = "choice-options effect-options";
  const effectLevels: Record<string, number> = { none: 1, outfit: 15, glow: 15, shimmer: 20, aurora: 30 };
  for (const [value, label] of Object.entries({ none: "No effect", outfit: "Follow outfit", glow: "Glow", shimmer: "Shimmer", aurora: "Aurora" })) {
    const minimum = effectLevels[value]!;
    const unlocked = user.level >= minimum;
    const button = text("button", unlocked ? label : `${label} · Lv${minimum}`, user.textEffect === value ? "selected" : "");
    button.setAttribute("type", "button");
    button.toggleAttribute("disabled", !unlocked);
    button.classList.add(`effect-choice-${value}`);
    button.addEventListener("click", () => saveProfileStyle({ textEffect: value }));
    effects.append(button);
  }
  section.append(effects, text("p", "Follow outfit adds a restrained wash based on your current robes. Effects never change text contrast, and Classic view hides them.", "setting-help"));

  section.append(text("h3", `Blocked people · ${state.blockedUsers.length}`));
  const blocked = document.createElement("div");
  blocked.className = "blocked-list";
  if (!state.blockedUsers.length) blocked.append(text("p", "You have not blocked anyone.", "empty-small"));
  for (const hiddenUser of state.blockedUsers) {
    const row = document.createElement("div");
    row.append(avatar(hiddenUser), text("span", hiddenUser.nickname));
    const unhide = text("button", "Unblock", "action-link");
    unhide.setAttribute("type", "button");
    unhide.addEventListener("click", async () => {
      await setBlocked(hiddenUser, false, false);
      openProfileSettings();
    });
    row.append(unhide);
    blocked.append(row);
  }
  section.append(blocked);

  section.append(text("h3", "Account & data"));
  const privacy = document.createElement("a");
  privacy.className = "secondary wide info-link-button";
  privacy.href = "/privacy.html#account-deletion";
  privacy.textContent = "Privacy notice & account deletion";
  section.append(
    privacy,
    text("p", "Deletion requests are verified and reviewed so profile data can be removed or de-identified without erasing public conversation context, appeal rights or necessary safety records.", "setting-help"),
  );

  const sessions = text("button", "Sign-in sessions", "secondary wide");
  sessions.setAttribute("type", "button");
  sessions.addEventListener("click", () => void openSessionManager());
  section.append(sessions);

  const logout = text("button", "Sign out", "danger-link wide");
  logout.addEventListener("click", async () => {
    await api("/api/auth/logout", { method: "POST" });
    closeModal();
    state.viewer = null;
    state.messages.clear();
    state.catchupAfter = null;
    state.catchupMode = "recent";
    state.roomState = null;
    state.returnStartSeq = null;
    state.returnViewOpened = false;
    state.readCursorSent = 0;
    state.pending.clear();
    state.gamePending.clear();
    clearChatRateLimitState();
    state.notifications = [];
    state.savedNotifications = [];
    state.notificationUnreadCount = 0;
    state.notificationSavedCount = 0;
    connect();
  });
  if (user.role === "member") {
    const apply = text("button", user.unlocks.adminApplication ? "Apply to become an admin" : "Admin applications unlock at level 30 + 30 days", "secondary wide");
    apply.toggleAttribute("disabled", !user.unlocks.adminApplication);
    apply.addEventListener("click", openAdminApplication);
    section.append(apply);
  }
  if (user.role !== "member") {
    const mod = text("button", "Open moderation desk", "secondary wide");
    mod.addEventListener("click", () => window.location.assign("/admin"));
    section.append(mod);
  }
  section.append(logout);
  openModal(section);
}

function setPresenceStatus(status: PresenceStatus) {
  state.presenceStatus = status;
  localStorage.setItem("kiwichat:presence-status", status);
  sendSocket({ type: "presence_status", status });
  renderIdentity();
}

function openPresenceStatus() {
  if (!state.viewer || state.viewer.isGuest) return;
  const section = document.createElement("div");
  section.className = "presence-status-view";
  const heading = text("h2", "Online status");
  heading.id = "modal-title";
  const choices = document.createElement("div");
  choices.className = "presence-status-options";
  const options: Array<{ value: PresenceStatus; title: string; detail: string }> = [
    { value: "online", title: "Online", detail: "Shown in the room and included in the live count." },
    { value: "busy", title: "Busy", detail: "Shown in the room with a busy marker so people know replies may be slow." },
    { value: "invisible", title: "Invisible", detail: "Hidden from member lists and live counts until you send a public chat message." },
  ];
  for (const option of options) {
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.colorTheme = option.value;
    button.className = `presence-status-option status-${option.value}${state.presenceStatus === option.value ? " selected" : ""}`;
    button.setAttribute("aria-pressed", String(state.presenceStatus === option.value));
    button.append(
      text("span", "", "presence-status-dot"),
      text("strong", option.title),
      text("span", option.detail),
    );
    button.addEventListener("click", () => {
      setPresenceStatus(option.value);
      closeModal();
      toast(`You are now ${option.title.toLowerCase()}.`);
    });
    choices.append(button);
  }
  section.append(
    heading,
    text("p", "Choose how you appear to other people. Sending a public chat message automatically changes Invisible back to Online.", "modal-intro"),
    choices,
  );
  openModal(section);
}

async function saveChatAvatar(update: { avatar?: string; avatarSource: AvatarSource }, avatarCollectionId?: string) {
  try {
    const result = await api<{ user: User }>("/api/profile/style", { method: "POST", body: JSON.stringify(update) });
    state.viewer = result.user;
    renderAll();
    openProfileSettings(avatarCollectionId);
    toast(update.avatarSource === "character" ? "Chat avatar now follows your character portrait." : "System chat avatar updated.");
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not save the chat avatar.", "error");
  }
}

async function saveProfileStyle(update: { avatar?: string; avatarSource?: AvatarSource; fontStyle?: string; textEffect?: string; profileTheme?: string }) {
  try {
    const result = await api<{ user: User }>("/api/profile/style", { method: "POST", body: JSON.stringify(update) });
    state.viewer = result.user;
    closeModal();
    renderAll();
    toast("Profile updated.");
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not save.", "error");
  }
}

async function saveCustomTitle(customTitle: string | null) {
  try {
    const result = await api<{ user: User }>("/api/profile/title", { method: "POST", body: JSON.stringify({ customTitle }) });
    state.viewer = result.user;
    closeModal();
    renderAll();
    toast(customTitle ? "Custom title updated." : "System title restored.");
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not save the title.", "error");
  }
}

function openLevelGuide() {
  const section = document.createElement("div");
  section.className = "level-guide";
  const heading = text("h2", "ManyHellos levels 1–50");
  heading.id = "modal-title";
  section.append(
    heading,
    text("p", "Levels use 40 × (level − 1)² total XP. You can earn up to 80 XP per New Zealand day, so Level 50 takes at least 1,201 days.", "modal-intro"),
  );
  if (!state.progression.length) {
    section.append(text("p", "The level table is still loading. Try again in a moment.", "notice"));
    openModal(section);
    return;
  }
  const wrap = document.createElement("div");
  wrap.className = "level-table-wrap";
  const table = document.createElement("table");
  table.className = "level-table";
  const head = document.createElement("thead");
  const header = document.createElement("tr");
  for (const label of ["Level", "XP from prior", "Total XP", "Title and unlocks"]) header.append(text("th", label));
  head.append(header);
  const body = document.createElement("tbody");
  const number = new Intl.NumberFormat("en-NZ");
  for (const item of state.progression) {
    const row = document.createElement("tr");
    if (state.viewer?.level === item.level) {
      row.className = "current-level";
      row.setAttribute("aria-current", "true");
    }
    row.append(
      text("td", `Lv${item.level}`),
      text("td", item.level === 1 ? "—" : `+${number.format(item.xpFromPrevious)}`),
      text("td", number.format(item.xp)),
      text("td", `${item.title}${item.unlocks.length ? ` · ${item.unlocks.join(" · ")}` : ""}`),
    );
    body.append(row);
  }
  table.append(head, body);
  wrap.append(table);
  section.append(wrap, text("p", "Online XP requires a visible, recently active tab. Extra tabs, idle time, repeated text and rapid posting do not increase the reward.", "setting-help"));
  openModal(section);
}

async function setBlocked(user: User, blocked: boolean, closeAfter = false) {
  try {
    const result = await api<{ blockedIds: string[]; blockedUsers: User[]; roomState: RoomMemberState }>("/api/blocks", {
      method: "POST",
      body: JSON.stringify({ roomId: state.roomId, targetUserId: user.id, blocked }),
    });
    state.blockedIds = new Set(result.blockedIds);
    state.blockedUsers = result.blockedUsers;
    applyRoomState(result.roomState);
    if (closeAfter) closeModal();
    renderMessages();
    if (state.game) renderGameMessages(state.game);
    toast(blocked ? `${user.nickname} is blocked. Their messages are hidden in every view.` : `${user.nickname} is unblocked and visible again.`);
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not update blocked people.", "error");
  }
}

function openBlockList() {
  if (!state.viewer) return;
  const section = document.createElement("div");
  section.className = "block-list-view";
  const heading = text("h2", "Blocked people");
  heading.id = "modal-title";
  section.append(
    heading,
    text("p", "Blocked people disappear from All, Focus, the room stream and game chat. Blocking is private and does not remove their public posts for anyone else.", "modal-intro"),
  );
  const list = document.createElement("div");
  list.className = "blocked-list";
  if (!state.blockedUsers.length) list.append(text("p", "You have not blocked anyone.", "empty-small"));
  for (const blockedUser of state.blockedUsers) {
    const row = document.createElement("div");
    row.append(avatar(blockedUser), text("span", blockedUser.nickname));
    const unblock = text("button", "Unblock", "secondary") as HTMLButtonElement;
    unblock.type = "button";
    unblock.addEventListener("click", async () => {
      unblock.disabled = true;
      await setBlocked(blockedUser, false, false);
      openBlockList();
    });
    row.append(unblock);
    list.append(row);
  }
  section.append(list);
  openModal(section);
}

function openMember(user: User) {
  const section = document.createElement("div");
  const heading = document.createElement("h2");
  heading.append(titleBadge(user, "identity-title-prefix"), document.createTextNode(` ${user.nickname}`));
  heading.id = "modal-title";
  section.dataset.theme = user.profileTheme;
  section.append(
    avatar(user, "identity-avatar"),
    heading,
    text("p", user.isGuest ? "Temporary public-chat guest" : user.customTitle ? `Level ${user.level} · System title ${user.systemTitle}` : `Level ${user.level}`, "modal-intro"),
    text("p", user.isGuest ? "Guest profiles, XP and saved features are not available." : `Joined ManyHellos: ${user.joinedYear}`, "tenure-card"),
  );
  if (user.badges.length) section.append(text("p", user.badges.join(" · "), "notice"));
  if (state.viewer && state.viewer.id !== user.id) {
    const mention = text("button", `Mention @${user.nickname}`, "secondary wide");
    mention.addEventListener("click", () => mentionUser(user));
    const isBlocked = state.blockedIds.has(user.id);
    const block = text("button", isBlocked ? "Unblock member" : "Block this member", "secondary wide");
    block.addEventListener("click", () => setBlocked(user, !isBlocked, true));
    const report = text("button", "Report member", "danger-link wide");
    report.addEventListener("click", () => openReport(undefined, user));
    section.append(mention, block, report);
    if (state.viewer.role !== "member") {
      const moderate = text("button", "Moderator actions", "secondary wide");
      moderate.addEventListener("click", () => openModerate(user));
      section.append(moderate);
    }
  }
  openModal(section);
}

function openFocusView() {
  if (!state.viewer || state.viewer.isGuest) return;
  const people = new Map<string, User>();
  for (const user of state.members) people.set(user.id, user);
  for (const message of state.messages.values()) people.set(message.author.id, message.author);
  people.delete(state.viewer.id);
  for (const blockedId of state.blockedIds) people.delete(blockedId);
  const candidates = [...people.values()].sort((left, right) => left.nickname.localeCompare(right.nickname, "en-NZ", { sensitivity: "base" }));

  const section = document.createElement("div");
  section.className = "focus-view-settings";
  const heading = text("h2", "Focus view");
  heading.id = "modal-title";
  const count = text("p", "", "setting-help");
  count.setAttribute("aria-live", "polite");
  const choices = document.createElement("div");
  choices.className = "focus-choices";
  const selected = new Set(state.focusIds);
  const updateCount = () => { count.textContent = `${selected.size} of 20 people selected · your own messages and public @mentions stay visible.`; };

  for (const user of candidates) {
    const label = document.createElement("label");
    label.className = "focus-choice";
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = selected.has(user.id);
    checkbox.addEventListener("change", () => {
      if (checkbox.checked && selected.size >= 20) {
        checkbox.checked = false;
        toast("Focus view can include up to 20 people.", "error");
        return;
      }
      if (checkbox.checked) selected.add(user.id);
      else selected.delete(user.id);
      updateCount();
    });
    label.append(checkbox, avatar(user), text("span", user.nickname));
    choices.append(label);
  }
  if (!candidates.length) choices.append(text("p", "No other members have appeared in this room yet.", "empty-small"));

  const actions = document.createElement("div");
  actions.className = "modal-actions";
  const save = text("button", "Use Focus view", "primary");
  save.setAttribute("type", "button");
  save.addEventListener("click", async () => {
    save.setAttribute("disabled", "true");
    disable.setAttribute("disabled", "true");
    try {
      await saveFocusView(true, [...selected]);
      closeModal();
      renderMembers();
      renderMessages();
      toast(`Focus view is showing ${selected.size} selected ${selected.size === 1 ? "person" : "people"}, plus your mentions in this room.`);
    } catch (error) {
      save.removeAttribute("disabled");
      disable.removeAttribute("disabled");
      toast(error instanceof Error ? error.message : "Could not save Focus view.", "error");
    }
  });
  const disable = text("button", "Show everyone", "secondary");
  disable.setAttribute("type", "button");
  disable.addEventListener("click", async () => {
    save.setAttribute("disabled", "true");
    disable.setAttribute("disabled", "true");
    try {
      await saveFocusView(false, [...selected]);
      closeModal();
      renderMembers();
      renderMessages();
      toast("Focus view is off. Everyone in this room is visible.");
    } catch (error) {
      save.removeAttribute("disabled");
      disable.removeAttribute("disabled");
      toast(error instanceof Error ? error.message : "Could not update Focus view.", "error");
    }
  });
  actions.append(save, disable);
  updateCount();
  section.append(
    heading,
    text("p", "Choose whose messages you want to see. The public room stays unchanged, and your list is private.", "modal-intro"),
    count,
    choices,
    actions,
  );
  openModal(section);
}

type GuestbookPage = { entries: GuestbookEntry[]; nextCursor: string | null };

function guestbookPagePath(cursor?: string | null) {
  const search = new URLSearchParams({ limit: String(GUESTBOOK_PAGE_SIZE) });
  if (cursor) search.set("before", cursor);
  return `/api/guestbook?${search.toString()}`;
}

function appendWallNoteActions(note: HTMLElement, entry: GuestbookEntry) {
  const actions = document.createElement("div");
  actions.className = "wall-note-actions";
  if (state.viewer && state.viewer.id !== entry.author.id) {
    const report = text("button", "Report", "action-link") as HTMLButtonElement;
    report.type = "button";
    report.addEventListener("click", () => openReport(undefined, entry.author, undefined, entry.id));
    actions.append(report);
  }
  const canModerate = state.viewer?.role === "owner"
    || (state.viewer?.role === "admin" && entry.author.role === "member" && entry.authorRoleAtPost === "member");
  if (state.viewer?.id === entry.author.id || canModerate) {
    const remove = text("button", state.viewer?.id === entry.author.id ? "Remove my note" : "Remove", "action-link remove-action") as HTMLButtonElement;
    remove.type = "button";
    remove.addEventListener("click", () => openRemoveGuestbookEntry(entry));
    actions.append(remove);
  }
  if (actions.childElementCount) note.append(actions);
}

function communityWallNote(entry: GuestbookEntry, index: number, openNote?: (source: HTMLButtonElement) => void) {
  const presentation = wallNotePresentation(entry.id, index);
  const note = document.createElement("article");
  note.className = `wall-note wall-slot-${presentation.slot} wall-tone-${presentation.tone}${openNote ? "" : " wall-note-expanded"}`;
  note.style.setProperty("--wall-tilt", `${presentation.tilt}deg`);
  if (openNote) note.setAttribute("role", "listitem");

  const top = document.createElement("div");
  top.className = "wall-note-top";
  const authorLine = document.createElement("div");
  authorLine.className = "wall-author";
  authorLine.append(titleBadge(entry.author));
  const author = text("button", entry.author.nickname, "wall-author-name") as HTMLButtonElement;
  author.type = "button";
  author.addEventListener("click", () => openMember(entry.author));
  authorLine.append(author, text("span", `Lv.${entry.author.level}`, "wall-level"));
  const time = text("time", notificationTime(entry.createdAt), "wall-time") as HTMLTimeElement;
  time.dateTime = new Date(entry.createdAt).toISOString();
  top.append(authorLine, time);
  note.append(top, text("p", entry.body, "wall-note-body"), text("p", `— ${entry.signature}`, "wall-signature"));

  if (openNote) {
    const expand = text("button", "Open", "wall-note-expand") as HTMLButtonElement;
    expand.type = "button";
    expand.setAttribute("aria-label", `Open note signed ${entry.signature}`);
    expand.setAttribute("aria-expanded", "false");
    expand.addEventListener("click", () => openNote(expand));
    note.append(expand);
    note.addEventListener("click", (event) => {
      if ((event.target as Element).closest("button")) return;
      expand.click();
    });
  }
  appendWallNoteActions(note, entry);
  return note;
}

async function openCommunityWall() {
  try {
    const firstPage = await api<GuestbookPage>(guestbookPagePath());
    applyQuietRoomWallEntries(firstPage.entries);
    if (elements.messages.querySelector(".quiet-room-card")) renderMessages(false);

    const section = document.createElement("div");
    section.className = "community-wall-view";
    const base = document.createElement("div");
    base.className = "wall-view-base";
    const header = document.createElement("header");
    header.className = "wall-view-header";
    const headingGroup = document.createElement("div");
    headingGroup.className = "wall-heading-group";
    const heading = text("h2", "Community Message Wall");
    heading.id = "modal-title";
    headingGroup.append(
      heading,
      text("p", "Leave a thought, or open a note to bring it forward.", "modal-intro"),
      text("p", "Public · reportable · no links, social accounts or contact information", "wall-safety-line"),
    );
    const toolbar = document.createElement("div");
    toolbar.className = "wall-toolbar";
    const leave = text("button", viewerHasAccount() ? "Leave a note" : "Create account to write", "primary") as HTMLButtonElement;
    leave.type = "button";
    leave.addEventListener("click", () => viewerHasAccount() ? openGuestbookComposer() : openAuth("register"));
    const feedback = text("button", "Send private feedback", "secondary") as HTMLButtonElement;
    feedback.type = "button";
    feedback.addEventListener("click", openFeedbackBox);
    toolbar.append(leave, feedback);
    header.append(headingGroup, toolbar);

    const wallRegion = document.createElement("div");
    wallRegion.className = "wall-scroll-region";
    const wallMeta = document.createElement("div");
    wallMeta.className = "wall-meta";
    const count = text("p", "", "wall-count");
    count.setAttribute("aria-live", "polite");
    wallMeta.append(count, text("p", "Scroll to explore · select a note to read it larger", "wall-scroll-hint"));
    const wall = document.createElement("div");
    wall.className = "guestbook-wall";
    wall.setAttribute("role", "list");
    wall.setAttribute("aria-label", "Public community notes");

    const focusLayer = document.createElement("div");
    focusLayer.className = "wall-focus-layer hidden";
    focusLayer.setAttribute("aria-hidden", "true");
    const focusFrame = document.createElement("div");
    focusFrame.className = "wall-focus-frame";
    focusFrame.setAttribute("role", "region");
    focusFrame.setAttribute("aria-label", "Expanded wall note");
    focusLayer.append(focusFrame);
    section.append(base, focusLayer);

    const closeSelected = (source?: HTMLButtonElement) => {
      focusLayer.classList.remove("is-open");
      focusLayer.classList.add("hidden");
      focusLayer.setAttribute("aria-hidden", "true");
      base.inert = false;
      if (activeWallNoteClose) activeWallNoteClose = null;
      if (source?.isConnected) {
        source.setAttribute("aria-expanded", "false");
        source.focus();
      }
    };

    const openSelected = (entry: GuestbookEntry, index: number, source: HTMLButtonElement) => {
      const close = text("button", "Close note", "wall-focus-close") as HTMLButtonElement;
      close.type = "button";
      const selected = communityWallNote(entry, index);
      const dismiss = () => closeSelected(source);
      close.addEventListener("click", dismiss);
      focusFrame.replaceChildren(close, selected);
      base.inert = true;
      source.setAttribute("aria-expanded", "true");
      focusLayer.classList.remove("hidden");
      focusLayer.setAttribute("aria-hidden", "false");
      requestAnimationFrame(() => focusLayer.classList.add("is-open"));
      focusLayer.onclick = (event) => { if (event.target === focusLayer) dismiss(); };
      activeWallNoteClose = dismiss;
      close.focus();
    };

    let loadedEntries = [...firstPage.entries];
    const knownIds = new Set(loadedEntries.map((entry) => entry.id));
    let nextCursor = firstPage.nextCursor;
    const appendClusters = (entries: GuestbookEntry[], startIndex: number) => {
      for (const clusterEntries of wallClusters(entries)) {
        const cluster = document.createElement("section");
        cluster.className = "wall-cluster";
        cluster.setAttribute("aria-label", "Message wall notes");
        clusterEntries.forEach((entry, clusterIndex) => {
          const index = startIndex + clusterIndex;
          cluster.append(communityWallNote(entry, index, (source) => openSelected(entry, index, source)));
        });
        startIndex += clusterEntries.length;
        wall.append(cluster);
      }
    };

    if (loadedEntries.length) appendClusters(loadedEntries, 0);
    else wall.append(text("p", "No public notes yet. You can leave the first one.", "wall-empty"));
    const footer = document.createElement("footer");
    footer.className = "wall-footer";
    const loadMore = text("button", "Load 24 more notes", "secondary") as HTMLButtonElement;
    loadMore.type = "button";
    const updateWallMeta = () => {
      count.textContent = loadedEntries.length === 1 ? "Showing 1 public note" : `Showing ${loadedEntries.length} public notes`;
      loadMore.classList.toggle("hidden", !nextCursor);
    };
    loadMore.addEventListener("click", async () => {
      if (!nextCursor) return;
      const cursor = nextCursor;
      loadMore.disabled = true;
      loadMore.textContent = "Loading notes…";
      try {
        const nextPage = await api<GuestbookPage>(guestbookPagePath(cursor));
        const freshEntries = nextPage.entries.filter((entry) => !knownIds.has(entry.id));
        const startIndex = loadedEntries.length;
        for (const entry of freshEntries) knownIds.add(entry.id);
        loadedEntries = loadedEntries.concat(freshEntries);
        appendClusters(freshEntries, startIndex);
        nextCursor = freshEntries.length ? nextPage.nextCursor : null;
        updateWallMeta();
      } catch (error) {
        toast(error instanceof Error ? error.message : "Could not load more wall messages.", "error");
      } finally {
        loadMore.disabled = false;
        loadMore.textContent = "Load 24 more notes";
      }
    });
    footer.append(loadMore);
    updateWallMeta();
    wallRegion.append(wallMeta, wall, footer);
    base.append(header, wallRegion);
    openModal(section);
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not load the message wall.", "error");
  }
}

function openGuestbookComposer() {
  if (!state.viewer || state.viewer.isGuest) {
    openAuth("register");
    return;
  }
  const section = document.createElement("div");
  const heading = text("h2", "Leave a note");
  heading.id = "modal-title";
  const form = document.createElement("form");
  form.className = "stack-form";
  const signature = field("Signature", "text", "signature", { minlength: "2", maxlength: "40", value: state.viewer.nickname, autocomplete: "off" });
  form.append(signature, textareaField("Wall message", "body", "Write a short note for the community.", 240));
  const submit = text("button", "Post to the public wall", "primary wide") as HTMLButtonElement;
  submit.type = "submit";
  form.append(submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    submit.disabled = true;
    const data = new FormData(form);
    try {
      await api("/api/guestbook", { method: "POST", body: JSON.stringify({ signature: data.get("signature"), body: data.get("body") }) });
      toast("Your note is now on the public wall.");
      await openCommunityWall();
    } catch (error) {
      submit.disabled = false;
      toast(error instanceof Error ? error.message : "Could not post the wall message.", "error");
    }
  });
  section.append(
    heading,
    text("p", "This note is public and linked to your profile. Do not include links, social accounts or contact details. You can remove it later.", "modal-intro"),
    form,
  );
  openModal(section);
}

function openFeedbackBox() {
  if (!state.viewer || state.viewer.isGuest) {
    toast("Create an account before sending a private note to the ManyHellos team.");
    openAuth("register");
    return;
  }
  const section = document.createElement("div");
  const heading = text("h2", "Private feedback");
  heading.id = "modal-title";
  const form = document.createElement("form");
  form.className = "stack-form";
  const categoryWrap = document.createElement("label");
  categoryWrap.className = "field";
  categoryWrap.append(text("span", "What is this about?"));
  const category = document.createElement("select");
  category.name = "category";
  for (const [value, label] of [["suggestion", "Suggestion"], ["experience", "My ManyHellos experience"], ["other", "Something else"]]) {
    const option = document.createElement("option");
    option.value = value!;
    option.textContent = label!;
    category.append(option);
  }
  categoryWrap.append(category);
  form.append(categoryWrap, textareaField("Your message", "body", "Share an idea, a problem, or anything else you want us to know.", 1000));
  const submit = text("button", "Send privately", "primary wide") as HTMLButtonElement;
  submit.type = "submit";
  form.append(submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    submit.disabled = true;
    const data = new FormData(form);
    try {
      await api("/api/feedback", { method: "POST", body: JSON.stringify({ category: data.get("category"), body: data.get("body") }) });
      closeModal();
      toast("Private feedback sent.");
    } catch (error) {
      submit.disabled = false;
      toast(error instanceof Error ? error.message : "Could not send your private note.", "error");
    }
  });
  section.append(
    heading,
    text("p", "Only the owner and admins can read this. It will not appear on the message wall. For a safety or moderation issue, use Report or Appeal instead.", "modal-intro"),
    form,
  );
  openModal(section);
}

function openRemoveGuestbookEntry(entry: GuestbookEntry) {
  if (!state.viewer) return;
  const own = state.viewer.id === entry.author.id;
  const section = document.createElement("div");
  const heading = text("h2", own ? "Remove your wall message?" : "Remove this wall message?");
  heading.id = "modal-title";
  const form = document.createElement("form");
  form.className = "stack-form";
  if (!own) form.append(textareaField("Reason", "reason", "Explain which community rule this wall message breaks.", 300));
  const submit = text("button", "Remove from wall", "danger wide") as HTMLButtonElement;
  submit.type = "submit";
  form.append(submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    submit.disabled = true;
    try {
      if (own) {
        await api("/api/guestbook/remove", { method: "POST", body: JSON.stringify({ entryId: entry.id }) });
      } else {
        await api("/api/mod/action", { method: "POST", body: JSON.stringify({ action: "delete_guestbook_entry", guestbookEntryId: entry.id, reason: new FormData(form).get("reason") }) });
      }
      toast(own ? "Your wall message was removed." : "Wall message removed and recorded in the audit log.");
      await openCommunityWall();
    } catch (error) {
      submit.disabled = false;
      toast(error instanceof Error ? error.message : "Could not remove the wall message.", "error");
    }
  });
  section.append(heading, text("p", `“${entry.body.slice(0, 160)}${entry.body.length > 160 ? "…" : ""}”`, "notice"), form);
  openModal(section);
}

function textareaField(label: string, name: string, placeholder: string, max = 1000) {
  const wrap = document.createElement("label");
  wrap.className = "field";
  wrap.append(text("span", label));
  const area = document.createElement("textarea");
  area.name = name;
  area.placeholder = placeholder;
  area.maxLength = max;
  area.required = true;
  wrap.append(area);
  return wrap;
}

function openReport(message?: Message, user?: User, gameMessageId?: number, guestbookEntryId?: number) {
  const target = user ?? message?.author;
  if (!state.viewer || !target) return;
  const section = document.createElement("div");
  const heading = text("h2", `Report ${message || gameMessageId || guestbookEntryId ? "message" : target.nickname}`);
  heading.id = "modal-title";
  const form = document.createElement("form");
  form.className = "stack-form";
  const canReportLanguage = message?.roomId === "reo-maori";
  const categoryWrap = document.createElement("label");
  categoryWrap.className = "field";
  categoryWrap.append(text("span", "Report type"));
  const category = document.createElement("select");
  category.name = "category";
  for (const value of REPORT_CATEGORIES) {
    if (value === "wrong_language" && !canReportLanguage) continue;
    const option = document.createElement("option");
    option.value = value;
    option.textContent = REPORT_CATEGORY_RULES[value].label;
    category.append(option);
  }
  category.value = "safety";
  categoryWrap.append(category);
  form.append(categoryWrap);
  const urgentHelp = text("p", "If someone is in immediate danger in New Zealand, call 111. Sending this report alerts ManyHellos moderators; it does not contact emergency services.", "report-urgent-help hidden");
  const syncUrgentHelp = () => urgentHelp.classList.toggle("hidden", category.value !== "threat");
  category.addEventListener("change", syncUrgentHelp);
  syncUrgentHelp();
  form.append(urgentHelp);
  form.append(textareaField("What happened?", "reason", "Give the moderator enough context to review this.", 300));
  const submit = text("button", "Send report", "primary wide");
  submit.setAttribute("type", "submit");
  form.append(submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const data = new FormData(form);
    try {
      const result = await api<{ safetyHold: boolean }>("/api/reports", { method: "POST", body: JSON.stringify({ messageSeq: message?.seq, gameMessageId, guestbookEntryId, targetUserId: target.id, reason: data.get("reason"), category: data.get("category") ?? "safety" }) });
      closeModal();
      toast(result.safetyHold ? "Report sent. This member’s chat access has been paused for 10 minutes while a moderator reviews the reports." : "Report sent to the moderation team.");
    } catch (error) { toast(error instanceof Error ? error.message : "Report failed.", "error"); }
  });
  const reportCopy = "Reports are private. If three established accounts report the same member for a safety issue tied to a public post, that member may be paused briefly while a moderator checks it. Language reports do not trigger this pause. Misuse may lead to restrictions.";
  section.append(heading, text("p", reportCopy, "modal-intro"), form);
  openModal(section);
}

function openRemoveMessage(message: Message) {
  if (!state.viewer || state.viewer.role === "member" || message.deletedAt || message.recalledAt) return;
  if (state.viewer.role === "admin" && (message.author.role !== "member" || message.authorRoleAtPost !== "member")) return;
  const section = document.createElement("div");
  const heading = text("h2", "Remove this message?");
  heading.id = "modal-title";
  const readableBody = stickerTextFallback(message.body ?? "");
  const excerpt = readableBody.slice(0, 180);
  const form = document.createElement("form");
  form.className = "stack-form";
  form.append(textareaField("Reason", "reason", "Explain which community rule this message breaks.", 300));
  const submit = text("button", "Remove message", "danger wide");
  submit.setAttribute("type", "submit");
  form.append(submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    submit.setAttribute("disabled", "true");
    try {
      await api("/api/mod/action", {
        method: "POST",
        body: JSON.stringify({ action: "delete_message", messageSeq: message.seq, reason: new FormData(form).get("reason") }),
      });
      closeModal();
      toast("Message removed and recorded in the moderation log.");
    } catch (error) {
      submit.removeAttribute("disabled");
      toast(error instanceof Error ? error.message : "Message could not be removed.", "error");
    }
  });
  section.append(
    heading,
    text("p", `From ${message.author.nickname}: “${excerpt}${readableBody.length > excerpt.length ? "…" : ""}”`, "notice"),
    text("p", "Everyone will see that the message was removed. The reason is saved in the moderation log.", "modal-intro"),
    form,
  );
  openModal(section);
}

function openAdminApplication() {
  const section = document.createElement("div");
  const heading = text("h2", "Apply to help moderate");
  heading.id = "modal-title";
  const form = document.createElement("form");
  form.className = "stack-form";
  form.append(textareaField("Why would you be a good admin?", "body", "Tell the site owner how you would help keep the room welcoming."));
  const submit = text("button", "Send application", "primary wide");
  submit.setAttribute("type", "submit");
  form.append(submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      await api("/api/admin-applications", { method: "POST", body: JSON.stringify({ body: new FormData(form).get("body") }) });
      closeModal(); toast("Application sent to the site owner.");
    } catch (error) { toast(error instanceof Error ? error.message : "Application failed.", "error"); }
  });
  section.append(heading, text("p", "You can apply at level 30 once your account is 30 days old. The site owner decides every appointment.", "modal-intro"), form);
  openModal(section);
}

function openAppeal(sanction: Sanction) {
  const section = document.createElement("div");
  const heading = text("h2", sanction.type === "black_room" ? "Temporary chat restriction" : sanction.type === "kick" ? "Lobby removal notice" : "Mute notice");
  heading.id = "modal-title";
  section.append(heading, text("p", `Reason: ${sanction.reason}`, "notice"), text("p", `Applied by ${sanction.actorNickname}. ${remaining(sanction.expiresAt)} remaining.`, "modal-intro"));
  const form = document.createElement("form");
  form.className = "stack-form";
  form.append(textareaField("Your appeal", "body", "Explain briefly why this penalty should be reviewed."));
  const submit = text("button", "Submit appeal", "primary wide");
  submit.setAttribute("type", "submit");
  form.append(submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      await api("/api/appeals", { method: "POST", body: JSON.stringify({ sanctionId: sanction.id, body: new FormData(form).get("body") }) });
      closeModal(); toast("Appeal submitted.");
    } catch (error) { toast(error instanceof Error ? error.message : "Appeal failed.", "error"); }
  });
  section.append(form);
  openModal(section);
}

function openModerate(user: User) {
  const section = document.createElement("div");
  const heading = text("h2", `Moderate ${user.nickname}`);
  heading.id = "modal-title";
  const form = document.createElement("form");
  form.className = "stack-form";
  const selectWrap = document.createElement("label");
  selectWrap.className = "field";
  selectWrap.append(text("span", "Action"));
  const select = document.createElement("select");
  select.name = "action";
  const actions: Array<[string, string]> = [["mute", "Mute"], ["kick", "Kick out"], ["black_room", "Temporarily restrict chat"]];
  if (user.customTitle) actions.push(["clear_custom_title", `Remove custom title · ${user.customTitle}`]);
  for (const [value, label] of actions) {
    const option = document.createElement("option"); option.value = value; option.textContent = label; select.append(option);
  }
  selectWrap.append(select);
  const durationField = field("Duration (minutes)", "number", "minutes", { min: "1", max: "10080", value: "30" });
  const durationInput = durationField.querySelector<HTMLInputElement>("input")!;
  const syncActionFields = () => {
    const removesTitle = select.value === "clear_custom_title";
    durationField.classList.toggle("hidden", removesTitle);
    durationInput.disabled = removesTitle;
  };
  select.addEventListener("change", syncActionFields);
  syncActionFields();
  form.append(selectWrap, durationField, textareaField("Reason", "reason", "Visible to the member and recorded in the moderation log.", 300));
  if (state.viewer?.role === "owner") {
    const role = text("button", user.role === "admin" ? "Remove admin role" : "Appoint as admin", "secondary wide");
    role.setAttribute("type", "button");
    role.addEventListener("click", async () => {
      try {
        await api("/api/mod/action", { method: "POST", body: JSON.stringify({ action: "set_role", targetUserId: user.id, role: user.role === "admin" ? "member" : "admin", reason: "Manual role decision by site owner" }) });
        closeModal(); toast("Role updated.");
      } catch (error) { toast(error instanceof Error ? error.message : "Role update failed.", "error"); }
    });
    form.append(role);
  }
  const submit = text("button", "Apply action", "danger wide");
  submit.setAttribute("type", "submit");
  form.append(submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const data = new FormData(form);
    try {
      await api("/api/mod/action", { method: "POST", body: JSON.stringify({ action: data.get("action"), targetUserId: user.id, minutes: Number(data.get("minutes")), reason: data.get("reason") }) });
      closeModal(); toast("Moderation action recorded.");
    } catch (error) { toast(error instanceof Error ? error.message : "Action failed.", "error"); }
  });
  section.append(heading, text("p", "Every action needs a reason and is saved in the moderation log.", "modal-intro"), form);
  openModal(section);
}

async function openModerationDesk() {
  try {
    const data = await api<{ reports: Array<Record<string, unknown>>; feedback: Array<Record<string, unknown>>; appeals: Array<Record<string, unknown>>; reportRestrictionAppeals: Array<Record<string, unknown>>; applications: Array<Record<string, unknown>>; log: Array<Record<string, unknown>> }>("/api/mod/dashboard");
    const section = document.createElement("div");
    section.className = "moderation-desk";
    const heading = text("h2", "Moderation desk"); heading.id = "modal-title";
    const activeReports = data.reports.filter((row) => row.status === "open" || row.status === "reviewing").length;
    const activeFeedback = data.feedback.filter((row) => row.status === "open").length;
    const activeAppeals = data.appeals.filter((row) => row.status === "open").length;
    const activeReportRestrictionAppeals = data.reportRestrictionAppeals.filter((row) => row.status === "open").length;
    const activeApplications = data.applications.filter((row) => row.status === "open").length;
    section.append(heading, text("p", `${activeReports} active reports · ${activeFeedback} private notes · ${activeAppeals} sanction appeals · ${activeReportRestrictionAppeals} reporting appeals · ${activeApplications} admin applications`, "modal-intro"));

    const runCaseAction = async (button: HTMLButtonElement, caseType: string, caseId: number, action: string, note = "") => {
      button.disabled = true;
      try {
        await api("/api/mod/case-action", { method: "POST", body: JSON.stringify({ caseType, caseId, action, note }) });
        toast("Case decision recorded in the moderation log.");
        await openModerationDesk();
      } catch (error) {
        button.disabled = false;
        toast(error instanceof Error ? error.message : "Could not update this case.", "error");
      }
    };

    const caseCard = (row: Record<string, unknown>, label: string, summary: string) => {
      const card = document.createElement("article");
      card.className = "review-case";
      const top = document.createElement("div");
      top.className = "review-case-top";
      top.append(text("strong", `${label} #${Number(row.id)}`), text("span", String(row.status ?? "open"), `case-status status-${String(row.status ?? "open")}`));
      card.append(top, text("p", summary, "review-case-summary"));
      if (row.assigned_nickname) card.append(text("p", `Assigned to ${String(row.assigned_nickname)}`, "review-case-meta"));
      if (row.resolution) card.append(text("p", `${String(row.resolved_by_nickname ?? "Moderator")}: ${String(row.resolution)}`, "case-resolution"));
      return card;
    };

    const resolutionControls = (card: HTMLElement, caseType: string, caseId: number, actions: Array<{ action: string; label: string; className: string }>) => {
      const note = document.createElement("textarea");
      note.className = "case-note";
      note.maxLength = 500;
      note.rows = 2;
      note.placeholder = "Required decision note (3–500 characters)";
      note.setAttribute("aria-label", "Decision note");
      const controls = document.createElement("div");
      controls.className = "case-actions";
      for (const spec of actions) {
        const button = text("button", spec.label, spec.className) as HTMLButtonElement;
        button.type = "button";
        button.addEventListener("click", () => runCaseAction(button, caseType, caseId, spec.action, note.value));
        controls.append(button);
      }
      card.append(note, controls);
    };

    const listSection = (titleValue: string, rows: Array<Record<string, unknown>>, render: (row: Record<string, unknown>) => HTMLElement) => {
      section.append(text("h3", titleValue));
      const list = document.createElement("div"); list.className = "review-list";
      if (!rows.length) list.append(text("p", `No ${titleValue.toLowerCase()} yet.`, "empty-small"));
      for (const row of rows.slice(0, 30)) list.append(render(row));
      section.append(list);
    };

    listSection("Reports", data.reports, (row) => {
      const subject = row.target_nickname ? ` · about ${row.target_nickname}` : "";
      const category = isReportCategory(row.category) ? row.category : "safety";
      const room = row.reported_room_id === "reo-maori" ? "te reo Māori room" : "national lobby";
      const evidence = typeof row.reported_body === "string"
        ? ` · ${row.reported_channel === "game" ? "table" : row.reported_channel === "guestbook" ? "message wall" : room} message: “${stickerTextFallback(row.reported_body)}”${row.reported_deleted_at ? " (removed)" : ""}`
        : " · member report (no message attached)";
      const card = caseCard(row, REPORT_CATEGORY_RULES[category].deskLabel, `${row.reporter_nickname}: ${row.reason}${subject}${evidence}`);
      card.classList.add(`report-category-${category}`);
      card.dataset.reportCategory = category;
      if (row.status === "open" || row.status === "reviewing") {
        const caseId = Number(row.id);
        const targetsViewer = String(row.target_user_id ?? "") === state.viewer?.id;
        const targetsStaff = row.target_role === "admin" || row.target_role === "owner";
        if (targetsViewer && state.viewer?.role === "owner") {
          card.append(text("p", "Because this report concerns you, you cannot decide its merits or penalize the reporter. You may only remove it from the active queue while retaining it for independent review.", "review-case-meta"));
          resolutionControls(card, "report", caseId, [
            { action: "refer_external", label: "Refer for independent review", className: "secondary" },
          ]);
        } else if (targetsViewer || (targetsStaff && state.viewer?.role !== "owner")) {
          card.append(text("p", targetsViewer
            ? "You cannot claim or decide a report about yourself."
            : "Only the site owner can review a report about staff.", "review-case-meta"));
        } else {
          if (row.status === "open") {
            const claim = text("button", "Claim for review", "secondary") as HTMLButtonElement;
            claim.type = "button";
            claim.addEventListener("click", () => runCaseAction(claim, "report", caseId, "claim"));
            card.append(claim);
          }
          resolutionControls(card, "report", caseId, [
            { action: "resolve", label: "Resolve", className: "primary" },
            { action: "dismiss", label: "Dismiss", className: "secondary" },
            { action: "mark_malicious", label: "Confirm malicious report", className: "danger" },
          ]);
        }
      }
      return card;
    });
    listSection("Private feedback", data.feedback, (row) => {
      const category = row.category === "experience" ? "Experience" : row.category === "other" ? "Other" : "Suggestion";
      const card = caseCard(row, category, `${row.nickname}: ${row.body}`);
      if (row.status === "open") resolutionControls(card, "feedback", Number(row.id), [
        { action: "close", label: "Mark reviewed", className: "primary" },
      ]);
      return card;
    });
    listSection("Appeals", data.appeals, (row) => {
      const active = Number(row.sanction_active) === 1 && Number(row.sanction_expires_at) > Date.now();
      const card = caseCard(row, "Appeal", `${row.nickname}: ${row.body} · ${row.sanction_type} for “${row.sanction_reason}”${active ? " (active)" : " (expired or released)"}`);
      if (row.status === "open") resolutionControls(card, "appeal", Number(row.id), [
        { action: "approve", label: "Approve & release", className: "primary" },
        { action: "deny", label: "Deny", className: "secondary" },
      ]);
      return card;
    });
    listSection("Reporting restriction appeals", data.reportRestrictionAppeals, (row) => {
      const active = Number(row.current_restriction_until) > Date.now();
      const restrictionTime = notificationTime(Number(row.restriction_until));
      const card = caseCard(row, "Reporting appeal", `${row.nickname}: ${row.body} · restriction scheduled until ${restrictionTime}${active ? " (active)" : " (expired or released)"}`);
      if (row.status === "open" && state.viewer?.role === "owner") resolutionControls(card, "report_restriction_appeal", Number(row.id), [
        { action: "approve", label: "Approve & restore reporting", className: "primary" },
        { action: "deny", label: "Deny", className: "secondary" },
      ]);
      else if (row.status === "open") card.append(text("p", "Only the site owner can make this decision.", "review-case-meta"));
      return card;
    });
    listSection("Admin applications", data.applications, (row) => {
      const card = caseCard(row, "Admin application", `${row.nickname}: ${row.body}`);
      if (row.status === "open" && state.viewer?.role === "owner") resolutionControls(card, "application", Number(row.id), [
        { action: "approve", label: "Appoint admin", className: "primary" },
        { action: "deny", label: "Decline", className: "secondary" },
      ]);
      else if (row.status === "open") card.append(text("p", "Only the site owner can make this decision.", "review-case-meta"));
      return card;
    });
    listSection("Recent audit log", data.log, (row) => caseCard(
      { id: row.id, status: "recorded" },
      "Audit",
      `${row.actor_nickname} · ${row.action}${row.target_nickname ? ` · ${row.target_nickname}` : ""} · ${row.reason}`,
    ));
    openModal(section);
  } catch (error) { toast(error instanceof Error ? error.message : "Could not load moderation desk.", "error"); }
}

function openInfo() {
  const section = document.createElement("div");
  section.className = "info-view";
  const heading = text("h2", "Community rules & privacy"); heading.id = "modal-title";
  section.append(heading, text("p", "ManyHellos is a public text-chat community for adults in Aotearoa New Zealand. Everyone who joins must confirm they are 18 or older.", "modal-intro"));
  const rules = [
    "Treat people with respect. No harassment, threats, hate, sexual exploitation, or impersonation.",
    "Messages are public. Do not share social accounts, contact details or anyone else's private information.",
    "No external links, images, files, private messages, advertising, bots, or automated posting.",
    "Use report and block tools. Moderators may remove messages, mute, kick, or temporarily restrict an account’s chat access.",
    "Focus view changes only your screen. @mentions remain public.",
    "The message wall is public and follows the same rules as chat. Private feedback is visible only to the owner and admins.",
    "Anyone can join the Te Reo Māori Room. Please chat in te reo Māori; a moderator checks language reports.",
  ];
  const list = document.createElement("ol"); for (const rule of rules) list.append(text("li", rule));
  section.append(
    list,
    text("h3", "How progression works"),
    text("p", "Registered members earn one XP per qualified active minute (up to 60 a day) and one XP per eligible message outside the reward cooldown (up to 20 a day). Extra tabs, idle/background time, rapid messages and repeated content do not multiply rewards. Levels unlock expression only; the site owner appoints every admin manually."),
    text("h3", "Privacy summary"),
    text("p", "ManyHellos is pseudonymous, not fully anonymous. We do not require a legal name or social graph, but public activity remains linked to an internal guest or member record so blocking, reporting, appeals and human moderation work. A guest session lasts up to 24 hours; its public messages and internal record do not automatically disappear when the session ends. The application processes source network addresses for abuse controls without writing them to its database, while the web server and service providers keep limited operational logs. Public content and safety evidence are retained by purpose, with legal, security and safety holds where needed."),
  );
  const levels = text("button", "View the complete 1–50 XP table", "secondary wide");
  levels.addEventListener("click", openLevelGuide);
  const formalComplaint = document.createElement("a");
  formalComplaint.className = "secondary wide info-link-button";
  formalComplaint.href = "/legal-content-complaint.html";
  formalComplaint.textContent = "Formal content complaint & external help";
  const privacyNotice = document.createElement("a");
  privacyNotice.className = "secondary wide info-link-button";
  privacyNotice.href = "/privacy.html";
  privacyNotice.textContent = "Full privacy notice & account deletion";
  section.append(levels, privacyNotice, formalComplaint);
  openModal(section);
}

function openAppearance() {
  const section = document.createElement("div");
  section.className = "appearance-view";
  const heading = text("h2", "Appearance");
  heading.id = "modal-title";
  section.append(
    heading,
    text("p", "Choose the light around ManyHellos. This is separate from Classic view, which controls artistic type and message effects.", "modal-intro"),
  );
  const choices = document.createElement("div");
  choices.className = "appearance-options";
  const options: Array<{ value: ColorThemePreference; title: string; detail: string; icon: string }> = [
    { value: "night", title: "Night harbour", detail: "Deep Southern Sky colours and low-light chat surfaces.", icon: "☾" },
    { value: "light", title: "Morning mist", detail: "A calm daylight harbour with dark, high-contrast text.", icon: "☀" },
    { value: "system", title: "Follow this device", detail: `Uses your device setting. It is ${systemColorPreference.matches ? "light" : "dark"} right now.`, icon: "◐" },
  ];
  for (const option of options) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = state.colorTheme === option.value ? "selected" : "";
    button.setAttribute("aria-pressed", String(state.colorTheme === option.value));
    button.append(
      text("span", option.icon, "appearance-option-icon"),
      text("strong", option.title),
      text("span", option.detail),
    );
    button.addEventListener("click", () => {
      state.colorTheme = option.value;
      localStorage.setItem("kiwichat:color-theme", option.value);
      renderColorTheme();
      closeModal();
      toast(`${option.title} selected.`);
    });
    choices.append(button);
  }
  const backgrounds = document.createElement("div");
  backgrounds.className = "chat-background-options";
  for (const background of CHAT_BACKGROUNDS) {
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.chatBackground = background.id;
    button.dataset.backgroundAppearance = background.appearance;
    button.className = state.chatBackground === background.id ? "selected" : "";
    button.setAttribute("aria-pressed", String(state.chatBackground === background.id));
    const preview = text("span", "", "chat-background-preview");
    preview.setAttribute("aria-hidden", "true");
    const fit = background.appearance === "night" ? "Night" : background.appearance === "light" ? "Morning" : "Both";
    button.append(
      preview,
      text("strong", background.title),
      text("span", background.detail, "chat-background-detail"),
      text("span", fit, "chat-background-fit"),
    );
    button.addEventListener("click", () => {
      state.chatBackground = background.id;
      localStorage.setItem("kiwichat:chat-background", background.id);
      const currentAppearance = resolvedColorTheme();
      const needsAppearanceChange = background.appearance !== "any" && background.appearance !== currentAppearance;
      if (needsAppearanceChange) {
        state.colorTheme = background.appearance;
        localStorage.setItem("kiwichat:color-theme", background.appearance);
        renderColorTheme();
        for (const choice of choices.querySelectorAll<HTMLButtonElement>("button")) {
          const selected = choice.dataset.colorTheme === state.colorTheme;
          choice.classList.toggle("selected", selected);
          choice.setAttribute("aria-pressed", String(selected));
        }
      }
      renderChatBackground();
      for (const choice of backgrounds.querySelectorAll<HTMLButtonElement>("button")) {
        const selected = choice.dataset.chatBackground === state.chatBackground;
        choice.classList.toggle("selected", selected);
        choice.setAttribute("aria-pressed", String(selected));
      }
      toast(`${background.title} selected.${needsAppearanceChange ? ` Switched to ${background.appearance === "light" ? "Morning mist" : "Night harbour"}.` : ""}`);
    });
    backgrounds.append(button);
  }
  section.append(
    choices,
    text("h3", "Chat-room background"),
    text("p", "Dark scenes appear in Night harbour; bright scenes appear in Morning mist. Classic view always keeps its quiet reading surface.", "setting-help"),
    backgrounds,
  );
  openModal(section);
}

function openMobileRoomMenu() {
  const section = document.createElement("div");
  section.className = "mobile-room-menu";
  const heading = text("h2", "Room menu");
  heading.id = "modal-title";
  const roomTitle = currentRoom().title;
  const intro = roomTitle === "New Zealand Lobby"
    ? `Tools for the ${roomTitle}. The public conversation stays behind this sheet.`
    : `Room tools: ${roomTitle}. The public conversation stays behind this sheet.`;
  section.append(
    heading,
    text("p", intro, "modal-intro"),
  );

  const actions = document.createElement("div");
  actions.className = "mobile-room-menu-actions";
  const addAction = (
    label: string,
    detail: string,
    action: () => void,
    options: { kind?: "destination" | "toggle" | "plain"; emphasis?: "mystery" } = {},
  ) => {
    const { kind = "destination", emphasis } = options;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "mobile-room-menu-action";
    if (emphasis) button.classList.add(`is-${emphasis}`);
    button.append(text("strong", label), text("span", detail, "mobile-room-menu-detail"));
    if (kind === "destination") {
      const chevron = text("span", "›", "mobile-room-menu-chevron");
      chevron.setAttribute("aria-hidden", "true");
      button.append(chevron);
    } else if (kind === "toggle") {
      const toggle = text("span", "", "mobile-room-menu-switch");
      toggle.setAttribute("aria-hidden", "true");
      button.append(toggle);
    }
    button.addEventListener("click", () => closeModal({ then: action }));
    actions.append(button);
    return button;
  };
  addAction(
    "Open Mystery table",
    "Play a shared text mystery with 3–6 people",
    openMysteryTable,
    { emphasis: "mystery" },
  );
  addAction("Message wall", "Public notes from members", () => void openCommunityWall());
  addAction("Private feedback", "A message for the site team", openFeedbackBox);
  addAction("Rules & privacy", "Public-room boundaries and data use", openInfo);
  if (viewerCanModerate()) {
    addAction(
      state.slowMode ? `Slow mode: ${state.slowMode.intervalSeconds}s` : "Slow mode",
      state.slowMode ? "Review, update, or turn off the current room limit" : "Temporarily pace messages in this room",
      openSlowModeControl,
    );
  }
  addAction(
    "Appearance & background",
    `${state.colorTheme === "system" ? "System" : resolvedColorTheme() === "light" ? "Morning mist" : "Night harbour"} · ${chatBackgroundTitle(state.chatBackground)} background`,
    openAppearance,
  );
  const clear = addAction(
    "Clear on this device",
    elements.clearViewButton.disabled ? "Nothing to clear yet" : "Hide loaded messages here without deleting server history",
    clearCurrentRoomView,
    { kind: "plain" },
  );
  clear.disabled = elements.clearViewButton.disabled;
  const classic = addAction(
    "Classic view",
    state.classicMode ? "Tap to return to the harbour view" : "Turn off animations and custom fonts",
    () => elements.classicToggle.click(),
    { kind: "toggle" },
  );
  classic.setAttribute("aria-pressed", String(state.classicMode));
  section.append(actions);
  openModal(section);
}

elements.authButton.addEventListener("click", () => openAuth(state.viewer?.isGuest ? "register" : "login"));
elements.brand.addEventListener("click", (event) => {
  event.preventDefault();
  setHomeVisible(true);
});
elements.homeEnterButton.addEventListener("click", () => enterRoomFromHome(state.homePreview.selectedRoom));
elements.homeEnterBottomButton.addEventListener("click", () => enterRoomFromHome(state.homePreview.selectedRoom));
document.querySelector<HTMLButtonElement>("#home-register")!.addEventListener("click", () => openAuth("register"));
elements.homeRefreshButton.addEventListener("click", () => dispatchHomePreview({ type: "changeExample" }, true));
elements.homeOrbit.addEventListener("click", () => replayHomeHeroCycle(true));
elements.homeStoryEnterButton.addEventListener("click", () => enterRoomFromHome(state.homePreview.selectedRoom));
document.querySelectorAll<HTMLButtonElement>("[data-home-room]").forEach((button) => {
  button.addEventListener("click", () => dispatchHomePreview(
    { type: "selectRoom", roomId: button.dataset.homeRoom === "reo-maori" ? "reo-maori" : "nz-lobby" },
    true,
  ));
});
document.querySelectorAll<HTMLButtonElement>("[data-home-select]").forEach((button) => {
  button.addEventListener("click", () => {
    const module = button.dataset.homeSelect as HomeModule;
    if (module === "rooms") dispatchHomePreview({ type: "selectRooms" }, true);
    else if (module === "game") dispatchHomePreview({ type: "selectGame" }, true);
    else if (module === "safety") dispatchHomePreview({ type: "openSafety" }, true);
    else dispatchHomePreview({ type: "selectConversation" }, true);
  });
});
document.querySelector<HTMLButtonElement>("#home-game")!.addEventListener("click", () => {
  dispatchHomePreview({ type: "selectGame" });
  enterRoomFromHome("nz-lobby");
  window.requestAnimationFrame(() => elements.gameButton.click());
});
document.querySelector<HTMLButtonElement>("#home-safety")!.addEventListener("click", () => {
  dispatchHomePreview({ type: "openSafety" });
  openInfo();
});
document.querySelector<HTMLButtonElement>("#home-footer-info")!.addEventListener("click", openInfo);
document.querySelectorAll<HTMLElement>("[data-home-module]").forEach((module) => {
  module.addEventListener("click", (event) => {
    if ((event.target as Element).closest("button")) return;
    const moduleName = module.dataset.homeModule as HomeModule;
    if (moduleName === "rooms") dispatchHomePreview({ type: "selectRooms" }, true);
    else if (moduleName === "game") dispatchHomePreview({ type: "selectGame" }, true);
    else if (moduleName === "safety") dispatchHomePreview({ type: "openSafety" }, true);
    else dispatchHomePreview({ type: "selectConversation" }, true);
  });
});
document.querySelectorAll<HTMLElement>("[data-home-preview-target], [data-home-select]").forEach((control) => {
  const moduleName = (control.dataset.homePreviewTarget ?? control.dataset.homeSelect ?? "conversation") as HomeModule;
  const focusPreview = () => dispatchHomePreview({ type: "focus", module: moduleName });
  const blurPreview = () => dispatchHomePreview({ type: "blur" });
  control.addEventListener("focus", focusPreview);
  control.addEventListener("blur", blurPreview);
  control.addEventListener("pointerenter", focusPreview);
  control.addEventListener("pointerleave", () => {
    if (document.activeElement !== control) blurPreview();
  });
});
document.querySelector<HTMLButtonElement>("#home-feedback")!.addEventListener("click", openFeedbackBox);
document.querySelector<HTMLButtonElement>("#home-wall")!.addEventListener("click", () => void openCommunityWall());
elements.profileButton.addEventListener("click", openProfile);
elements.notificationButton.addEventListener("click", openNotifications);
elements.allViewButton.addEventListener("click", () => void chooseMessageView(false));
elements.focusViewButton.addEventListener("click", () => void chooseMessageView(true));
elements.focusEditButton.addEventListener("click", openFocusView);
elements.roomStreamButton.addEventListener("click", () => setRoomStreamOpen(!state.roomStreamOpen));
elements.roomStreamClose.addEventListener("click", () => setRoomStreamOpen(false));
elements.blockListButton.addEventListener("click", openBlockList);
elements.focusSuggestionChoose.addEventListener("click", () => {
  state.focusSuggestionVisible = false;
  renderFocusControl();
  openFocusView();
});
elements.focusSuggestionDismiss.addEventListener("click", dismissFocusSuggestion);
elements.slowModeButton.addEventListener("click", openSlowModeControl);
elements.clearViewButton.addEventListener("click", clearCurrentRoomView);
document.querySelector("#join-button")!.addEventListener("click", openGuestEntry);
document.querySelector("#identity-signin")?.addEventListener("click", () => openAuth("login"));
document.querySelector("#wall-button")!.addEventListener("click", () => void openCommunityWall());
document.querySelector("#feedback-button")!.addEventListener("click", openFeedbackBox);
document.querySelector("#info-button")!.addEventListener("click", openInfo);
elements.mobileInfoButton.addEventListener("click", openMobileRoomMenu);

const mobileLayout = window.matchMedia("(max-width: 720px)");
function setMemberDrawer(open: boolean) {
  const expanded = mobileLayout.matches && open;
  elements.memberPanel.classList.toggle("open", expanded);
  elements.memberToggle.setAttribute("aria-expanded", String(expanded));
  if (mobileLayout.matches) {
    elements.memberPanel.inert = !expanded;
    elements.memberPanel.setAttribute("aria-hidden", String(!expanded));
  } else {
    elements.memberPanel.inert = false;
    elements.memberPanel.removeAttribute("aria-hidden");
  }
  if (expanded) document.querySelector<HTMLButtonElement>("#members-close")!.focus();
  else if (open) elements.memberToggle.focus();
}
elements.memberToggle.addEventListener("click", () => setMemberDrawer(true));
document.querySelector("#members-close")!.addEventListener("click", () => {
  setMemberDrawer(false);
  elements.memberToggle.focus();
});
mobileLayout.addEventListener("change", () => setMemberDrawer(false));
setMemberDrawer(false);
elements.blueContinue.addEventListener("click", () => {
  setBlueScreenOpen(false);
  if (!elements.modalBackdrop.classList.contains("hidden")) elements.modalContent.querySelector<HTMLElement>("input,button,textarea")?.focus();
  else if (state.viewer && !elements.input.disabled) elements.input.focus();
});
elements.blueDetails.addEventListener("click", () => {
  setBlueScreenOpen(false);
  if (state.sanction) openAppeal(state.sanction);
});

renderRoomContext();
renderHome();
const homeViewportObserver = new IntersectionObserver(([entry]) => {
  homeInViewport = entry?.isIntersecting ?? true;
  syncHomeMotionPolicy();
}, { threshold: .05 });
homeViewportObserver.observe(elements.home);
document.addEventListener("visibilitychange", () => {
  syncHomeMotionPolicy();
  if (document.hidden) cancelPresenceMotion();
});
homeMotionPreference.addEventListener("change", () => {
  syncHomeMotionPolicy();
  if (homeMotionPreference.matches) cancelPresenceMotion();
});
updateComposerState();
const authOutcome = new URLSearchParams(location.search).get("auth");
if (authOutcome) history.replaceState(null, "", `${location.pathname}${location.hash}`);
connect();
if (authOutcome === "recovery") queueMicrotask(openPasswordRecovery);
else if (authOutcome === "verified") queueMicrotask(() => toast("Email verified. Welcome to ManyHellos."));
else if (authOutcome === "link-error") queueMicrotask(() => {
  openAuth("login");
  toast("That secure email link is invalid or has expired. Request a new one.", "error");
});
